'use client'

import { useStickyStackTop } from './stickyStack'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ReferenceDot,
  ResponsiveContainer,
  LabelList,
} from 'recharts'
import {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardCopy,
  Download,
  Eye,
  EyeOff,
  FileJson,
  Filter,
  GripVertical,
  Info,
  Loader2,
  Mail,
  Maximize2,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Search,
  Square,
  Tags,
  X,
} from 'lucide-react'
import { ResizableTable, TableTd, TableCheckboxTd, TableCheckboxHeaderLabel, rowClickToggleOrOpen, FILTER_NONE_SELECTED, CHECKBOX_COL_WIDTH, CHECKBOX_COL_TH_CLASS, type ColumnDef } from './ResizableTable'
import { Highlight } from './ProcessedEntry/DomainEntry/sharedComponents'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'
import { Input } from '@/components/ui/input'
import Turnstile, { type TurnstileHandle } from './Turnstile'
import { EntryDetailShell, type DetailBadge } from './EntryDetail/EntryDetailShell'
import { InvestigationSidebar, type InvestigationAction } from './InvestigationSidebar'
import { EntryDetailPanel, detailGridCols } from './EntryDetail/EntryDetailPanel'
import { EntryDomainsWidget } from './EntryDetail/EntryDomainsWidget'
import { SIBS_WIDGET_LIBRARY } from './EntryDetail/widgetLibrary'
import { CollapsibleSection } from './Entries/CollapsibleSection'
import { ActionsWidget } from './EntryDetail/ActionsWidget'
import { RawProcessedEntryPanel } from './Entries/RawProcessedEntryPanel'
import { BulkClassifyDialog, applyBulkClassifyPatch, type BulkClassifyForm, type BulkClassifyResult } from './Entries/BulkClassifyDialog'
import { GenericEntryRow, GENERIC_ROW_HEIGHT } from './Entries/GenericEntryRow'
import { apiPost, useCatalog } from './Entries/entriesApi'
import { useDetailPath } from './Entries/useDetailPath'
import type { Catalog } from './Entries/entryTypes'
import {
  SibsEmailWidget,
  SibsEntityReferenceEmailsWidget,
  SibsCaseReportWidget,
  sibsCommunication,
  RetryBanner,
  type SibsEntry,
  unsolicitedLabel,
} from './EntryDetail/SibsWidgets'
import { SparkCard, ProtectionLatencyCard } from './charts'
import { parseDbTimestamp } from '@/lib/dates'
import { BaseDashboard } from './BaseDashboard'
import { PILOT_MONTHS, DashboardRangePicker, periodLabel, isoToPtDate, todayIso, useDashboardRange } from './dashboardRange'
import { EntryLocationMap } from './EntryLocationMap'

const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? ''


type SibsChartEntry = {
  created_at: string
  entry_type: string | null
  email_sent?: boolean
  id?: string
  beneficiario?: string | null
  entity_reference_entity?: string | null
  platform_id?: number | null
  platform_namelong?: string | null
}
// user_entry_id: the stable identity to prefer when matching a log to an
// entry (see tableDispatchTimeById below) — entry_id is whichever row
// (raw_entries pre-Accept, processed_entries after) the dispatch happened
// to be logged against at send time, and drifts once an entry is Accepted
// or reprocessed. NULL for call-derived (scalls) dispatches, which have no
// user_entries row to begin with — entry_id is the only match for those.
type SibsDispatchLog = { created_at: string; entry_id?: string; user_entry_id?: string | null; status?: string }
export type FraudWaveDatum = { date: string; fraudCases: number; takenDown: number; live: number; unmitigatedLive: number }
export type BeneficiaryRankItem = { key: string; label: string; count: number; rank: number }
type SibsDashboardAggregate = {
  overview: {
    total_entries: number | string
    total_email_sent: number | string
    total_no_email_sent: number | string
    total_with_entity_reference: number | string
    unique_entity_references: number | string
    unique_beneficiaries: number | string
  } | null
  daily: Array<{
    day: string
    total_entries: number | string
    email_sent_count: number | string
    no_email_sent_count: number | string
  }>
  beneficiaryCounts: {
    total_unique_beneficiaries: number | string
    fraudulent_unique_beneficiaries: number | string
    legitimate_unique_beneficiaries: number | string
  } | null
  topLegitimate: Array<{ beneficiario: string; entry_count: number | string }>
  topFraudulent: Array<{ beneficiario: string; entry_count: number | string }>
  latencyBuckets: Array<{ bucket: string; entry_count: number | string }>
  medianProtection: { median_hours: number | string | null; measured_entries: number | string } | null
  topEmailedBeneficiaries: Array<{
    beneficiario: string | null
    linked_user_entries: number | string
    distinct_entity_references: number | string
    emails_sent: number | string
  }>
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// bulkClassifyRawEntries (backend) resolves each entry via its raw_entry_id
// (getUserEntryByRawEntryId), then branches on whether a processed_entries
// row already exists — updating it in place if so, same as re-clicking
// Accept does for a single already-accepted entry (AcceptEntryWidget's own
// "re-clicking is how an edit made after approval gets saved"). So this
// isn't limited to source_type === 'raw_entry': both a still-raw entry and
// an already-processed one are classify-able, as long as raw_entry_id is
// populated.
//
// Deliberately e.raw_entry_id, not e.user_entry_id: unlike
// SibsEntryDetailPage's own canManageRawEntry (which resolves the real
// raw_entry_id via a live per-entry /userentrydetail fetch keyed on
// user_entry_id, so it works for any source), this list-level check has
// only get_sibs_fraud_entries' own raw_entry_id output to go on — and that
// field is narrower, populated only for multibanco-origin entries (see that
// RPC's branch_processed/mbo LATERAL). A native (non-multibanco) MBWAY
// entry's e.id there is processed_entries.id, not a raw_entries.id at all,
// so falling back to e.id the way the panel's resolvedRawEntryId does would
// send bulkClassifyRawEntries a raw_entries lookup that can never match —
// excluding those entries here (rather than sending a request guaranteed to
// fail) is the correct behavior until the bulk path gets its own
// user_entry_id-based resolution.
function canBulkClassify(e: SibsEntry): boolean {
  return e.raw_entry_id != null
}
const CANNOT_BULK_CLASSIFY_TITLE = 'This entry has no editable raw entry behind it (e.g. a native MBWay submission or a call-derived entry), so it can\'t be classified this way.'

// Sentinel for the platform filter's "Unassigned" option — distinct from
// every real platform.id (positive integers), so it can sit in the same
// multi-select array alongside one or more real platform ids. Shared by
// CampaignsView's own entries table and AlertsAnalysisView's EmailsSentTable
// so both build the same platform_ids/platform_unassigned query params the
// same way.
const UNASSIGNED_PLATFORM_FILTER = '__unassigned__'
// Full known source_type vocabulary across all three get_sibs_fraud_entries
// branches (see that RPC): branch_multibanco's CASE yields raw_entry/
// processed_entry/brand_campaign, branch_processed's processed_entries_flat
// yields processed_entry/sCalls, branch_calls hardcodes sCalls. Used as
// EmailsSentTable's Source_Type filter options instead of deriving them from
// the currently-loaded emailEntries -- once that filter is pushed server-side
// (see AlertsAnalysisView's emailSourceTypeFilter/fetchAlertedEmails),
// emailEntries only ever contains rows matching whatever's already selected,
// so deriving the option list from it would make every option but the
// selected one disappear the moment a filter is applied.
const EMAIL_SOURCE_TYPE_OPTIONS = ['raw_entry', 'processed_entry', 'brand_campaign', 'sCalls']
// Options list a platform FilterDropdown should offer, prepending the
// Unassigned sentinel to the real catalog — same "options come from the
// static catalog, not from currently-loaded entries" reasoning as before
// (see EMAIL_SOURCE_TYPE_OPTIONS' own comment): once this filter is
// server-side, deriving choices from what's already loaded would hide every
// option but the one(s) currently selected.
function platformFilterOptions(catalog: Catalog): string[] {
  return [UNASSIGNED_PLATFORM_FILTER, ...catalog.platforms.map((p) => String(p.id))]
}
function platformFilterOptionLabel(catalog: Catalog): (value: string) => string {
  const byId = new Map(catalog.platforms.map((p) => [String(p.id), p.name_long ?? p.name]))
  return (value) => (value === UNASSIGNED_PLATFORM_FILTER ? 'Unassigned' : (byId.get(value) ?? value))
}
function platformFilterToQueryParams(platformFilter: string[]): { platform_ids?: string; platform_unassigned?: string } {
  // FilterDropdown's "(Select All)" checkbox can now toggle to explicitly
  // "nothing selected" (see FILTER_NONE_SELECTED) — every other consumer of
  // an activeFilters array handles that sentinel for free since it never
  // .includes()/=ANY()-matches real data, but this one can't: the ids below
  // get sent straight through parseInt, and parseInt(FILTER_NONE_SELECTED)
  // is NaN, which Postgres can't parse as an integer array element. -1 is a
  // real, always-matches-zero-rows integer instead (platform.id is always a
  // positive serial, confirmed via the platform table's own min(id) = 1),
  // so it produces the same "show nothing" result without a bad request.
  if (platformFilter.length === 1 && platformFilter[0] === FILTER_NONE_SELECTED) {
    return { platform_ids: '-1' }
  }
  const unassigned = platformFilter.includes(UNASSIGNED_PLATFORM_FILTER)
  const ids = platformFilter.filter((v) => v !== UNASSIGNED_PLATFORM_FILTER)
  return {
    ...(ids.length > 0 ? { platform_ids: ids.join(',') } : {}),
    ...(unassigned ? { platform_unassigned: '1' } : {}),
  }
}
// Same matching rule as platformFilterToQueryParams above, applied client-side
// instead of pushed into an RPC — used by AlertsAnalysisView's top-of-dashboard
// platform filter to scope chartSource (the chart-mode fetch already carries
// platform_id/platform_namelong per row, see SibsChartEntry), rather than
// requiring a new server param on every dashboard RPC just to isolate one
// platform's fraud wave.
function matchesPlatformFilter(platformId: number | null | undefined, platformFilter: string[]): boolean {
  if (platformFilter.length === 0) return true
  if (platformFilter.length === 1 && platformFilter[0] === FILTER_NONE_SELECTED) return false
  const unassigned = platformFilter.includes(UNASSIGNED_PLATFORM_FILTER)
  const ids = platformFilter.filter((v) => v !== UNASSIGNED_PLATFORM_FILTER).map((v) => parseInt(v, 10))
  return platformId == null ? unassigned : ids.includes(platformId)
}

// Standalone top-of-dashboard platform filter — same options/labels/sentinel
// as the platform_id column filter (platformFilterOptions/platformFilterOptionLabel
// above), just rendered as its own button+popover instead of a table column
// header, since AlertsAnalysisView's chart widgets sit above any table. Same
// "click outside to close" pattern as TimeRangePicker below.
function PlatformFilterPicker({
  catalog,
  value,
  onChange,
  isDark,
  border,
  muted,
}: {
  catalog: Catalog
  value: string[]
  onChange: (v: string[]) => void
  isDark: boolean
  border: string
  muted: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const options = platformFilterOptions(catalog)
  const labelFor = platformFilterOptionLabel(catalog)
  const panel = 'border-border bg-card text-muted-foreground'
  const active = value.length > 0

  const display = value.length === 0
    ? 'All platforms'
    : value.length === 1 && value[0] === FILTER_NONE_SELECTED
      ? 'No platform'
      : value.length === 1
        ? labelFor(value[0])
        : `${value.length} platforms`

  const toggle = (opt: string) => {
    const withoutNoneSelected = value.filter((v) => v !== FILTER_NONE_SELECTED)
    onChange(withoutNoneSelected.includes(opt) ? withoutNoneSelected.filter((v) => v !== opt) : [...withoutNoneSelected, opt])
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex h-7 items-center gap-2 border px-2.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${border} ${active ? 'border-signal text-signal' : muted} hover:border-signal hover:text-signal`}
      >
        <Filter className="h-3.5 w-3.5" />
        <span className="max-w-36 truncate">{display}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={`absolute right-0 top-full z-40 mt-1 max-h-72 w-56 overflow-y-auto rounded border p-1 shadow-notice ${panel}`}>
          <button
            type="button"
            onClick={() => { onChange([]); setOpen(false) }}
            className={`flex w-full items-center rounded px-2 py-1.5 text-left font-mono text-[11px] transition-colors ${value.length === 0 ? 'bg-signal-surface text-signal' : `${muted} hover:bg-secondary hover:text-signal`}`}
          >
            All platforms
          </button>
          <div className={`my-1 border-t border-border`} />
          {options.map((opt) => (
            <label key={opt} className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 font-mono text-[11px] transition-colors hover:bg-secondary ${muted}`}>
              <input
                type="checkbox"
                checked={value.includes(opt)}
                onChange={() => toggle(opt)}
                className="h-3 w-3 accent-sky-500"
              />
              <span className="truncate">{labelFor(opt)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

function claimCount(v: any): number {
  if (v == null) return 0
  if (typeof v === 'number') return v
  if (Array.isArray(v)) return v.length
  if (typeof v === 'object') return Object.keys(v).length
  const n = parseInt(String(v), 10)
  return isNaN(n) ? 0 : n
}

function entryLevel(e: SibsEntry): 'critical' | 'warning' | 'info' {
  const claims = claimCount(e.scam_claims)
  if (e.entry_type === 'ENTITY_REFERENCE' || claims >= 5) return 'critical'
  if (claims >= 2) return 'warning'
  return 'info'
}

function entryIdentifier(e: SibsEntry): string {
  return e.user_entry_id ?? e.id ?? 'unknown'
}

function entryTitle(e: SibsEntry): string {
  if (e.entry_type === 'ENTITY_REFERENCE')
    return `Entity Reference · ${e.entity_reference_entity ?? 'Unknown'}`
  if (e.entry_type === 'MBWAY')
    return `MB Way · ${e.sender ?? e.platform_namelong ?? 'unknown sender'}`
  if (e.beneficiario)
    return `Multibanco · ${e.beneficiario}`
  return `Fraud Report · ${entryIdentifier(e)}`
}

// Walks up from a DOM node to find its nearest actually-scrolling ancestor —
// not just one styled overflow:auto (several wrapper divs are, whether or not
// they currently have overflow content), but one where scrollHeight actually
// exceeds clientHeight right now. Module-level since it doesn't depend on any
// component state, and needs a stable identity for the callback ref that uses
// it below — an inline closure would get a new identity every render, which
// would make that ref re-fire (and re-run its hide/restore-scroll logic) on
// every re-render instead of only on true mount/unmount.
function getScrollParent(start: HTMLElement | null): HTMLElement | null {
  let el = start?.parentElement ?? null
  while (el) {
    if (/(auto|scroll)/.test(getComputedStyle(el).overflowY) && el.scrollHeight > el.clientHeight) return el
    el = el.parentElement
  }
  return null
}

function buildSibsChartData(
  entries: SibsChartEntry[],
  dispatchLogs: Array<{ created_at: string }>,
  fromDate: Date,
  days: number,
) {
  const dayMs = 86400000
  const origin = new Date(fromDate)
  origin.setHours(0, 0, 0, 0)
  const map = new Map<string, { entity_ref: number; other: number; dispatched: number; sent: number; legitimate: number }>()
  for (let i = 0; i < days; i++) {
    const d = new Date(origin.getTime() + i * dayMs)
    map.set(`${d.getDate()}/${d.getMonth() + 1}`, { entity_ref: 0, other: 0, dispatched: 0, sent: 0, legitimate: 0 })
  }
  for (const e of entries) {
    const d = parseDbTimestamp(e.created_at)
    if (!d) continue
    const key = `${d.getDate()}/${d.getMonth() + 1}`
    const bucket = map.get(key)
    if (bucket) {
      if (e.entry_type === 'ENTITY_REFERENCE') bucket.entity_ref++
      else bucket.other++
      if (e.email_sent) bucket.dispatched++
    }
  }
  for (const log of dispatchLogs) {
    const d = parseDbTimestamp(log.created_at)
    if (!d) continue
    const key = `${d.getDate()}/${d.getMonth() + 1}`
    const bucket = map.get(key)
    if (bucket) bucket.sent++
  }
  return Array.from(map.entries()).map(([date, counts]) => ({
    date,
    ...counts,
    legitimate: Math.max(0, counts.entity_ref + counts.other - counts.dispatched),
    fraudRatio: counts.entity_ref + counts.other > 0
      ? Math.round((counts.dispatched / (counts.entity_ref + counts.other)) * 100)
      : 0,
  }))
}

function buildFraudWaveData(
  entries: SibsChartEntry[],
  fraudLogs: SibsDispatchLog[],
  fromDate: Date,
  days: number,
  unmitigatedActiveDays: number,
): FraudWaveDatum[] {
  const dayMs = 86400000
  const origin = new Date(fromDate)
  origin.setHours(0, 0, 0, 0)
  const map = new Map<string, FraudWaveDatum>()
  for (let i = 0; i < days; i++) {
    const d = new Date(origin.getTime() + i * dayMs)
    map.set(`${d.getDate()}/${d.getMonth() + 1}`, { date: `${d.getDate()}/${d.getMonth() + 1}`, fraudCases: 0, takenDown: 0, live: 0, unmitigatedLive: 0 })
  }
  // fraudCases bucketed by entry creation date, same field/predicate as the
  // Volume chart's "Fraudulent" bars (buildSibsChartData's `dispatched`
  // counter) — this used to bucket by the dispatch log's created_at
  // instead, which is when the case got resolved, not when it happened.
  // That's a different (usually later) day, so the wave's bumps landed
  // under the wrong x-axis position relative to the Legitimate-Fraudulent
  // chart above it.
  for (const e of entries) {
    if (!e.email_sent) continue
    const d = parseDbTimestamp(e.created_at)
    if (!d) continue
    const bucket = map.get(`${d.getDate()}/${d.getMonth() + 1}`)
    if (bucket) bucket.fraudCases++
  }
  // takenDown stays bucketed by dispatch date — when the case actually got
  // resolved, which can genuinely lag behind detection (see
  // medianProtectionMinutes) instead of always matching fraudCases 1:1.
  for (const log of fraudLogs) {
    if (log.status && log.status !== 'sent') continue
    const d = parseDbTimestamp(log.created_at)
    if (!d) continue
    const bucket = map.get(`${d.getDate()}/${d.getMonth() + 1}`)
    if (!bucket) continue
    bucket.takenDown++
  }
  const values = Array.from(map.values())
  let cumulativeFraud = 0
  let cumulativeTakenDown = 0
  const holdDays = Math.max(0, Math.round(unmitigatedActiveDays))
  for (let i = 0; i < values.length; i++) {
    const day = values[i]
    cumulativeFraud += day.fraudCases
    cumulativeTakenDown += day.takenDown
    day.live = Math.max(0, cumulativeFraud - cumulativeTakenDown)
    day.unmitigatedLive = holdDays === 0
      ? 0
      : values
        .slice(Math.max(0, i - holdDays + 1), i + 1)
        .reduce((sum, d) => sum + d.fraudCases, 0)
  }
  return values
}

export type WeekdayHourDatum = { day: number; hour: number; count: number }

// Same "fraud = dispatched" predicate and created_at (detection time, not
// dispatch time) bucketing as buildFraudWaveData's own fraudCases above, just
// bucketed by weekday+hour-of-day instead of by calendar date — reads local
// (browser) time, matching every other date bucketing in this file (see
// buildSibsChartData's own d.getDate()/d.getMonth()). day is Monday-first
// (0=Mon..6=Sun) since that's how the heatmap's own x-axis reads — JS's
// getDay() is Sunday-first (0=Sun), re-indexed here rather than at every call
// site.
function buildWeekdayHourHeatmap(entries: SibsChartEntry[]): WeekdayHourDatum[] {
  const counts: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0))
  for (const e of entries) {
    if (!e.email_sent) continue
    const d = parseDbTimestamp(e.created_at)
    if (!d) continue
    const day = (d.getDay() + 6) % 7
    counts[day][d.getHours()]++
  }
  const out: WeekdayHourDatum[] = []
  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) out.push({ day, hour, count: counts[day][hour] })
  }
  return out
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)


// Same 5 buckets/labels get_sibs_protection_latency_buckets returns server-
// side (ProtectionLatencyCard keys colors off these exact strings — see
// LATENCY_COLORS in charts/ProtectionLatencyCard) — shared so CampaignsView's
// own local/replay aggregate and AlertsAnalysisView's platform-filtered
// local aggregate bucket minutes into buckets identically to what the server
// RPC would, instead of each view re-deriving its own boundaries.
const LATENCY_BUCKET_ORDER = ['<30 min', '30-60 min', '1-2h', '2-6h', '>6h']
function latencyBucketForMinutes(min: number): string {
  return min < 30 ? '<30 min' : min < 60 ? '30-60 min' : min < 120 ? '1-2h' : min < 360 ? '2-6h' : '>6h'
}

function formatDurationMinutes(minutes: number | null): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—'
  const rounded = Math.max(0, Math.round(minutes))
  if (rounded < 60) return `${rounded} min`
  const hours = Math.floor(rounded / 60)
  const mins = rounded % 60
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`
}

const toNumber = (value: number | string | null | undefined) => Number(value ?? 0) || 0

const fmtDate = (iso: string) => {
  const d = parseDbTimestamp(iso)
  if (!d) return '—'
  const time = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit' }).format(d)
  const date = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
  return `${time} ${date}`
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CAMP_SEV_ICON = { critical: AlertCircle, warning: AlertTriangle, info: Info } as const
const CAMP_SEV_TONE: Record<string, string> = {
  critical: 'text-destructive',
  warning:  'text-warning',
  info:     'text-signal',
}

const PAGE_SIZE = 30

// ─── Sub-components ───────────────────────────────────────────────────────────

// ─── Pilot month stepper ────────────────────────────────────────────────────
// Hardcoded to the current pilot's window (April–September 2026) rather than
// a generic year/month picker — this is a temporary navigation aid for
// stepping through that specific pilot, not a permanent feature. Sits next to
// TimeRangePicker as an alternative, faster way to set chartFrom/chartTo:
// open-the-dropdown-and-type is slower than clicking Next six times in a row
// to watch alert volume evolve month over month.

// For a closed month (fully in the past) this is the full number of days in
// it; for the currently open month it's only the days elapsed so far
// (through today) rather than the full month, so a rate computed against it
// reflects "how it's trending so far" instead of being diluted by days that
// haven't happened yet; for a month that hasn't started, 0 (no rate).
function daysElapsedInMonth(month: (typeof PILOT_MONTHS)[number]): number {
  const today = todayIso()
  if (today < month.from) return 0
  const from = new Date(month.from + 'T00:00:00')
  const endBound = today < month.to ? today : month.to
  const end = new Date(endBound + 'T00:00:00')
  return Math.round((end.getTime() - from.getTime()) / 86400000) + 1
}

// Metrics shown as rows in SibsPilotOverviewView's table (months as columns).
// `get` returns null (rendered as "—") rather than routing through
// toNumber's null-to-0 coercion when the underlying RPC section itself is
// missing (a partial dashboard-RPC failure) — 0 would misleadingly claim a
// confirmed zero count instead of "unknown".
// rpcKey matches the keys campaignManagement.ts's dashboard=1 handler labels
// its per-RPC `errors` object with — lets a cell distinguish "this RPC
// errored (statement timeout etc.)" from "this RPC succeeded and genuinely
// returned no rows" instead of collapsing both into the same "—".
const PILOT_METRICS: Array<{
  key: string
  label: string
  rpcKey: string
  get: (d: SibsDashboardAggregate, month: (typeof PILOT_MONTHS)[number]) => number | null
  format: (v: number) => string
}> = [
  { key: 'total_entries', label: 'Total Entries', rpcKey: 'overview', get: (d) => d.overview ? toNumber(d.overview.total_entries) : null, format: (v) => String(v) },
  { key: 'alerts_sent', label: 'Alerts Sent', rpcKey: 'overview', get: (d) => d.overview ? toNumber(d.overview.total_email_sent) : null, format: (v) => String(v) },
  // For the currently-open month, daysElapsedInMonth counts only through
  // today rather than the full month — an in-progress month otherwise reads
  // as a much lower rate than it's actually trending at.
  {
    key: 'alerts_per_day',
    label: 'Alerts Sent / Day',
    rpcKey: 'overview',
    get: (d, month) => {
      if (!d.overview) return null
      const days = daysElapsedInMonth(month)
      if (days <= 0) return null
      return toNumber(d.overview.total_email_sent) / days
    },
    format: (v) => v.toFixed(1),
  },
  { key: 'unique_beneficiaries', label: 'Unique Beneficiaries', rpcKey: 'overview', get: (d) => d.overview ? toNumber(d.overview.unique_beneficiaries) : null, format: (v) => String(v) },
  { key: 'fraudulent_beneficiaries', label: 'Fraudulent Beneficiaries', rpcKey: 'beneficiaryCounts', get: (d) => d.beneficiaryCounts ? toNumber(d.beneficiaryCounts.fraudulent_unique_beneficiaries) : null, format: (v) => String(v) },
  { key: 'unique_entity_references', label: 'Unique Entity References', rpcKey: 'overview', get: (d) => d.overview ? toNumber(d.overview.unique_entity_references) : null, format: (v) => String(v) },
  { key: 'median_protection', label: 'Median Protection Time', rpcKey: 'medianProtection', get: (d) => d.medianProtection?.median_hours == null ? null : toNumber(d.medianProtection.median_hours) * 60, format: (v) => formatDurationMinutes(v) },
]

function PilotMonthStepper({
  from,
  to,
  disabled,
  border,
  muted,
  onApply,
}: {
  from: string
  to: string
  disabled?: boolean
  border: string
  muted: string
  onApply: (from: string, to: string) => void
}) {
  // A closed month is applied as its full from..to range, so exact equality
  // matches it. The currently-open month is applied as from..today instead
  // (AlertsAnalysisView's "to date" default) — its `to` never equals the
  // month's own `to` until the month actually ends, so that case is matched
  // separately by "to is today, and today falls in this month". Without
  // this, appliedIndex sits at -1 for the entire open month: the label shows
  // the "Pilot month" placeholder instead of naming it, and worse, Prev/Next
  // both treat -1 as "off the end" and jump to the wrong end of the array
  // (Prev to the last month, Next to the first) instead of stepping
  // adjacent to the month actually being viewed.
  const appliedIndex = PILOT_MONTHS.findIndex((m) => {
    if (from !== m.from) return false
    if (to === m.to) return true
    const today = todayIso()
    return to === today && today >= m.from && today <= m.to
  })
  // Cycling months fans out into several concurrent RPC fetches downstream
  // (chart, dashboard aggregate, logs — see AlertsAnalysisView), so calling
  // onApply on every click fires all of them once per month stepped
  // through, not once for the month the user actually lands on.
  // The label still updates immediately off this local index for responsive
  // feel; only the onApply call (and everything it triggers) is debounced.
  const [pendingIndex, setPendingIndex] = useState(appliedIndex)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setPendingIndex(appliedIndex)
  }, [appliedIndex])

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
  }, [])

  const goTo = (index: number) => {
    const month = PILOT_MONTHS[index]
    if (!month) return
    setPendingIndex(index)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => onApply(month.from, month.to), 400)
  }

  return (
    <div className={`flex h-7 items-center border font-mono text-[10px] uppercase tracking-widest ${border}`}>
      <button
        type="button"
        disabled={disabled || pendingIndex === 0}
        onClick={() => goTo((pendingIndex === -1 ? PILOT_MONTHS.length : pendingIndex) - 1)}
        title="Previous pilot month"
        className={`flex h-full items-center px-1.5 transition-colors disabled:opacity-30 ${muted} hover:text-signal`}
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <span className={`min-w-16 px-1 text-center ${pendingIndex >= 0 ? 'text-signal' : muted}`}>
        {pendingIndex >= 0 ? PILOT_MONTHS[pendingIndex].label : 'Pilot month'}
      </span>
      <button
        type="button"
        disabled={disabled || pendingIndex === PILOT_MONTHS.length - 1}
        onClick={() => goTo(pendingIndex + 1)}
        title="Next pilot month"
        className={`flex h-full items-center px-1.5 transition-colors disabled:opacity-30 ${muted} hover:text-signal`}
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}


function CampMeta({ k, v, isDark = false }: { k: string; v: string; isDark?: boolean }) {
  return (
    <div>
      <div className={`font-mono text-[9px] uppercase tracking-widest text-subtle`}>{k}</div>
      <div className={`font-mono text-[12px] truncate mt-0.5 text-foreground`}>{v}</div>
    </div>
  )
}

export function BeneficiaryRankingCard({
  eyebrow,
  title,
  items,
  accent,
  isDark,
  className = 'min-h-72',
  limit = 8,
  dense = false,
  uniqueCount,
  emptyLabel = 'No beneficiaries in range',
}: {
  eyebrow: string
  title: string
  items: BeneficiaryRankItem[]
  accent: string
  isDark: boolean
  className?: string
  limit?: number
  dense?: boolean
  uniqueCount?: number
  /** Despite the component's name, callers rank arbitrary things by count (see CTT's Custom Mail Alert view ranking senders/domains) — override this so the empty state doesn't say "beneficiaries" for those. */
  emptyLabel?: string
}) {
  const previousRef = useRef<Map<string, { count: number; rank: number }>>(new Map())
  const initializedRef = useRef(false)
  const [changedIds, setChangedIds] = useState<Set<string>>(new Set())
  const [moves, setMoves] = useState<Record<string, 'up' | 'down'>>({})

  useEffect(() => {
    const previous = previousRef.current
    const next = new Map(items.map((item) => [item.key, { count: item.count, rank: item.rank }]))
    if (initializedRef.current) {
      const changed = new Set<string>()
      const nextMoves: Record<string, 'up' | 'down'> = {}
      for (const item of items) {
        const before = previous.get(item.key)
        if (!before) {
          changed.add(item.key)
          nextMoves[item.key] = 'up'
          continue
        }
        if (before.count !== item.count || before.rank !== item.rank) changed.add(item.key)
        if (before.rank !== item.rank) nextMoves[item.key] = item.rank < before.rank ? 'up' : 'down'
      }
      setChangedIds(changed)
      setMoves(nextMoves)
      const timer = setTimeout(() => {
        setChangedIds(new Set())
        setMoves({})
      }, 900)
      previousRef.current = next
      return () => clearTimeout(timer)
    }
    initializedRef.current = true
    previousRef.current = next
  }, [items])

  const visibleItems = items.slice(0, limit)
  const maxCount = Math.max(1, ...visibleItems.map((item) => item.count))
  const card = 'border-border bg-card'
  const muted = 'text-subtle'
  const text = 'text-foreground'
  const rail = 'bg-secondary'
  const flash = 'bg-signal-surface'

  return (
    <div className={`flex min-w-0 flex-col overflow-hidden border px-4 py-3 ${card} ${className}`}>
      <div className={`${dense ? 'mb-1' : 'mb-3'} flex shrink-0 items-start justify-between gap-3`}>
        <div className="min-w-0">
          <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>{eyebrow}</div>
          <div className={`${dense ? 'mt-0 text-[12px]' : 'mt-1 text-base'} truncate font-semibold tracking-tight ${text}`}>{title}</div>
        </div>
        <div className={`shrink-0 text-right font-mono text-[10px] ${muted}`}>
          {uniqueCount != null
            ? <><span style={{ color: accent }}>{uniqueCount}</span> unique</>
            : visibleItems.length ? `top ${visibleItems.length}` : 'no data'}
        </div>
      </div>

      <div className={`flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden pr-1 ${dense ? 'gap-1' : 'gap-2'}`}>
        {visibleItems.length === 0 ? (
          <div className={`flex min-h-0 flex-1 items-center justify-center border border-dashed text-[11px] font-mono border-border text-muted-foreground`}>
            {emptyLabel}
          </div>
        ) : visibleItems.map((item) => {
          const isChanged = changedIds.has(item.key)
          const move = moves[item.key]
          return (
            <div
              key={item.key}
              className={`rounded-md px-2 ${dense ? 'py-0.5' : 'py-1.5'} transition-all duration-500 ${isChanged ? `${flash} ${move === 'up' ? '-translate-y-0.5' : move === 'down' ? 'translate-y-0.5' : ''}` : ''}`}
            >
              <div className={`${dense ? 'mb-0.5 gap-2' : 'mb-1 gap-3'} flex items-center`}>
                <span className={`w-5 shrink-0 font-mono text-[10px] tabular-nums ${muted}`}>{String(item.rank).padStart(2, '0')}</span>
                <span className={`min-w-0 flex-1 truncate ${dense ? 'text-[12px]' : 'text-sm'} font-semibold ${text}`}>{item.label}</span>
                <span className={`shrink-0 font-mono text-[12px] tabular-nums ${text}`}>{item.count}</span>
                <span className={`flex h-4 w-4 shrink-0 items-center justify-center ${move === 'up' ? 'text-success' : move === 'down' ? 'text-rose-500' : muted}`}>
                  {move === 'up' ? <ArrowUp className="h-3 w-3" /> : move === 'down' ? <ArrowDown className="h-3 w-3" /> : null}
                </span>
              </div>
              <div className={`ml-8 h-1 rounded-full ${rail}`}>
                <div
                  className="h-1 rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${Math.max(4, (item.count / maxCount) * 100)}%`, backgroundColor: accent }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

type LegitimateFraudulentWaveDatum = {
  date: string
  legitimate: number | null
  dispatched: number | null
  sent?: number | null
  entity_ref?: number
  other?: number
}

type LegitimateFraudulentWaveChartProps = {
  data: LegitimateFraudulentWaveDatum[]
  sourceLength: number
  yMax: number
  isDark: boolean
  isReplaying: boolean
  replayDayIdxShifted: number
  chartFrom: string
  chartTo: string
  muted: string
  text: string
  chartDataLoading?: boolean
  chartDataError?: string | null
  onRetry?: () => void
  onExpand?: () => void
  className?: string
  chartId?: string
  animationNonce?: number
}

function LegitimateFraudulentWaveChart({
  data,
  sourceLength,
  yMax,
  isDark,
  isReplaying,
  replayDayIdxShifted,
  chartFrom,
  chartTo,
  muted,
  text,
  chartDataLoading = false,
  chartDataError = null,
  onRetry,
  onExpand,
  className = 'h-full',
  chartId = 'main',
  animationNonce = 0,
}: LegitimateFraudulentWaveChartProps) {
  const rafRef = useRef<number | null>(null)
  const [reveal, setReveal] = useState(1)
  const showDots = sourceLength <= 14
  const bg = 'var(--card)'
  const tickInterval = Math.max(0, Math.floor(sourceLength / 7) - 1)
  const tooltipCls = 'border-border bg-card text-muted-foreground'
  const legitGradientId = `sibsGradLegit-${chartId}`
  const fraudGradientId = `sibsGradDisp-${chartId}`
  const buttonCls = 'text-muted-foreground hover:bg-secondary hover:text-muted-foreground'

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  useEffect(() => {
    if (isReplaying || data.length === 0) {
      setReveal(1)
      return
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      setReveal(1)
      return
    }

    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    setReveal(0)
    const startedAt = performance.now()
    const durationMs = 900

    const tick = (now: number) => {
      const elapsed = Math.min(1, (now - startedAt) / durationMs)
      setReveal(easeOutCubic(elapsed))
      if (elapsed < 1) rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [animationNonce, data.length, isReplaying])

  const animatedData = useMemo(() => {
    if (isReplaying) return data
    const visibleCount = Math.max(1, Math.ceil(data.length * reveal))
    return data.map((d, i) => {
      if (i < visibleCount) return d
      return { ...d, legitimate: null, dispatched: null }
    })
  }, [data, isReplaying, reveal])

  const makeDot = (color: string, r = 2.5) => (props: any) => {
    const { cx, cy, index, value } = props
    if (value == null || value === 0) return <g key={index} />
    const isLatest = isReplaying && index === replayDayIdxShifted
    if (!showDots && !isLatest) return <g key={index} />
    return (
      <g key={index}>
        {isLatest && (
          <circle cx={cx} cy={cy} r={r} fill={color}>
            <animate attributeName="r" values={`${r};${r + 9}`} dur="1s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.45;0" dur="1s" repeatCount="indefinite" />
          </circle>
        )}
        <circle cx={cx} cy={cy} r={r} fill={bg} stroke={color} strokeWidth={1.5} />
      </g>
    )
  }

  return (
    <div className={`relative flex min-w-0 flex-col overflow-hidden border border-border bg-card ${className}`}>
      {chartDataLoading && !chartDataError && (
        <div className="pointer-events-none absolute inset-0 z-20 ">
          <div className={`absolute inset-0 animate-pulse bg-signal-surface/60`} />
          <div className={`absolute right-3 top-3 flex items-center gap-2 rounded-[4px] border px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest shadow-sm border-signal/30 bg-card text-signal`}>
            <Loader2 className="h-3 w-3 animate-spin" />
            Updating
          </div>
        </div>
      )}

      <div className="flex shrink-0 items-start justify-between gap-4 px-4 pt-3 pb-2">
        <div className="min-w-0">
          <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Legitimate · Fraudulent</div>
          <div className={`mt-0.5 text-base font-semibold tracking-tight ${text}`}>{periodLabel(chartFrom, chartTo)}</div>
        </div>
        <div className={`flex min-w-0 shrink items-center justify-end gap-3 ${onExpand ? 'pr-8' : ''} font-mono text-[9px] ${muted}`}>
          <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-signal" />Legitimate</span>
          <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-destructive" />Fraudulent</span>
        </div>
      </div>

      {onExpand && (
        <button
          onClick={onExpand}
          title="Expand chart"
          className={`absolute right-3 top-3 z-10 flex h-6 w-6 items-center justify-center rounded transition-colors ${buttonCls}`}
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      )}

      <div className="min-h-0 flex-1">
        {chartDataError ? (
          <RetryBanner
            message={chartDataError}
            onRetry={onRetry ?? (() => {})}
            retrying={chartDataLoading}
            isDark={isDark}
          />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={animatedData} margin={{ top: 4, right: 8, left: -24, bottom: 4 }}>
              <defs>
                <linearGradient id={legitGradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="var(--signal)" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="var(--signal)" stopOpacity={0.06} />
                </linearGradient>
                <linearGradient id={fraudGradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="var(--destructive)" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="var(--destructive)" stopOpacity={0.06} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} interval={tickInterval} padding={{ left: 16, right: 8 }} tickFormatter={v => v === '' ? '' : v} />
              <YAxis yAxisId="left" orientation="left" domain={[0, yMax]} tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={({ active, payload, label: lbl }: any) => {
                if (!active || !payload?.length) return null
                return (
                  <div className={`rounded border px-3 py-2 text-[11px] font-mono shadow-notice ${tooltipCls}`}>
                    <div className={`mb-1 font-semibold ${muted}`}>{lbl}</div>
                    {payload.map((p: any) => (
                      <div key={p.dataKey} className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} />
                        <span>{({ legitimate: 'Legitimate', dispatched: 'Fraudulent' } as Record<string, string>)[p.dataKey] ?? p.dataKey}</span>
                        <span className={`ml-auto pl-4 font-semibold ${text}`}>{p.value}</span>
                      </div>
                    ))}
                  </div>
                )
              }} />
              <Area yAxisId="left" type="monotone" dataKey="legitimate" stroke="var(--signal)" strokeWidth={1.5} fill={`url(#${legitGradientId})`} dot={makeDot('var(--signal)')} activeDot={(p: any) => p.value > 0 ? <circle key={p.index} cx={p.cx} cy={p.cy} r={4} fill="var(--signal)" stroke={bg} strokeWidth={1.5} /> : <g key={p.index} />} isAnimationActive={false} />
              <Area yAxisId="left" type="monotone" dataKey="dispatched" stroke="var(--destructive)" strokeWidth={1.5} fill={`url(#${fraudGradientId})`} dot={makeDot('var(--destructive)')} activeDot={(p: any) => p.value > 0 ? <circle key={p.index} cx={p.cx} cy={p.cy} r={4} fill="var(--destructive)" stroke={bg} strokeWidth={1.5} /> : <g key={p.index} />} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

function LegitimateFraudulentMaximizedWindow({
  onClose,
  ...chartProps
}: LegitimateFraudulentWaveChartProps & { onClose: () => void }) {
  const shellRef = useRef<HTMLDivElement | null>(null)
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [animationNonce, setAnimationNonce] = useState(0)
  const isDark = chartProps.isDark
  const border = 'border-border'
  const shellBg = 'bg-secondary'
  const headerBg = 'bg-card'
  const text = 'text-foreground'
  const muted = 'text-subtle'

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  useEffect(() => {
    const el = shellRef.current
    if (!el) return
    let first = true
    const observer = new ResizeObserver(() => {
      if (first) {
        first = false
        setAnimationNonce((value) => value + 1)
        return
      }
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current)
      resizeTimerRef.current = setTimeout(() => {
        setAnimationNonce((value) => value + 1)
      }, 140)
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current)
    }
  }, [])

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-overlay p-4 backdrop-blur-sm">
      <div
        ref={shellRef}
        className={`relative flex flex-col overflow-hidden border shadow-2xl ${border} ${shellBg}`}
        style={{
          width: 'min(1180px, 92vw)',
          height: 'min(760px, 84vh)',
          minWidth: 520,
          minHeight: 360,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 32px)',
          resize: 'both',
        }}
      >
        <div className={`flex shrink-0 items-center justify-between border-b px-3 py-2 ${border} ${headerBg}`}>
          <div>
            <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Expanded chart</div>
            <div className={`text-sm font-semibold ${text}`}>Legitimate · Fraudulent</div>
          </div>
          <button
            onClick={onClose}
            title="Close expanded chart"
            className={`flex h-7 w-7 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 p-3">
          <LegitimateFraudulentWaveChart {...chartProps} chartDataLoading={false} onExpand={undefined} className="h-full" chartId="expanded" animationNonce={animationNonce} />
        </div>
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute bottom-1 right-1 h-5 w-5 border-b-2 border-r-2 border-border`}
        />
      </div>
    </div>
  )
}

type FraudWaveChartProps = {
  data: FraudWaveDatum[]
  isDark: boolean
  replayActive: boolean
  chartFrom: string
  replayMaxDate: string | null
  medianProtectionMinutes: number | null
  unmitigatedActiveDays: number
  onUnmitigatedActiveDaysChange: (days: number) => void
  className?: string
  animationNonce?: number
  expanded?: boolean
}

export function FraudWaveChart({
  data,
  isDark,
  replayActive,
  chartFrom,
  replayMaxDate,
  medianProtectionMinutes,
  unmitigatedActiveDays,
  onUnmitigatedActiveDaysChange,
  className = 'h-72',
  animationNonce = 0,
  expanded = false,
}: FraudWaveChartProps) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const hasAnimatedRef = useRef(false)
  const rafRef = useRef<number | null>(null)
  const [reveal, setReveal] = useState(1)
  const [fall, setFall] = useState(1)
  const [settled, setSettled] = useState(true)
  const [domainMax, setDomainMax] = useState(4)
  // Own local toggle, same "no parent state needed for a pure display
  // preference" reasoning as FraudHeatmapCard's own showColors — hides the
  // "Unchecked active days" slider row without touching
  // unmitigatedActiveDays itself, so the chart keeps rendering with
  // whatever value was last set even while the control is hidden.
  const [showUnmitigatedSlider, setShowUnmitigatedSlider] = useState(true)
  // Same pattern — hides just the "N fraud neutralized" headline number
  // (the takedown count), not the Taken down series/legend/area on the
  // chart itself.
  const [showNeutralizedCount, setShowNeutralizedCount] = useState(true)

  const replayDayIdx = replayActive && replayMaxDate
    ? Math.floor((new Date(replayMaxDate + 'T00:00').getTime() - new Date(chartFrom + 'T00:00').getTime()) / 86400000)
    : Infinity
  const visibleData = replayActive
    ? data.filter((_, i) => i <= replayDayIdx)
    : data

  const metrics = useMemo(() => {
    let cumulativeFraud = 0
    let cumulativeTakenDown = 0
    let peakUnchecked = 0
    let peakLive = 0
    let peakDailyFraud = 0
    for (const d of visibleData) {
      peakDailyFraud = Math.max(peakDailyFraud, d.fraudCases)
      cumulativeFraud += d.fraudCases
      cumulativeTakenDown += d.takenDown
      peakUnchecked = Math.max(peakUnchecked, d.unmitigatedLive)
      peakLive = Math.max(peakLive, Math.max(0, cumulativeFraud - cumulativeTakenDown))
    }
    const neutralized = visibleData.reduce((sum, d) => sum + d.takenDown, 0)
    const liveNow = Math.max(0, cumulativeFraud - cumulativeTakenDown)
    const uncheckedNow = visibleData[visibleData.length - 1]?.unmitigatedLive ?? 0
    const peakIndex = visibleData.reduce((best, d, i) => d.unmitigatedLive > visibleData[best]?.unmitigatedLive ? i : best, 0)
    return { peakUnchecked, peakLive, peakDailyFraud, neutralized, liveNow, uncheckedNow, peakIndex, totalFraud: cumulativeFraud }
  }, [visibleData])

  const runAnimation = useCallback(() => {
    if (replayActive) {
      setReveal(1)
      setFall(1)
      setSettled(true)
      return
    }
    if (hasAnimatedRef.current) {
      setReveal(1)
      setFall(1)
      setSettled(true)
      return
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion || data.length === 0) {
      hasAnimatedRef.current = true
      setReveal(1)
      setFall(1)
      setSettled(true)
      return
    }

    hasAnimatedRef.current = true
    setReveal(0)
    setFall(0)
    setSettled(false)

    const riseMs = 1100
    const holdMs = 450
    const flattenMs = 1700
    const startedAt = performance.now()

    const tick = (now: number) => {
      const elapsed = now - startedAt
      if (elapsed < riseMs) {
        setReveal(easeOutCubic(elapsed / riseMs))
        setFall(0)
      } else if (elapsed < riseMs + holdMs) {
        setReveal(1)
        setFall(0)
      } else {
        const flattenElapsed = Math.min(1, (elapsed - riseMs - holdMs) / flattenMs)
        setReveal(1)
        setFall(easeOutCubic(flattenElapsed))
      }

      if (elapsed < riseMs + holdMs + flattenMs) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        setReveal(1)
        setFall(1)
        setSettled(true)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [data.length, replayActive])

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  useEffect(() => {
    if (animationNonce === 0) return
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    hasAnimatedRef.current = false
    runAnimation()
  }, [animationNonce, runAnimation])

  useEffect(() => {
    if (replayActive) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      setReveal(1)
      setFall(1)
      setSettled(true)
      return
    }
    if (!rootRef.current) return
    if (hasAnimatedRef.current) {
      setReveal(1)
      setFall(1)
      setSettled(true)
      return
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        observer.disconnect()
        runAnimation()
      }
    }, { threshold: 0.35 })
    observer.observe(rootRef.current)
    return () => observer.disconnect()
  }, [runAnimation, replayActive])

  const animatedData = useMemo(() => {
    const visibleCount = Math.max(1, Math.ceil(data.length * reveal))
    return data.map((d, i) => {
      const hidden = i >= visibleCount || (replayActive && i > replayDayIdx)
      const unchecked = d.fraudCases + (d.unmitigatedLive - d.fraudCases) * fall
      const live = d.fraudCases + (d.live - d.fraudCases) * fall
      return {
        date: d.date,
        unchecked: hidden ? null : unchecked,
        live: hidden ? null : live,
        takenDown: hidden ? null : -d.takenDown * fall,
      }
    })
  }, [data, fall, reveal, replayActive, replayDayIdx])

  useEffect(() => {
    if (replayActive) return
    const maxAbs = Math.max(1, ...data.map(d => Math.max(d.fraudCases, d.takenDown, d.live, d.unmitigatedLive)))
    setDomainMax(Math.max(4, Math.ceil(maxAbs)))
  }, [data, replayActive])

  const yMax = domainMax
  const heroValue = Math.round(metrics.peakDailyFraud)
  const neutralized = Math.round(metrics.neutralized * fall)
  const peak = visibleData[metrics.peakIndex]
  const baseline = 'var(--border)'
  const bg = 'bg-card border-border'
  const text = 'text-foreground'
  const muted = 'text-muted-foreground'
  const tooltipCls = 'border-border bg-card text-muted-foreground'
  const chartBg = 'var(--card)'
  const showDots = data.length <= 14
  // Same leading zero-anchor column the Volume chart prepends to
  // visibleChartData — without it the two stacked charts have different
  // column counts (N vs N+1) and their x-axes drift out of alignment day by
  // day. replayDayIdxShifted mirrors the Volume chart's own +1 so the "latest"
  // dot still lines up with the real (shifted) render index.
  const anchor = { date: '', unchecked: 0, live: 0, takenDown: 0 }
  const chartData = [anchor, ...animatedData]
  const replayDayIdxShifted = replayDayIdx === Infinity ? Infinity : replayDayIdx + 1
  const makeDot = (color: string, r = 2.5) => (props: any) => {
    const { cx, cy, index, value } = props
    if (value == null || value === 0) return <g key={index} />
    const isLatest = replayActive && index === replayDayIdxShifted
    if (!showDots && !isLatest) return <g key={index} />
    return (
      <g key={index}>
        {isLatest && (
          <circle cx={cx} cy={cy} r={r} fill={color}>
            <animate attributeName="r" values={`${r};${r + 9}`} dur="1s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.45;0" dur="1s" repeatCount="indefinite" />
          </circle>
        )}
        <circle cx={cx} cy={cy} r={r} fill={chartBg} stroke={color} strokeWidth={1.5} />
      </g>
    )
  }

  return (
    <div ref={rootRef} className={`flex flex-col border overflow-hidden ${bg} ${className}`}>
      <div className="flex shrink-0 items-start justify-between gap-4 px-4 pt-3 pb-2">
        <div className="min-w-0">
          <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Fraud wave flattened</div>
          {showNeutralizedCount && (
            <div className={`mt-0.5 text-base font-semibold tracking-tight ${text}`}>
              <span className="font-mono tabular-nums">{neutralized}</span>
              <span className={`ml-2 text-[12px] font-normal ${muted}`}>fraud neutralized</span>
            </div>
          )}
          {expanded && showNeutralizedCount && (
            <div className={`mt-1 font-mono text-[11px] ${muted}`}>
              <span className={`font-semibold tabular-nums ${text}`}>{metrics.totalFraud}</span> fraudulent campaigns
            </div>
          )}
        </div>
        <div className="flex min-w-0 shrink items-center justify-end gap-2 pr-16">
          <div className={`flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-1 font-mono text-[9px] ${muted}`}>
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[#f59e0b]" />Fraud, if left unchecked</span>
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[#e34948]" />Fraud, live</span>
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[#1baf7a]" />Taken down</span>
          </div>
          <button
            type="button"
            onClick={() => setShowNeutralizedCount((v) => !v)}
            title={showNeutralizedCount ? 'Hide takedown count' : 'Show takedown count'}
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
          >
            {showNeutralizedCount ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          </button>
          {!replayActive && (
            <button
              type="button"
              onClick={() => setShowUnmitigatedSlider((v) => !v)}
              title={showUnmitigatedSlider ? 'Hide "Unchecked active days" slider' : 'Show "Unchecked active days" slider'}
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
            >
              {showUnmitigatedSlider ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            </button>
          )}
        </div>
      </div>

      {!replayActive && showUnmitigatedSlider && (
        <div className={`mx-4 mb-2 grid shrink-0 grid-cols-[1fr_auto] items-center gap-3 rounded border px-3 py-2 ${isDark ? 'border-border bg-slate-950/20' : 'border-border bg-secondary'}`}>
          <label className={`flex min-w-0 items-center gap-3 font-mono text-[9px] uppercase tracking-widest ${muted}`}>
            <span className="shrink-0">Unchecked active days</span>
            <input
              type="range"
              min={0}
              max={30}
              step={1}
              value={unmitigatedActiveDays}
              onChange={(e) => onUnmitigatedActiveDaysChange(Number(e.target.value))}
              className="min-w-0 flex-1 accent-amber-500"
            />
          </label>
          <span className={`font-mono text-[11px] font-semibold tabular-nums ${text}`}>
            {unmitigatedActiveDays}d
          </span>
        </div>
      )}

      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -24, bottom: 4 }}>
            <defs>
              <linearGradient id="fraudWaveLive" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#e34948" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#e34948" stopOpacity={0.03} />
              </linearGradient>
              <linearGradient id="fraudWaveTakenDown" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1baf7a" stopOpacity={0.03} />
                <stop offset="100%" stopColor="#1baf7a" stopOpacity={0.15} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(data.length / 7) - 1)} padding={{ left: 16, right: 8 }} />
            <YAxis domain={[-yMax, yMax]} ticks={[-yMax, -Math.round(yMax / 2), 0, Math.round(yMax / 2), yMax]} tickFormatter={(v) => String(Math.abs(Number(v)))} tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} allowDecimals={false} />
            <ReferenceLine y={0} stroke={baseline} strokeWidth={1.3} />
            <Tooltip
              cursor={false}
              wrapperStyle={{ pointerEvents: settled ? 'auto' : 'none' }}
              content={({ active, payload, label: lbl }: any) => {
                if (!settled || !active || !payload?.length) return null
                return (
                  <div className={`rounded border px-3 py-2 text-[11px] font-mono shadow-notice ${tooltipCls}`}>
                    <div className={`mb-1 font-semibold ${muted}`}>{lbl}</div>
                    {payload.map((p: any) => (
                      <div key={p.dataKey} className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} />
                        <span>{({ unchecked: `Fraud, if left unchecked for ${unmitigatedActiveDays}d`, live: 'Fraud, live', takenDown: 'Taken down' } as Record<string, string>)[p.dataKey] ?? p.dataKey}</span>
                        <span className={`ml-auto pl-4 font-semibold ${text}`}>{Math.abs(Math.round(p.value))}</span>
                      </div>
                    ))}
                  </div>
                )
              }}
            />
            <Area type="monotone" dataKey="unchecked" stroke="var(--warning)" strokeWidth={2} fill="rgba(245,158,11,0.08)" dot={makeDot('var(--warning)')} activeDot={(p: any) => p.value > 0 ? <circle key={p.index} cx={p.cx} cy={p.cy} r={4} fill="var(--warning)" stroke={chartBg} strokeWidth={1.5} /> : <g key={p.index} />} isAnimationActive={false} />
            <Area type="monotone" dataKey="live" stroke="#e34948" strokeWidth={2} fill="url(#fraudWaveLive)" dot={makeDot('var(--destructive)')} activeDot={(p: any) => p.value > 0 ? <circle key={p.index} cx={p.cx} cy={p.cy} r={4} fill="#e34948" stroke={chartBg} strokeWidth={1.5} /> : <g key={p.index} />} isAnimationActive={false} />
            <Area type="monotone" dataKey="takenDown" stroke="#1baf7a" strokeWidth={2} fill="url(#fraudWaveTakenDown)" dot={makeDot('var(--success)')} activeDot={(p: any) => p.value < 0 ? <circle key={p.index} cx={p.cx} cy={p.cy} r={4} fill="#1baf7a" stroke={chartBg} strokeWidth={1.5} /> : <g key={p.index} />} isAnimationActive={false} />
            {settled && peak && (
              <>
                <ReferenceLine x={peak.date} stroke={'var(--subtle-text)'} strokeDasharray="3 3" />
                <ReferenceDot x={peak.date} y={peak.unmitigatedLive} r={3.5} fill="var(--warning)" stroke="none" />
                <ReferenceDot x={peak.date} y={peak.live} r={4} fill="#e34948" stroke={'var(--card)'} strokeWidth={1.5} />
                <ReferenceDot x={peak.date} y={-peak.takenDown} r={3.5} fill="#1baf7a" stroke="none" />
              </>
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className={`grid shrink-0 grid-cols-2 gap-4 border-t px-4 py-3 font-mono text-[11px] border-border`}>
        <div><span className={muted}>Peak fraud cases</span> <span className={`ml-2 font-semibold ${text}`}>{heroValue}</span></div>
        <div><span className={muted}>Median to protection</span> <span className={`ml-2 font-semibold ${text}`}>{formatDurationMinutes(medianProtectionMinutes)}</span></div>
      </div>
    </div>
  )
}

function FraudWaveMaximizedWindow({
  onClose,
  ...chartProps
}: FraudWaveChartProps & { onClose: () => void }) {
  const shellRef = useRef<HTMLDivElement | null>(null)
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [animationNonce, setAnimationNonce] = useState(0)
  const isDark = chartProps.isDark
  const border = 'border-border'
  const shellBg = 'bg-secondary'
  const headerBg = 'bg-card'
  const text = 'text-foreground'
  const muted = 'text-subtle'

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  useEffect(() => {
    const el = shellRef.current
    if (!el) return
    let first = true
    const observer = new ResizeObserver(() => {
      if (first) {
        first = false
        setAnimationNonce((value) => value + 1)
        return
      }
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current)
      resizeTimerRef.current = setTimeout(() => {
        setAnimationNonce((value) => value + 1)
      }, 140)
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current)
    }
  }, [])

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-overlay p-4 backdrop-blur-sm">
      <div
        ref={shellRef}
        className={`relative flex flex-col overflow-hidden border shadow-2xl ${border} ${shellBg}`}
        style={{
          width: 'min(1180px, 92vw)',
          height: 'min(760px, 84vh)',
          minWidth: 520,
          minHeight: 360,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 32px)',
          resize: 'both',
        }}
      >
        <div className={`flex shrink-0 items-center justify-between border-b px-3 py-2 ${border} ${headerBg}`}>
          <div>
            <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Expanded chart</div>
            <div className={`text-sm font-semibold ${text}`}>Fraud wave flattened</div>
          </div>
          <button
            onClick={onClose}
            title="Close expanded chart"
            className={`flex h-7 w-7 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 p-3">
          <FraudWaveChart {...chartProps} className="h-full" animationNonce={animationNonce} expanded />
        </div>
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute bottom-1 right-1 h-5 w-5 border-b-2 border-r-2 border-border`}
        />
      </div>
    </div>
  )
}

// ─── Entry detail page ──────────────────────────────────────────────────────
// Shared by CampaignsView and AlertsAnalysisView below — both used to inline
// an identical hardcoded 6-panel EntryDetailShell here. Each caller still
// owns its own selected/sentIds/payloadRequested/getScreenshotToken state
// (they're genuinely separate, not shared across the two views) and passes
// it in as props.
//
// appendedWidgets is the new session-only "add a widget" mechanism: picked
// from SIBS_WIDGET_LIBRARY via WidgetLibraryPicker (click or drag-and-drop),
// always rendered full-width (cols=4) so each addition is its own new row,
// and reset whenever the viewed entry changes — no persistence.

// Same masking EntryEditorPanel/CasosView/CaseEntryDetailPage each already
// do before sending a 'call' entry's sender as the Accept payload's `from`.
function replaceSixthAndNinth(phone?: string) {
  if (!phone || phone.length < 9) return phone
  const chars = phone.split('')
  chars[5] = '*'
  chars[8] = '*'
  return chars.join('')
}

function SibsEntryDetailPage({
  entry,
  theme,
  onClose,
  sentIds,
  onSent,
  payloadRequested,
  onRequestPayload,
  getScreenshotToken,
  showInvestigationSidebar = false,
}: {
  entry: SibsEntry
  theme: ProcessedTheme
  onClose: () => void
  sentIds: Set<string>
  onSent: (id: string) => void
  payloadRequested: boolean
  onRequestPayload: () => void
  getScreenshotToken: () => Promise<string>
  /** Opt-in — this page is shared with AlertsAnalysisView's own fraud slice,
   * which doesn't get the sidebar; only Custom Mail Alert (CampaignsView)
   * passes this. */
  showInvestigationSidebar?: boolean
}) {
  const isDark = theme === 'dark'
  const lv = entryLevel(entry)
  const Icon = CAMP_SEV_ICON[lv]
  const badges: DetailBadge[] = [
    ...(sentIds.has(entry.id) ? [{ label: 'fraudulent', tone: 'sky' as const }] : []),
  ]

  const { catalog } = useCatalog()

  // Whether RawProcessedEntryPanel (below) even has something to manage —
  // gated on user_entry_id, not raw_entry_id: every user_entry has a real
  // raw_entries row underneath it, always (user_entries.raw_entry_id is NOT
  // NULL — see unified_entries_flat's own doc comment), regardless of
  // source, and the panel resolves it live by user_entry_id itself.
  // entry.raw_entry_id is get_sibs_fraud_entries' own narrower output field
  // though (branch_processed's mbo LATERAL only populates it for
  // multibanco-origin entries), so gating on that instead used to make this
  // panel disappear entirely for exactly the entries most likely to already
  // be processed. user_entry_id has no such gap: branch_multibanco and
  // branch_processed both always set it, only branch_calls (call_scrapes-
  // derived, no user_entries row to speak of at all) sets it NULL.
  const canManageRawEntry = entry.user_entry_id != null

  // Investigation sidebar — same shared component CaseEntryDetailPage uses,
  // just with a single Reclassify action for now rather than Browser Load's
  // domain-lookup set (this page has no domain-intelligence row to feed a
  // "Target" field with). Seeded from the entry's own sender since Reclassify
  // itself is target-less (requiresTarget: false below) — it's here so the
  // field isn't just a confusing empty box if a target-based action gets
  // added later.
  const [investigationTarget, setInvestigationTarget] = useState(entry.sender ?? '')
  useEffect(() => { setInvestigationTarget(entry.sender ?? '') }, [entry.id, entry.sender])

  // Gated on user_entry_id only, same reasoning as canManageRawEntry above:
  // entry.raw_entry_id is get_sibs_fraud_entries' narrower output field
  // (multibanco-origin rows only), but every user_entry has a raw_entries row
  // and /retryclassify resolves it from user_entry_id itself when it's not
  // sent — so email-sourced SIBS entries are reclassifiable too. force: this
  // is the operator's manual re-run — the backend re-runs the full
  // classify_e34 intake (rehydrating the original eml/screenshot/text on the
  // Python side) and makes Python refresh what an earlier run already linked,
  // beneficiary included, instead of deduping the re-run away. The response
  // is only the ack that it started; the result lands asynchronously as
  // classify_success in Pipeline Steps and the entry title updates on refresh.
  const investigationActions: InvestigationAction[] = useMemo(() => [
    {
      id: 'reclassify',
      label: 'Reclassify',
      description: 'Re-run the full classification (forces bank lookup + metadata refresh)',
      icon: RefreshCw,
      requiresTarget: false,
      run: async () => {
        if (!entry.user_entry_id) {
          return { success: false, error: "This entry has no user entry behind it, so it can't be reclassified." }
        }
        return apiPost('/retry-classify', {
          userEntryId: entry.user_entry_id,
          rawEntryId: entry.raw_entry_id ?? undefined,
          force: true,
        })
      },
    },
  ], [entry.user_entry_id, entry.raw_entry_id])

  return (
    <EntryDetailShell
      mode="page"
      open
      onClose={onClose}
      theme={theme}
      cols={4}
      icon={<Icon className={'h-4 w-4 ' + CAMP_SEV_TONE[lv]} />}
      title={entryTitle(entry)}
      subtitle={fmtDate(entry.created_at)}
      badges={badges}
      sidebar={showInvestigationSidebar ? (
        <InvestigationSidebar
          theme={theme}
          target={investigationTarget}
          onTargetChange={setInvestigationTarget}
          actions={investigationActions}
        />
      ) : undefined}
    >
      {/* The shared Generic row (Entries/GenericEntryRow) — same tiles as
          Cases/Unified and the Workbench, plus this page's Domains tile.
          defaultOpen, unlike Cases: here Generic is the page's primary
          content, not detail behind a Domain Intelligence row. */}
      <GenericEntryRow
        theme={theme}
        entry={entry}
        catalog={catalog}
        steps={entry.pipeline_steps ?? []}
        stepsReady
        submission={{
          r2Key: entry.r2_key,
          getToken: getScreenshotToken,
          communication: sibsCommunication(entry),
          postType: entry.post_type,
          messageTypeId: entry.message_type_id,
          messageTypeText: entry.message_type_text,
        }}
        preview={{
          raw: entry,
          processed: null,
          source: entry.source,
          userEntryId: entry.user_entry_id ?? undefined,
        }}
        payload={payloadRequested ? undefined : (
          <div className="flex h-full items-center justify-center">
            <button
              onClick={onRequestPayload}
              className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-700/40' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
            >
              <FileJson className="h-3 w-3" />
              Show payload
            </button>
          </div>
        )}
        extraPanels={canManageRawEntry ? (
          // Every domain the classifier linked to this entry (BR-654),
          // fetched live by user_entry_id since get_sibs_fraud_entries only
          // carries one domain_id; re-fetched when the step list moves.
          <EntryDetailPanel label="Domains" theme={theme} cols={2} accent="sky" scrollable className={GENERIC_ROW_HEIGHT}>
            <EntryDomainsWidget
              userEntryId={entry.user_entry_id ?? entry.id}
              theme={theme}
              refreshKey={`${entry.domain_id ?? ''}:${entry.pipeline_steps?.length ?? 0}`}
            />
          </EntryDetailPanel>
        ) : undefined}
        library={SIBS_WIDGET_LIBRARY}
        defaultOpen
      />

      {/* ── SIBS-specific email/alert panels — kept out of Generic since
          none of these apply to a non-SIBS entry (unlike Entry Data/
          Pipeline/Payload above, which are the same widgets Cases' own
          Generic row uses). ── */}
      <CollapsibleSection label="SIBS Email Alert" theme={theme} defaultOpen>
        <div className={`grid gap-3 ${detailGridCols(4)}`}>
          {/* ── row 1: email alert ── */}
          <EntryDetailPanel label="Email Alert" theme={theme} cols={2} rows={1} accent="sky" scrollable>
            <SibsEmailWidget entry={entry} theme={theme} sentIds={sentIds} onSent={onSent} />
          </EntryDetailPanel>

          {/* ── row 1: previous emails for this entity reference ── */}
          <EntryDetailPanel label="Entity Reference · Previous Emails" theme={theme} cols={2} rows={1} accent="sky" scrollable>
            <SibsEntityReferenceEmailsWidget entry={entry} theme={theme} />
          </EntryDetailPanel>

          {/* ── row 2: report suspicious case to SIBS ── */}
          <EntryDetailPanel label="New Fraud Type · SIBS Validation" theme={theme} cols={4} rows={1} accent="amber" scrollable>
            <SibsCaseReportWidget entry={entry} theme={theme} />
          </EntryDetailPanel>
        </div>
      </CollapsibleSection>

      {/* ── what was already sent about this entry: the SIBS alerts and case
          reports dispatched from the panels above, each logged as an action
          taken. A SIBS entry has no domain of its own, so there are no
          takedown channels to plan -- hasDomain={false} leaves the widget
          with its "Other actions" section alone. ── */}
      {entry.user_entry_id && (
        <CollapsibleSection label="Actions taken" theme={theme} defaultOpen>
          <ActionsWidget userEntryId={entry.user_entry_id} hostname={null} hasDomain={false} theme={theme} />
        </CollapsibleSection>
      )}

      {/* ── raw/processed entry management — only for an entry with a real
          user_entry_id behind it, see canManageRawEntry above ── */}
      {canManageRawEntry && (
        <RawProcessedEntryPanel userEntryId={entry.user_entry_id ?? entry.id} theme={theme} />
      )}
    </EntryDetailShell>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function CampaignsView({ theme = 'dark', entriesOnly = false }: { theme?: ProcessedTheme; entriesOnly?: boolean }) {
  const isDark = theme === 'dark'
  const panelBg  = 'bg-card'
  const border   = 'border-border'
  const gapBg    = 'bg-muted'
  const rowLine  = 'border-border'
  const softBg   = 'bg-secondary'
  const label    = 'text-muted-foreground'
  const muted    = 'text-muted-foreground'
  const text     = 'text-foreground'
  const soft     = 'text-muted-foreground'

  const [entries, setEntries] = useState<SibsEntry[]>([])
  const [chartEntries, setChartEntries] = useState<SibsChartEntry[]>([])
  const [chartDataLoading, setChartDataLoading] = useState(false)
  // Surfaces chart-fetch failures (e.g. a statement timeout on a wide date
  // range) instead of silently leaving the previous range's stale data on
  // screen with no indication anything went wrong.
  const [chartDataError, setChartDataError] = useState<string | null>(null)
  // Bumped by the chart error banner's "Retry" button (or its auto-retry
  // effect) to re-trigger the fetch without waiting for chartFrom to change.
  const [chartRetryTick, setChartRetryTick] = useState(0)
  const CHART_MAX_AUTO_RETRIES = 3
  const chartAutoRetryCountRef = useRef(0)
  const [dashboardAggregate, setDashboardAggregate] = useState<SibsDashboardAggregate | null>(null)
  const [dashboardAggregateLoading, setDashboardAggregateLoading] = useState(false)
  const [dashboardAggregateError, setDashboardAggregateError] = useState<string | null>(null)
  // Per-RPC failures (e.g. one query timing out) — the dashboard endpoint now
  // returns whatever data it could get plus this map, rather than failing the
  // whole request. Shown as a non-blocking banner above whatever charts did load.
  const [dashboardPartialErrors, setDashboardPartialErrors] = useState<Record<string, string> | null>(null)
  // Bumped by the "Retry" button (or the auto-retry effect below) on a
  // dashboard-fetch failure to re-trigger the effect without waiting for
  // chartFrom/chartTo to change.
  const [dashboardRetryTick, setDashboardRetryTick] = useState(0)
  // Capped so a genuinely-still-timing-out query doesn't get auto-retried
  // forever, hammering the same expensive statement repeatedly — after this
  // many silent attempts it's left to the manual Retry button.
  const DASHBOARD_MAX_AUTO_RETRIES = 3
  const dashboardAutoRetryCountRef = useRef(0)
  const [dispatchLogs,       setDispatchLogs]       = useState<SibsDispatchLog[]>([])
  const [dispatchLogsLoaded, setDispatchLogsLoaded] = useState(false)
  // Logs data is supplementary (fills in the chart's "sent" series and the
  // fraud wave chart) so a failure is never surfaced as an error banner —
  // but it still needs its own retry path. Without one, the only way to
  // retry a failed logs fetch was to force a chartFrom/chartTo change,
  // which redundantly re-ran the chart and dashboard-aggregate fetches too.
  const [dispatchLogsError, setDispatchLogsError] = useState<string | null>(null)
  const [logsRetryTick, setLogsRetryTick] = useState(0)
  const LOGS_MAX_AUTO_RETRIES = 3
  const logsAutoRetryCountRef = useRef(0)
  // Logs are opt-in — the fetch below sits behind a "Show logs" button
  // instead of firing automatically on load/range-change like chart and
  // dashboard-aggregate do, since it's the third concurrent query and
  // purely supplementary (the "sent" series and the fraud wave chart).
  // Once toggled on it stays live and follows chartFrom/chartTo like
  // everything else.
  const [logsRequested, setLogsRequested] = useState(false)
  const [payloadRequested, setPayloadRequested] = useState(false)
  const [legitimateFraudulentExpanded, setLegitimateFraudulentExpanded] = useState(false)
  const [fraudWaveExpanded, setFraudWaveExpanded] = useState(false)
  const [fraudWaveUnmitigatedActiveDays, setFraudWaveUnmitigatedActiveDays] = useState(5)
  const [dashboardReloadVisible, setDashboardReloadVisible] = useState(false)
  const [dashboardReloadProgress, setDashboardReloadProgress] = useState(0)
  // The shared dashboard range (dashboardRange.ts): last 30 days unless this
  // dashboard's cookie remembers another choice. Mail and its Entries child
  // each keep their own.
  const { from: chartFrom, to: chartTo, setRange: setChartRange } = useDashboardRange(entriesOnly ? 'sibs-mail-entries' : 'sibs-mail')
  const [chartCoupled, setChartCoupled] = useState(false)
  // null when the backend didn't compute an exact count (plain paginated
  // listing, no search) — computing one would mean scanning the full date
  // range again, defeating the point of pushing LIMIT/OFFSET into the RPC.
  const [total, setTotal] = useState<number | null>(0)
  const [hasMore, setHasMore] = useState(false)
  // Entries table is opt-in too, same as logs — this used to default to
  // true and drive a top-level `if (loading) return <spinner/>` that hid
  // the whole page (dashboard included) until the entries fetch resolved.
  // Now the fetch only fires once entriesRequested flips true, so `loading`
  // starts false and the dashboard renders immediately on mount.
  const [loading, setLoading] = useState(false)
  const [entriesRequested, setEntriesRequested] = useState(entriesOnly)
  const [hasLoadedEntries, setHasLoadedEntries] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Ticker/relative-time formatting now lives in BaseDashboard's own
  // RefreshControl (see lastUpdated prop below) — this only needs to track
  // the raw timestamp.
  const [lastLoadedAt, setLastLoadedAt] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  const [appliedQuery, setAppliedQuery] = useState('')
  // Platform filter, pushed to get_sibs_fraud_entries itself (see
  // fetchEntries' platform_ids/platform_unassigned query params) rather than
  // filtered client-side — same reasoning as search: this table shows a
  // paginated slice of a much larger date window, so filtering after the
  // fact would just mean fewer rows per page instead of actually finding
  // the platform-matching entries further back. Multi-select (Excel-style
  // column filter, same as Source_Type) rather than a single picker —
  // UNASSIGNED_PLATFORM_FILTER is a sentinel distinct from every real
  // platform.id (all positive integers), so it can sit in the same array
  // alongside one or more real platform ids. Empty array = no filter.
  const [platformFilter, setPlatformFilter] = useState<string[]>([])
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set())
  const [newEntryIds, setNewEntryIds] = useState<Set<string>>(new Set())
  const [copyFlash, setCopyFlash] = useState(false)
  const { catalog } = useCatalog()
  const [bulkClassifyOpen, setBulkClassifyOpen] = useState(false)
  const [bulkClassifySubmitting, setBulkClassifySubmitting] = useState(false)
  const [bulkClassifyResult, setBulkClassifyResult] = useState<BulkClassifyResult | null>(null)
  const [selected, setSelected] = useState<SibsEntry | null>(null)
  // The open entry is the URL's last segment (/sibs/mail-entries/{id}), so an
  // entry can be linked, reloaded and opened in a new tab; the effect below
  // keeps `selected` in step with the address bar.
  const detail = useDetailPath()
  const [missingId, setMissingId] = useState<string | null>(null)
  // The entry last opened, kept highlighted (same blue as new entries) in the
  // list after going back — a "you were just here" marker.
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null)
  // The list view unmounts/remounts when toggling to/from the detail view
  // (EntryDetailShell page mode swaps it out entirely). Nothing inside this
  // component actually scrolls internally — the app shell's <section
  // overflow-y-auto h-screen> in BackofficeApp.tsx does — and since the detail
  // view is much shorter, switching to it clamps that ancestor's scrollTop,
  // which is then still clamped once we're back. Tracked in a ref (not state)
  // so scrolling doesn't trigger re-renders; captured on row click (while the
  // list DOM — and its scroll ancestor — is still there to read) and restored
  // via the list root's callback ref, which fires exactly when it (re)mounts.
  const listScrollTopRef = useRef(0)
  // useCallback with no deps gives this a stable identity across renders —
  // otherwise (an inline arrow function) React would call it on every single
  // re-render of the list, not just when the div actually (re)mounts, since
  // ref callbacks fire whenever their identity changes. That was firing the
  // hide/restore-scroll logic below on every re-render (e.g. just opening the
  // replay popover), visibly flickering the whole list.
  const restoreListScrollRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return
    // Hidden synchronously (before this commit ever paints) so the
    // pre-restore top-of-page position is never visible, then revealed once
    // scroll is actually corrected — otherwise the deferred restore below
    // shows as a visible jump.
    el.style.visibility = 'hidden'
    // Deferred to the next frame(s): the chart cards' Recharts
    // ResponsiveContainers measure their height asynchronously via
    // ResizeObserver, not synchronously on mount, so the scroll ancestor's
    // scrollHeight is still short (pre-charts) at the moment this ref first
    // fires — setting scrollTop immediately gets silently clamped, and
    // nothing re-applies it once the charts finish expanding a frame or two
    // later.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const sp = getScrollParent(el)
        if (sp) sp.scrollTop = listScrollTopRef.current
        el.style.visibility = 'visible'
      })
    })
  }, [])
  const [sentIds, setSentIds] = useState<Set<string>>(new Set())

  // Address bar -> open entry. A row click has already set `selected`; a cold
  // link resolves from the loaded rows, else asks /campaigns/sibs?id= for
  // that one row (same RPC and row mapping as the list, no date window).
  const resolveSeq = useRef(0)
  useEffect(() => {
    const id = detail.id
    const seq = ++resolveSeq.current
    if (!id) { setSelected(null); return }
    if (selected?.id === id) return
    const loaded = entries.find((e) => e.id === id)
    if (loaded) { setSelected(loaded); return }
    fetch(`/api/campaigns/sibs?id=${id}`)
      .then((r) => r.json())
      .catch(() => null)
      .then((payload) => {
        if (seq !== resolveSeq.current) return
        const row: SibsEntry | undefined = payload?.success ? payload.data?.entries?.[0] : undefined
        if (row) { setSelected(row); setLastSelectedId(row.id); setMissingId(null) }
        else { setMissingId(id); detail.close() }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.id, entries])
  const [entryTypeFilter, setEntryTypeFilter] = useState<string[]>([])
  const [sourceTypeFilter, setSourceTypeFilter] = useState<string[]>([])
  const [sourceFilter, setSourceFilter] = useState<string[]>([])

  type ReplayStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'done'
  const [replayOpen,   setReplayOpen]   = useState(false)
  const [replayStatus, setReplayStatus] = useState<ReplayStatus>('idle')
  const [replayQueue,  setReplayQueue]  = useState<SibsEntry[]>([])
  const [replayIndex,  setReplayIndex]  = useState(0)
  const [replayRate,   setReplayRate]   = useState(10)
  const replayLogQueueRef = useRef<SibsDispatchLog[]>([])
  const [replayMaxDate,   setReplayMaxDate]   = useState<string | null>(null)
  const replayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const replayPopupRef = useRef<HTMLDivElement | null>(null)
  const isReplaying = replayStatus !== 'idle'
  const replayControlsLocked = replayStatus === 'loading' || replayStatus === 'playing' || replayStatus === 'paused'
  // chartDataLoading intentionally excluded — the chart cell gets its own
  // local loading overlay (see Chart 1 · Volume below) instead of pulsing
  // the whole bento grid every time the chart's independent auto-retry
  // loop toggles, which used to make an isolated chart-endpoint timeout
  // look like the entire dashboard was affected.
  // !dispatchLogsLoaded only counts while logs are actually requested — logs
  // being opt-in now (see logsRequested/"Show logs" button) means it would
  // otherwise sit permanently true and pin this overlay on forever whenever
  // the user hasn't asked for logs yet.
  const dashboardUpdating = !isReplaying && (dashboardAggregateLoading || (logsRequested && !dispatchLogsLoaded))

  useEffect(() => {
    if (dashboardUpdating) {
      setDashboardReloadVisible(true)
      setDashboardReloadProgress(12)
      const timer = setInterval(() => {
        setDashboardReloadProgress((value) => Math.min(88, value + Math.max(3, Math.round((88 - value) * 0.18))))
      }, 180)
      return () => clearInterval(timer)
    }

    setDashboardReloadProgress((value) => (value > 0 ? 100 : 0))
    const timer = setTimeout(() => {
      setDashboardReloadVisible(false)
      setDashboardReloadProgress(0)
    }, 360)
    return () => clearTimeout(timer)
  }, [dashboardUpdating])

  const fetchAbort       = useRef<AbortController | null>(null)
  const sentinelRef      = useRef<HTMLDivElement | null>(null)
  const newEntryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const screenshotTurnstileRef = useRef<TurnstileHandle>(null)
  const pendingTokenRef  = useRef<((token: string) => void) | null>(null)

  const handleScreenshotVerify = useCallback((token: string) => {
    pendingTokenRef.current?.(token)
    pendingTokenRef.current = null
  }, [])

  const getScreenshotToken = useCallback((): Promise<string> =>
    new Promise((resolve) => {
      pendingTokenRef.current = resolve
      const tryRun = (attempt = 0) => {
        if (screenshotTurnstileRef.current) {
          screenshotTurnstileRef.current.reset()
          screenshotTurnstileRef.current.run()
          return
        }
        if (attempt < 20) setTimeout(() => tryRun(attempt + 1), 100)
      }
      tryRun()
    }), [])

  const entryTypeOptions = useMemo(
    () => [...new Set(entries.map((e) => e.entry_type ?? ''))].filter(Boolean).sort() as string[],
    [entries],
  )
  const sourceTypeOptions = useMemo(
    () => [...new Set(entries.map((e) => e.source_type ?? ''))].filter(Boolean).sort() as string[],
    [entries],
  )
  const sourceOptions = useMemo(
    () => [...new Set(entries.map((e) => e.source ?? ''))].filter(Boolean).sort() as string[],
    [entries],
  )

  const sibsColumns = useMemo((): ColumnDef[] => [
    { key: 'expand',                     label: '',                                    initialWidth: 32,  thClassName: 'w-8' },
    { key: 'created_at',                 label: 'Created_At',                          initialWidth: 105 },
    { key: 'post_type',                  label: 'Post_Type',                           initialWidth: 100 },
    { key: 'beneficiario',               label: 'Beneficiário',                        initialWidth: 160 },
    { key: 'unsolicited',                label: 'Unsolicited',                         initialWidth: 95 },
    { key: 'email_sent',                 label: <Mail className="h-3 w-3 text-foreground" />, initialWidth: 40 },
    { key: 'entry_type',                 label: 'Entry_Type',                          initialWidth: 150,
      filterOptions: entryTypeOptions, activeFilters: entryTypeFilter, onFilterChange: setEntryTypeFilter },
    { key: 'source_type',                label: 'Source_Type',                         initialWidth: 120,
      filterOptions: sourceTypeOptions, activeFilters: sourceTypeFilter, onFilterChange: setSourceTypeFilter },
    { key: 'source',                     label: 'Source',                              initialWidth: 140,
      filterOptions: sourceOptions, activeFilters: sourceFilter, onFilterChange: setSourceFilter },
    { key: 'entity_reference_entity',    label: 'Entity_Reference_Entity',             initialWidth: 160 },
    { key: 'entity_reference_reference', label: 'Entity_Reference_Reference',          initialWidth: 160 },
    { key: 'platform_namelong',          label: 'Platform',                            initialWidth: 150 },
    { key: 'platform_text',              label: 'Platform_Text',                       initialWidth: 150 },
    // Server-side (see fetchEntries' platform_ids/platform_unassigned query
    // params), unlike the client-side entry_type/source_type/source filters
    // above — options come from the static catalog, not from currently-
    // loaded entries, so picking a rare platform doesn't hide every other
    // choice once the results narrow to just it.
    { key: 'platform_id',                label: 'Platform_ID',                         initialWidth: 90,
      filterOptions: platformFilterOptions(catalog), activeFilters: platformFilter, onFilterChange: setPlatformFilter,
      filterOptionLabel: platformFilterOptionLabel(catalog) },
    { key: 'message_content',            label: 'Message_Content',                     initialWidth: 280 },
    { key: 'scam_claims',                label: 'Scam_Claims',                         initialWidth: 90 },
    { key: 'sender',                     label: 'Sender',                              initialWidth: 130 },
    { key: 'raw_free_input',             label: 'Raw_Free_Input',                      initialWidth: 180 },
    { key: 'id',                         label: 'ID',                                  initialWidth: 120 },
  ], [entryTypeOptions, entryTypeFilter, sourceTypeOptions, sourceTypeFilter, sourceOptions, sourceFilter, catalog, platformFilter])

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchEntries = async (offset = 0, isRefresh = false) => {
    // Replay/coupled mode owns `entries` entirely (see the tick effect's
    // drip-feed and startReplay's own reset) — a stray call landing here
    // while chartCoupled is true would race that and silently splice a
    // "current real state" snapshot into the middle of the replay's
    // chronological list, which is how specific entries end up stuck
    // in the table across a pause/replay-again cycle. Every caller
    // (the search/query effect, the "load more" observer, the manual
    // Refresh button) is already supposed to avoid calling this during
    // replay, but guarding here too means any of them slipping through —
    // now or in the future — can't corrupt the drip-feed.
    if (chartCoupled) return
    fetchAbort.current?.abort()
    const controller = new AbortController()
    fetchAbort.current = controller

    if (offset === 0) {
      if (isRefresh || hasLoadedEntries) setRefreshing(true)
      else setLoading(true)
    } else {
      setLoadingMore(true)
    }
    setError(null)

    try {
      const requestQuery = query.trim()
      const params = new URLSearchParams({
        offset: String(offset),
        limit: String(PAGE_SIZE),
        ...platformFilterToQueryParams(platformFilter),
      })
      if (requestQuery) params.set('q', requestQuery)
      const r = await fetch(`/api/campaigns/sibs?${params.toString()}`, { signal: controller.signal })
      const payload = await r.json()

      if (!payload?.success) {
        setError(payload?.error ?? 'Failed to load data')
        return
      }

      const page: SibsEntry[] = payload.data.entries ?? []

      if (offset === 0) {
        if (newEntryTimerRef.current) clearTimeout(newEntryTimerRef.current)
        if (isRefresh && entries.length > 0) {
          const previousIds = new Set(entries.map((entry) => entry.id))
          const addedIds = page.map((entry) => entry.id).filter((id) => !previousIds.has(id))
          setNewEntryIds(new Set(addedIds))
          if (addedIds.length > 0) {
            newEntryTimerRef.current = setTimeout(() => {
              setNewEntryIds(new Set())
              newEntryTimerRef.current = null
            }, 8000)
          }
        } else {
          setNewEntryIds(new Set())
        }
      }

      // Functional update, not `[...entries, ...page]` off the closed-over
      // `entries` — the infinite-scroll IntersectionObserver effect only
      // re-subscribes when entries.length changes (see its dependency
      // array below), so a refresh that returns the same page size leaves
      // it holding a stale closure. That stale closure still fires with a
      // correct-looking offset, but concatenating against its stale
      // `entries` would silently overwrite the just-refreshed state with
      // pre-refresh data + the newly loaded page. A functional update
      // always applies to the true latest state no matter which stale
      // closure triggered it.
      if (offset === 0) setEntries(page)
      else setEntries((prev) => [...prev, ...page])
      if (offset === 0) setAppliedQuery(requestQuery)
      setSentIds((prev) => {
        const base = offset === 0 ? new Set<string>() : new Set(prev)
        page.filter((e) => e.email_sent).forEach((e) => base.add(e.id))
        return base
      })
      if (offset === 0) setCheckedIds(new Set())

      setHasMore(payload.data.hasMore ?? false)
      setTotal(payload.data.total ?? null)
      if (offset === 0) setHasLoadedEntries(true)
      setLastLoadedAt(Date.now())
    } catch (err: any) {
      if (err?.name !== 'AbortError') setError('Network error')
    } finally {
      setLoading(false)
      setRefreshing(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    if (!entriesRequested) return
    // Same guard the chart/dashboard/logs effects already have. startReplay()
    // sets entriesRequested true (so the table's placeholder gate doesn't
    // block the drip-feed) in the same batch as chartCoupled — without this
    // check, that same state change also retriggers this effect, firing a
    // plain fetchEntries(0) that races the replay tick effect's own
    // setEntries calls. Landing after a few ticks have already dripped in,
    // it silently replaces the array with a real "current" snapshot,
    // unrelated to replay's chronological order — which is exactly how a
    // stray entry ends up stuck pinned at the top despite the replay
    // timeline having since moved past it.
    if (chartCoupled) return
    // 250ms used to sit here — shorter than the gap between keystrokes for
    // most typing speeds, so the timer routinely expired before the next
    // character landed and this fired a fetch on nearly every keystroke
    // instead of coalescing them. Each of those hits the expensive
    // full-table-scan search path (see fetchEntries), so a too-tight debounce
    // wasn't just a UX nit — it multiplied the exact requests most likely to
    // time out. 450ms is long enough to actually catch a normal typing pace.
    const timer = setTimeout(() => fetchEntries(0), query.trim() ? 450 : 0)
    return () => {
      clearTimeout(timer)
      fetchAbort.current?.abort()
    }
  }, [query, platformFilter, entriesRequested, chartCoupled])

  useEffect(() => () => {
    if (newEntryTimerRef.current) clearTimeout(newEntryTimerRef.current)
  }, [])

  // A new date range gets a fresh auto-retry budget.
  useEffect(() => {
    chartAutoRetryCountRef.current = 0
  }, [chartFrom, chartTo])

  useEffect(() => {
    // entriesOnly sub-view has no charts to feed — skip the fetch entirely
    // rather than just hiding the grid, so it doesn't pay for RPCs nothing
    // on screen actually reads.
    if (chartCoupled || entriesOnly) {
      setChartDataLoading(false)
      return
    }
    // Sequenced behind the dashboard-aggregate fetch (same trick the logs
    // effect already uses): on the render where chartFrom/chartTo just
    // changed, dashboard's own setDashboardAggregateLoading(true) hasn't
    // committed yet, so this guard doesn't block that first instant — but
    // the resulting re-render fires immediately after and aborts this
    // fetch (via the cleanup below) before it does any real work, then
    // re-fires once dashboardAggregateLoading flips back to false.
    if (dashboardAggregateLoading) return
    const controller = new AbortController()
    setChartDataLoading(true)
    setChartDataError(null)
    // `until` as well — the charts filter to chartTo client-side anyway, so
    // without it every row from chartTo to today was fetched just to be dropped.
    fetch(`/api/campaigns/sibs?chart=1&since=${chartFrom}&until=${chartTo}`, { signal: controller.signal })
      .then((r) => r.json())
      .then((payload) => {
        if (payload?.success && Array.isArray(payload.data)) {
          setChartEntries(payload.data)
          chartAutoRetryCountRef.current = 0 // clean success — reset the budget
        } else {
          setChartDataError(payload?.error ?? 'Failed to load chart data')
        }
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') setChartDataError('Network error loading chart data')
      })
      .finally(() => {
        if (!controller.signal.aborted) setChartDataLoading(false)
      })
    return () => controller.abort()
  }, [chartFrom, chartTo, chartCoupled, chartRetryTick, dashboardAggregateLoading, entriesOnly])

  // Same seamless-auto-retry treatment as the dashboard endpoint: retry once
  // automatically on failure rather than making the user notice and click
  // Retry, capped so a genuinely still-failing query doesn't get hammered
  // forever, and skipped entirely during replay (chartCoupled) since this
  // fetch doesn't even run then.
  useEffect(() => {
    if (chartCoupled || !chartDataError || chartAutoRetryCountRef.current >= CHART_MAX_AUTO_RETRIES) return
    const timer = setTimeout(() => {
      chartAutoRetryCountRef.current += 1
      setChartRetryTick((t) => t + 1)
    }, 1500)
    return () => clearTimeout(timer)
  }, [chartDataError, chartCoupled])

  // A new date range gets a fresh auto-retry budget.
  useEffect(() => {
    dashboardAutoRetryCountRef.current = 0
  }, [chartFrom, chartTo])

  useEffect(() => {
    // Same guard the chart-data effect already has — while replaying,
    // chartCoupled is true and the dashboard aggregates aren't relevant to
    // what's being visualized, so don't fetch (or retry) in the background.
    // entriesOnly skips it too — that sub-view never renders the grid these
    // 7 RPCs feed.
    if (chartCoupled || entriesOnly) {
      setDashboardAggregateLoading(false)
      return
    }
    const controller = new AbortController()
    setDashboardAggregateLoading(true)
    setDashboardAggregateError(null)
    setDashboardPartialErrors(null)
    fetch(`/api/campaigns/sibs?dashboard=1&since=${chartFrom}&until=${chartTo}`, { signal: controller.signal })
      .then((r) => r.json())
      .then((payload) => {
        if (!payload?.success) {
          setDashboardAggregate(null)
          setDashboardAggregateError(payload?.error ?? 'Failed to load dashboard RPCs')
          return
        }
        setDashboardAggregate(payload.data)
        setDashboardPartialErrors(payload.errors ?? null)
        if (!payload.errors) dashboardAutoRetryCountRef.current = 0 // clean success — reset the budget
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') {
          setDashboardAggregate(null)
          setDashboardAggregateError('Network error loading dashboard RPCs')
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setDashboardAggregateLoading(false)
      })
    return () => controller.abort()
  }, [chartFrom, chartTo, dashboardRetryTick, chartCoupled, entriesOnly])

  // Seamless auto-retry: a timeout on this endpoint is often transient (see
  // the Max Rows/statement-timeout saga elsewhere in this file's history), so
  // retry once automatically rather than making the user notice and click
  // Retry themselves. Capped at DASHBOARD_MAX_AUTO_RETRIES so a genuinely
  // still-failing query doesn't get hammered forever — after that it's left
  // to the manual button.
  useEffect(() => {
    const hasFailure = !!dashboardAggregateError || !!(dashboardPartialErrors && Object.keys(dashboardPartialErrors).length > 0)
    if (chartCoupled || !hasFailure || dashboardAutoRetryCountRef.current >= DASHBOARD_MAX_AUTO_RETRIES) return
    const timer = setTimeout(() => {
      dashboardAutoRetryCountRef.current += 1
      setDashboardRetryTick((t) => t + 1)
    }, 1500)
    return () => clearTimeout(timer)
  }, [dashboardAggregateError, dashboardPartialErrors, chartCoupled])

  useEffect(() => {
    // Same guard as the chart/dashboard effects — but critically, this one
    // was MISSING it, which is a real race condition: startReplay() is a
    // plain function call, not a chartFrom/chartTo change, so it doesn't
    // abort/retrigger this effect. If this fetch was still in flight (or
    // fires again for any reason) while replaying, its response would land
    // after startReplay()'s setDispatchLogs([]) reset and silently overwrite
    // it with the pre-replay range's stale logs — exactly the "persists data
    // from before play started" symptom on the FraudWaveChart, which reads
    // from dispatchLogs.
    if (chartCoupled) return
    if (!logsRequested) return
    // Sequenced behind the chart and dashboard fetches rather than firing all
    // three at once — those two are the expensive ones (see this whole
    // file's history of statement timeouts), and logs is comparatively cheap,
    // so there's no reason to add a third concurrent query competing for the
    // same DB resources while they're still in flight. On the render where
    // chartFrom/chartTo just changed, the chart/dashboard effects' own
    // setChartDataLoading(true)/setDashboardAggregateLoading(true) calls
    // haven't been committed yet, so this guard doesn't block that instant —
    // but the resulting re-render fires immediately after and aborts this
    // fetch (via the cleanup below) before it does any real work, then
    // re-fires once both flip back to false.
    if (chartDataLoading || dashboardAggregateLoading) return
    setDispatchLogsLoaded(false)
    const controller = new AbortController()
    fetch(`/api/campaigns/logs?since=${chartFrom}&until=${chartTo}`, { signal: controller.signal })
      .then((r) => r.json())
      .then((payload) => {
        if (payload?.success && Array.isArray(payload.data)) {
          setDispatchLogs(payload.data)
          setDispatchLogsError(null)
          logsAutoRetryCountRef.current = 0
        } else {
          setDispatchLogsError(payload?.error ?? 'Failed to load dispatch logs')
        }
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') setDispatchLogsError('Network error loading dispatch logs')
      })
      .finally(() => {
        if (!controller.signal.aborted) setDispatchLogsLoaded(true)
      })
    return () => controller.abort()
  }, [chartFrom, chartTo, chartCoupled, chartDataLoading, dashboardAggregateLoading, logsRetryTick, logsRequested])

  // Silent, independent auto-retry — same capped 1.5s-interval pattern as
  // the chart/dashboard fetches, but never shown to the user since logs
  // failures don't get an error banner (see dispatchLogsError above).
  useEffect(() => {
    if (chartCoupled || !dispatchLogsError || logsAutoRetryCountRef.current >= LOGS_MAX_AUTO_RETRIES) return
    const timer = setTimeout(() => {
      logsAutoRetryCountRef.current += 1
      setLogsRetryTick((t) => t + 1)
    }, 1500)
    return () => clearTimeout(timer)
  }, [dispatchLogsError, chartCoupled])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !isReplaying) fetchEntries(entries.length)
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  // `selected` and `error` are included even though neither is read in the
  // body — opening an entry (EntryDetailShell) or showing the error/retry
  // banner both swap the sentinel div out of the tree entirely (not a CSS
  // hide); going back or retrying successfully remounts a brand new sentinel
  // node. A failed load-more attempt in particular can leave hasMore,
  // loadingMore and entries.length exactly as they were before the error, so
  // without error as a dep this effect never re-runs to .observe() the new
  // node — the old observer keeps watching a detached element and
  // infinite-scroll silently stops working after a timeout + retry.
  }, [hasMore, loadingMore, entries.length, isReplaying, selected, error])

  // Replay tick: drip-feed one entry per interval
  useEffect(() => {
    if (replayStatus !== 'playing') {
      if (replayTimerRef.current) clearTimeout(replayTimerRef.current)
      return
    }
    if (replayIndex >= replayQueue.length) {
      const remaining = replayLogQueueRef.current
      replayLogQueueRef.current = []
      setReplayStatus('done')
      if (remaining.length > 0) setDispatchLogs(d => [...d, ...remaining])
      return
    }
    const delay = 1000 / Math.max(0.1, replayRate)
    replayTimerRef.current = setTimeout(() => {
      const entry = replayQueue[replayIndex]
      setEntries(prev => [entry, ...prev])
      setSentIds(prev => entry.email_sent ? new Set([...prev, entry.id]) : prev)
      setReplayMaxDate(entry.created_at.slice(0, 10))
      const threshold = new Date(entry.created_at).getTime()
      const queue = replayLogQueueRef.current
      const toAdd = queue.filter(l => new Date(l.created_at).getTime() <= threshold)
      replayLogQueueRef.current = queue.filter(l => new Date(l.created_at).getTime() > threshold)
      if (toAdd.length > 0) setDispatchLogs(d => [...d, ...toAdd])
      setReplayIndex(i => i + 1)
    }, delay)
    return () => { if (replayTimerRef.current) clearTimeout(replayTimerRef.current) }
  }, [replayStatus, replayIndex, replayQueue, replayRate])

  const startReplay = useCallback(async () => {
    fetchAbort.current?.abort()
    setReplayOpen(false)
    setReplayStatus('loading')
    // Replay drip-feeds the table itself (see the tick effect), bypassing
    // fetchEntries entirely — but the table's own "Load entries" gate would
    // otherwise still be showing its placeholder underneath, and once
    // replay ends the query-driven effect needs entriesRequested true to
    // actually respond to search input again.
    setEntriesRequested(true)
    setEntries([])
    setSentIds(new Set())
    setHasMore(false)
    setTotal(0)
    setReplayIndex(0)
    setReplayQueue([])
    replayLogQueueRef.current = []
    setReplayMaxDate(null)
    setDispatchLogs([])
    setDispatchLogsLoaded(false)
    setChartCoupled(true)
    try {
      const since = encodeURIComponent(chartFrom + 'T00:00')
      const until = encodeURIComponent(chartTo   + 'T23:59')
      const [r, logsR] = await Promise.all([
        fetch(`/api/campaigns/sibs?replay=1&since=${since}&until=${until}`),
        fetch(`/api/campaigns/logs?since=${chartFrom}&until=${chartTo}`),
      ])
      const payload     = await r.json()
      const logsPayload = await logsR.json().catch(() => null)
      if (!payload?.success) { setReplayStatus('idle'); return }
      const q: SibsEntry[] = payload.data.entries ?? []
      setReplayQueue(q)
      setTotal(q.length)
      if (logsPayload?.success && Array.isArray(logsPayload.data))
        replayLogQueueRef.current = logsPayload.data
      setReplayStatus(q.length > 0 ? 'playing' : 'done')
    } catch {
      setReplayStatus('idle')
    }
  }, [chartFrom, chartTo])

  const stopReplay = useCallback(() => {
    if (replayTimerRef.current) clearTimeout(replayTimerRef.current)
    setReplayStatus('idle')
    setReplayQueue([])
    replayLogQueueRef.current = []
    setReplayMaxDate(null)
    setReplayIndex(0)
    // Same fix as the chart preset buttons got earlier — nothing else resets
    // chartCoupled back to false on exiting replay, which left chartSource
    // stuck reading `filtered` (whatever partial slice of entries had been
    // dripped in before Stop was clicked) instead of switching back to the
    // correct, complete chartEntries. Setting it here also naturally
    // re-triggers the chart/dashboard/logs effects (all depend on
    // chartCoupled), so the manual logs fetch this used to do directly is
    // redundant now — and bypassed the charts-before-logs sequencing besides.
    setChartCoupled(false)
    fetchEntries(0)
  }, [fetchEntries])

  useEffect(() => {
    if (!replayOpen) return
    const handler = (e: MouseEvent) => {
      if (replayPopupRef.current && !replayPopupRef.current.contains(e.target as Node))
        setReplayOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [replayOpen])

  useEffect(() => {
    if (replayStatus === 'loading' || replayStatus === 'playing') setReplayOpen(false)
  }, [replayStatus])


  // ── Derived data ─────────────────────────────────────────────────────────────

  const filtered = useMemo(
    () =>
      entries.filter((e) => {
        if (entryTypeFilter.length > 0 && !entryTypeFilter.includes(e.entry_type ?? '')) return false
        if (sourceTypeFilter.length > 0 && !sourceTypeFilter.includes(e.source_type ?? '')) return false
        if (sourceFilter.length > 0 && !sourceFilter.includes(e.source ?? '')) return false
        return true
      }),
    [entries, entryTypeFilter, sourceTypeFilter, sourceFilter],
  )

  const chartSource = useMemo<SibsChartEntry[]>(() => {
    const from = new Date(chartFrom + 'T00:00:00').getTime()
    const to   = new Date(chartTo   + 'T23:59:59').getTime()
    // Use filtered (the table's drip-fed state) whenever chartCoupled, replay
    // included. The visibleChartData mask (below, in the render) reveals
    // buckets day-by-day as replayMaxDate advances, and it's paired with
    // this: the values behind each newly-revealed day are meant to build up
    // incrementally as replay drips matching entries in, not appear
    // pre-filled with their final total. That only works if chartSource
    // reflects the entries actually replayed so far, not the complete
    // dataset. This used to point at chartEntries instead, as a workaround
    // for the replay endpoint silently truncating wide ranges under
    // PostgREST's row cap — now fixed at the source (batched fetch, real
    // sinceDate), so replayQueue/filtered can be trusted to carry the full
    // selected range again.
    const base: SibsChartEntry[] = chartCoupled ? filtered : chartEntries
    return base.filter((e) => {
      const t = new Date(e.created_at).getTime()
      return t >= from && t <= to
    })
  }, [chartCoupled, chartEntries, filtered, chartFrom, chartTo])

  const sibsChartData = useMemo(() => {
    const from = new Date(chartFrom + 'T00:00:00')
    const to   = new Date(chartTo   + 'T23:59:59')
    const days = Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000) + 1)
    const raw = buildSibsChartData(chartSource, dispatchLogs, from, days)
    // Hide sent until logs arrive; during replay dispatchLogs fills incrementally so treat as ready
    const sentReady = dispatchLogsLoaded || isReplaying
    return sentReady ? raw : raw.map(d => ({ ...d, sent: null as unknown as number }))
  }, [chartSource, dispatchLogs, dispatchLogsLoaded, chartFrom, chartTo, chartCoupled, isReplaying])

  // Boundary for the progressive "day reached so far" reveal during replay —
  // hoisted out of the Volume chart's render so aggregatePreview.daily can
  // share the exact same cutoff instead of recomputing it independently and
  // risking drift between the two (which is how a "bump ahead of the
  // current replay position" can sneak into one but not the other).
  const replayDayIdxShifted = useMemo(() => {
    if (!replayMaxDate) return Infinity
    const idx = Math.floor((new Date(replayMaxDate + 'T00:00').getTime() - new Date(chartFrom + 'T00:00').getTime()) / 86400000)
    return idx + 1
  }, [replayMaxDate, chartFrom])

  const legitimateFraudulentWaveData = useMemo<LegitimateFraudulentWaveDatum[]>(() => {
    const anchor = { date: '', legitimate: 0, dispatched: 0, sent: 0, entity_ref: 0, other: 0 }
    return [
      anchor,
      ...sibsChartData.map((d, i) => {
        const masked = i + 1 > replayDayIdxShifted
          ? { ...d, legitimate: null, dispatched: null, sent: null }
          : d
        return { ...masked, sent: (masked.sent === 0 || masked.sent == null) ? null : masked.sent }
      }),
    ]
  }, [sibsChartData, replayDayIdxShifted])

  const fraudWaveData = useMemo(() => {
    const from = new Date(chartFrom + 'T00:00:00')
    const to   = new Date(chartTo   + 'T23:59:59')
    const days = Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000) + 1)
    return buildFraudWaveData(chartSource, dispatchLogs, from, days, fraudWaveUnmitigatedActiveDays)
  }, [chartSource, dispatchLogs, chartFrom, chartTo, fraudWaveUnmitigatedActiveDays])

  const sibsDiffData = useMemo(() =>
    sibsChartData.map((d, i) => ({
      date:      d.date,
      legitDiff: i === 0 ? 0 : d.legitimate - sibsChartData[i - 1].legitimate,
      dispDiff:  i === 0 ? 0 : d.dispatched  - sibsChartData[i - 1].dispatched,
    }))
  , [sibsChartData])

  const [domainLock, setDomainLock] = useState({ yLeftMax: 1, yDiffMin: 0, yDiffMax: 1 })
  useEffect(() => {
    if (isReplaying) return
    setDomainLock(prev => ({
      yLeftMax: Math.max(1, ...sibsChartData.map(d => Math.max(d.legitimate ?? 0, d.dispatched ?? 0))),
      yDiffMin: Math.min(0, ...sibsDiffData.map(d => Math.min(d.legitDiff, d.dispDiff))),
      yDiffMax: Math.max(1, ...sibsDiffData.map(d => Math.max(d.legitDiff, d.dispDiff))),
    }))
  }, [sibsChartData, sibsDiffData, isReplaying])

  // Earliest confirmed dispatch time per entry, kept as a fallback if the median RPC is unavailable.
  // Still keyed on entry_id, unlike tableDispatchTimeById's own user_entry_id
  // preference (see SibsDispatchLog's doc comment) — chartSource rows come
  // from get_sibs_fraud_chart_data, whose RETURNS TABLE doesn't expose
  // user_entry_id (it's internal-only there, used just for that RPC's own
  // dedup). So this join is still susceptible to the same entry_id-drift
  // this whole fix targets; fixing it for real needs that RPC's RETURNS
  // TABLE extended with a user_entry_id column first.
  const dispatchTimeById = useMemo(() => {
    const map = new Map<string, number>()
    for (const log of dispatchLogs) {
      if (!log.entry_id || log.status !== 'sent') continue
      const parsed = parseDbTimestamp(log.created_at)
      if (!parsed) continue
      const t = parsed.getTime()
      const existing = map.get(log.entry_id)
      if (existing == null || t < existing) map.set(log.entry_id, t)
    }
    return map
  }, [dispatchLogs])

  const protectionDurations = useMemo(() => {
    const minutes: number[] = []
    for (const e of chartSource) {
      if (!e.email_sent) continue
      const dispatchedAt = e.id ? dispatchTimeById.get(e.id) : undefined
      if (dispatchedAt == null) continue // no confirmed dispatch timestamp for this entry in range
      const createdAt = parseDbTimestamp(e.created_at)
      if (!createdAt) continue
      const diffMin = (dispatchedAt - createdAt.getTime()) / 60000
      if (diffMin < 0) continue
      minutes.push(diffMin)
    }
    return minutes.sort((a, b) => a - b)
  }, [chartSource, dispatchTimeById])

  const medianProtectionMinutes = useMemo(() => {
    if (protectionDurations.length === 0) return null
    const mid = Math.floor(protectionDurations.length / 2)
    return protectionDurations.length % 2 === 1
      ? protectionDurations[mid]
      : (protectionDurations[mid - 1] + protectionDurations[mid]) / 2
  }, [protectionDurations])

  const platformBreakdown = useMemo(() => buildPlatformBreakdown(chartSource, isDark), [chartSource, isDark])

  const aggregatePreview = useMemo(() => {
    if (isReplaying) {
      // The 7 dashboard-aggregate RPCs are deliberately skipped during
      // replay (see that fetch effect's chartCoupled guard) to avoid
      // re-running expensive server-side aggregates on every tick. But
      // every number they report is derivable from data already sitting in
      // the browser — chartSource is the same drip-fed rows behind the
      // Volume chart, and protectionDurations/medianProtectionMinutes
      // (below) already do this exact join against dispatchLogs for the
      // fraud-wave chart. So recompute the same shape client-side instead
      // of freezing on whatever dashboardAggregate held before Play.
      const total = chartSource.length
      const fraudulent = chartSource.filter(e => e.email_sent).length
      const legitimate = total - fraudulent
      const fraudRatio = total > 0 ? Math.round((fraudulent / total) * 100) : 0
      const todayKey = new Date().toISOString().slice(0, 10)
      const today = chartSource.filter(e => e.created_at.slice(0, 10) === todayKey).length
      const entityReferences = chartSource.filter(e => e.entry_type === 'ENTITY_REFERENCE').length
      const other = Math.max(0, total - entityReferences)

      const uniqueCount = (predicate: (e: SibsChartEntry) => boolean) => {
        const set = new Set<string>()
        for (const e of chartSource) {
          if (e.beneficiario && predicate(e)) set.add(e.beneficiario.toLowerCase())
        }
        return set.size
      }

      const latencyCounts = new Map(LATENCY_BUCKET_ORDER.map(k => [k, 0]))
      for (const min of protectionDurations) {
        const key = latencyBucketForMinutes(min)
        latencyCounts.set(key, (latencyCounts.get(key) ?? 0) + 1)
      }

      return {
        total,
        fraudulent,
        legitimate,
        fraudRatio,
        today,
        entityReferences,
        other,
        uniqueBeneficiaries: uniqueCount(() => true),
        fraudulentUniqueBeneficiaries: uniqueCount(e => !!e.email_sent),
        legitimateUniqueBeneficiaries: uniqueCount(e => !e.email_sent),
        medianProtectionMinutes,
        measuredProtectionEntries: protectionDurations.length,
        // Same replayDayIdxShifted cutoff as the Volume chart's
        // visibleChartData mask — without it, days beyond the current
        // replay position still carry whatever value buildSibsChartData
        // computed for them and show up as a bump ahead of where replay
        // actually is.
        daily: sibsChartData.map((d, i) => {
          const reached = i + 1 <= replayDayIdxShifted
          return {
            date: d.date,
            total: reached ? (d.legitimate ?? 0) + (d.dispatched ?? 0) : 0,
            fraudulent: reached ? (d.dispatched ?? 0) : 0,
            legitimate: reached ? (d.legitimate ?? 0) : 0,
            fraudRatio: reached ? (d.fraudRatio ?? 0) : 0,
          }
        }),
        latencyData: LATENCY_BUCKET_ORDER.map(key => ({ key, count: latencyCounts.get(key) ?? 0 })),
        topLegitimate: rankBeneficiaries(chartSource, e => !e.email_sent),
        topFraudulent: rankBeneficiaries(chartSource, e => !!e.email_sent),
      }
    }
    if (!dashboardAggregate) return null
    const overview = dashboardAggregate.overview
    const beneficiaryCounts = dashboardAggregate.beneficiaryCounts
    const total = toNumber(overview?.total_entries)
    const fraudulent = toNumber(overview?.total_email_sent)
    const legitimate = toNumber(overview?.total_no_email_sent)
    const fraudRatio = total > 0 ? Math.round((fraudulent / total) * 100) : 0
    const daily = dashboardAggregate.daily.map((d) => {
      const day = new Date(`${d.day}T00:00:00`)
      const dayTotal = toNumber(d.total_entries)
      const dayFraud = toNumber(d.email_sent_count)
      const dayLegit = toNumber(d.no_email_sent_count)
      return {
        date: `${day.getDate()}/${day.getMonth() + 1}`,
        total: dayTotal,
        fraudulent: dayFraud,
        legitimate: dayLegit,
        fraudRatio: dayTotal > 0 ? Math.round((dayFraud / dayTotal) * 100) : 0,
      }
    })
    const todayKey = new Date().toISOString().slice(0, 10)
    const today = dashboardAggregate.daily
      .filter((d) => d.day === todayKey)
      .reduce((sum, d) => sum + toNumber(d.total_entries), 0)
    const toRankItems = (rows: Array<{ beneficiario: string; entry_count: number | string }>): BeneficiaryRankItem[] =>
      rows.map((row, index) => ({
        key: row.beneficiario.toLowerCase(),
        label: row.beneficiario,
        count: toNumber(row.entry_count),
        rank: index + 1,
      }))
    const latencyData = dashboardAggregate.latencyBuckets.map((row) => ({
      key: row.bucket,
      count: toNumber(row.entry_count),
    }))
    // beneficiario can still be null (not every entity reference resolves
    // one) — rank/index keeps the key unique in that case since there's no
    // other per-row identifier in this grouped-by-beneficiary shape.
    const topEmailedBeneficiaries: BeneficiaryRankItem[] = dashboardAggregate.topEmailedBeneficiaries.map((row, index) => ({
      key: row.beneficiario?.toLowerCase() ?? `unknown-${index}`,
      label: row.beneficiario ?? 'Unknown beneficiary',
      count: toNumber(row.emails_sent),
      rank: index + 1,
    }))
    return {
      total,
      fraudulent,
      legitimate,
      fraudRatio,
      today,
      entityReferences: toNumber(overview?.total_with_entity_reference),
      other: Math.max(0, total - toNumber(overview?.total_with_entity_reference)),
      uniqueBeneficiaries: toNumber(beneficiaryCounts?.total_unique_beneficiaries),
      fraudulentUniqueBeneficiaries: toNumber(beneficiaryCounts?.fraudulent_unique_beneficiaries),
      legitimateUniqueBeneficiaries: toNumber(beneficiaryCounts?.legitimate_unique_beneficiaries),
      medianProtectionMinutes: dashboardAggregate.medianProtection?.median_hours == null
        ? null
        : toNumber(dashboardAggregate.medianProtection.median_hours) * 60,
      measuredProtectionEntries: toNumber(dashboardAggregate.medianProtection?.measured_entries),
      daily,
      latencyData,
      topLegitimate: toRankItems(dashboardAggregate.topLegitimate),
      topFraudulent: toRankItems(dashboardAggregate.topFraudulent),
      topEmailedBeneficiaries,
    }
  }, [dashboardAggregate, isReplaying, chartSource, protectionDurations, medianProtectionMinutes, sibsChartData, replayDayIdxShifted])

  // ── Actions ───────────────────────────────────────────────────────────────────

  const clearCompletedReplay = useCallback(() => {
    if (replayStatus !== 'done') return
    setReplayStatus('idle')
    setReplayOpen(false)
    setReplayQueue([])
    replayLogQueueRef.current = []
    setReplayMaxDate(null)
    setReplayIndex(0)
  }, [replayStatus])

  const applyTimeRange = useCallback((from: string, to: string) => {
    clearCompletedReplay()
    setChartRange(from, to)
    setChartCoupled(false)
  }, [clearCompletedReplay, setChartRange])

  const toggleCheck = (id: string) =>
    setCheckedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const toggleAll = () => {
    const eligible = filtered.filter(canBulkClassify)
    setCheckedIds((prev) =>
      prev.size === eligible.length ? new Set() : new Set(eligible.map((e) => e.id)),
    )
  }

  const copyTsv = () => {
    const rows = filtered.filter((e) => checkedIds.has(e.id))
    const esc = (v: string) => {
      if (v.includes('\t') || v.includes('\n') || v.includes('"'))
        return `"${v.replace(/"/g, '""')}"`
      return v
    }
    const header = ['User_Entry_ID', 'Created_At', 'Email_Sent', 'Entry_Type', 'Post_Type', 'Source_Type', 'Source', 'Entity_Reference_Entity', 'Entity_Reference_Reference', 'Beneficiário', 'Unsolicited', 'Sender', 'Message_Content', 'Scam_Claims', 'Platform', 'Platform_Text', 'Platform_ID', 'Raw_Free_Input', 'ID']
    const lines = rows.map((e) =>
      [
        e.user_entry_id ?? '',
        fmtDate(e.created_at),
        e.email_sent ? 'true' : 'false',
        e.entry_type ?? '',
        e.post_type ?? '',
        e.source_type ?? '',
        e.source ?? '',
        e.entity_reference_entity ?? '',
        e.entity_reference_reference ?? '',
        e.beneficiario ?? '',
        unsolicitedLabel(e) ?? '',
        e.sender ?? '',
        e.message_content,
        String(claimCount(e.scam_claims)),
        e.platform_namelong ?? '',
        e.platform_text ?? '',
        e.platform_id != null ? String(e.platform_id) : '',
        e.raw_free_input ?? '',
        e.id,
      ].map(esc).join('\t')
    )
    navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    setCopyFlash(true)
    setTimeout(() => setCopyFlash(false), 1500)
  }

  // rawEntryId over id — the backend's accept path keys off raw_entries, and
  // entry.raw_entry_id is only ever null for branches that never had one
  // (e.g. call_scrapes), where id already *is* the right identity to send.
  const handleBulkClassify = async (form: BulkClassifyForm) => {
    const selected = filtered.filter((e) => checkedIds.has(e.id))
    const rawEntryIds = selected.map((e) => e.raw_entry_id ?? e.id)
    // Reverse lookup so a succeeded rawEntryId can be traced back to the
    // entry.id checkedIds is actually keyed on — needed below to un-check
    // rows that just flipped source_type to 'processed_entry' (see
    // canBulkClassify), since a partial-failure batch doesn't clear
    // checkedIds wholesale and would otherwise leave them checked-but-
    // disabled.
    const entryIdByRawEntryId = new Map(selected.map((e) => [e.raw_entry_id ?? e.id, e.id]))
    if (rawEntryIds.length === 0) return
    setBulkClassifySubmitting(true)
    setBulkClassifyResult(null)
    const result = await apiPost<{ results: Array<{ rawEntryId: string; success: boolean; error?: unknown }> }>('/bulk-classify', {
      rawEntryIds,
      ...form,
    })
    setBulkClassifySubmitting(false)
    // Gate on results, not result.success: the backend's success is
    // results.every(ok), so one failed row used to discard every row that
    // did get classified (nothing patched, all reported failed).
    if (result.data?.results) {
      const failures = result.data.results.filter((r) => !r.success)
      const succeededIds = new Set(result.data.results.filter((r) => r.success).map((r) => r.rawEntryId))
      // Patch in place rather than refetching — see applyBulkClassifyPatch's
      // own doc comment for why a refetch here used to make just-accepted
      // rows vanish from the table.
      if (succeededIds.size > 0) {
        setEntries((prev) => applyBulkClassifyPatch(prev, succeededIds, form, catalog))
        setCheckedIds((prev) => {
          const next = new Set(prev)
          for (const rawEntryId of succeededIds) {
            const id = entryIdByRawEntryId.get(rawEntryId)
            if (id) next.delete(id)
          }
          return next
        })
      }
      setBulkClassifyResult({ successCount: succeededIds.size, failures })
      if (failures.length === 0) setBulkClassifyOpen(false)
    } else {
      setBulkClassifyResult({
        successCount: 0,
        failures: rawEntryIds.map((rawEntryId) => ({ rawEntryId, error: result.error ?? 'Request failed' })),
      })
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  // loading/error used to gate the entire component here (dashboard
  // included) until the entries table finished its first fetch. Now that
  // the table is opt-in (see entriesRequested/"Load entries"), both are
  // handled locally within the Table section below instead.

  return (
    <>
      <Turnstile ref={screenshotTurnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleScreenshotVerify} />
      <BulkClassifyDialog
        open={bulkClassifyOpen}
        onOpenChange={setBulkClassifyOpen}
        count={checkedIds.size}
        catalog={catalog}
        theme={theme}
        submitting={bulkClassifySubmitting}
        result={bulkClassifyResult}
        onSubmit={handleBulkClassify}
      />

      {selected ? (
        <SibsEntryDetailPage
          entry={selected}
          theme={theme}
          onClose={detail.close}
          sentIds={sentIds}
          onSent={(id) => setSentIds((prev) => new Set([...prev, id]))}
          payloadRequested={payloadRequested}
          onRequestPayload={() => setPayloadRequested(true)}
          getScreenshotToken={getScreenshotToken}
          showInvestigationSidebar
        />
      ) : (
        <BaseDashboard
          theme={theme}
          eyebrow="SIBS · Custom Mail Alerts"
          title="Alert volume"
          rootRef={restoreListScrollRef}
          lastUpdated={lastLoadedAt}
          // Guarded (rather than just disabling the button) so the shared
          // auto-refresh timer in RefreshControl — which calls onRefresh
          // directly, bypassing the disabled attribute — doesn't fire a
          // fetch mid-replay either.
          onRefresh={() => { if (!isReplaying) fetchEntries(0, true) }}
          refreshing={refreshing || isReplaying}
          storageKey="sibs-mail-header"
          timeRange={{ from: chartFrom, to: chartTo, onApply: applyTimeRange, disabled: replayControlsLocked }}
          headerActions={
            <>
              {missingId && (
                <span className={`font-mono text-[10px] ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                  Entry {missingId.slice(0, 8)} not found
                  <button type="button" className={`ml-2 underline ${muted}`} onClick={() => setMissingId(null)}>dismiss</button>
                </span>
              )}
              <button
                onClick={() => {
                  clearCompletedReplay()
                  setChartCoupled((v) => !v)
                }}
                disabled={replayControlsLocked}
                title={chartCoupled ? 'Using table filters — click to use the selected time range' : 'Using selected time range — click to couple charts to the table'}
                className={`flex h-7 items-center gap-1.5 border px-2.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-40 ${
                  chartCoupled
                    ? 'border-signal bg-signal-surface text-signal'
                    : `${border} ${muted} hover:border-signal hover:text-signal`
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${chartCoupled ? 'bg-signal' : 'bg-muted'}`} />
                {chartCoupled ? 'Table' : 'Range'}
              </button>
              <div className="relative" ref={replayPopupRef}>
                <button
                  onClick={() => {
                    if (replayStatus === 'loading') return
                    if (replayStatus === 'playing') {
                      setReplayStatus('paused')
                      setReplayOpen(false)
                      return
                    }
                    setReplayOpen(v => !v)
                  }}
                  title={replayStatus === 'loading' ? 'Loading replay' : replayStatus === 'playing' ? 'Pause replay' : 'Replay'}
                  className={`flex h-7 w-7 items-center justify-center border font-mono text-[15px] leading-none transition-colors ${
                    replayOpen || isReplaying
                      ? 'border-warning-border text-warning bg-warning-surface'
                      : `${border} ${muted} hover:border-warning-border hover:text-warning`
                  }`}
                >
                  {replayStatus === 'loading'
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : replayStatus === 'playing'
                      ? <Pause className="h-3.5 w-3.5" />
                      : '···'}
                </button>

                {replayOpen && replayStatus !== 'loading' && replayStatus !== 'playing' && (
                  <div className={`absolute right-0 top-full z-30 mt-1 w-72 rounded border p-4 shadow-notice bg-card border-border`}>
                    <div className={`font-mono text-[9px] uppercase tracking-widest mb-3 ${muted}`}>// Replay</div>

                    <div className="flex flex-col gap-2 mb-3">
                      <div className={`font-mono text-[10px] px-1 ${muted}`}>
                        {isoToPtDate(chartFrom)} → {isoToPtDate(chartTo)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-[9px] w-7 shrink-0 uppercase tracking-widest ${muted}`}>Rate</span>
                        <input type="number" min={1} value={replayRate}
                          onChange={e => setReplayRate(Math.max(1, Number(e.target.value)))}
                          className={`w-16 h-7 border px-2 font-mono text-[11px] text-center ${border} bg-card text-muted-foreground`}
                        />
                        <span className={`font-mono text-[10px] ${muted}`}>/s</span>
                      </div>
                    </div>

                    <div className={`border-t mb-3 ${border}`} />

                    <div className="flex items-center gap-2">
	                      {replayStatus === 'idle' && (
	                        <button onClick={startReplay}
	                          className="flex h-7 flex-1 items-center justify-center gap-1.5 border border-warning-border px-3 font-mono text-[10px] uppercase tracking-widest text-warning transition-colors hover:bg-warning-surface"
	                        ><Play className="h-3 w-3" /> Play</button>
	                      )}
	                      {replayStatus === 'paused' && (<>
	                        <button onClick={() => setReplayStatus('playing')}
	                          className="flex h-7 items-center gap-1.5 border border-warning-border px-3 font-mono text-[10px] uppercase tracking-widest text-warning transition-colors hover:bg-warning-surface"
	                        ><Play className="h-3 w-3" /> Resume</button>
	                        <button onClick={startReplay}
	                          className="flex h-7 items-center gap-1.5 border border-warning-border px-3 font-mono text-[10px] uppercase tracking-widest text-warning transition-colors hover:bg-warning-surface"
	                        ><RotateCcw className="h-3 w-3" /> Replay</button>
	                        <button onClick={stopReplay}
	                          className={`flex h-7 items-center gap-1.5 border px-3 font-mono text-[10px] uppercase tracking-widest transition-colors hover:border-destructive hover:text-destructive ${border} ${muted}`}
	                        ><Square className="h-3 w-3" /> Stop</button>
	                      </>)}
	                      {replayStatus === 'done' && (
	                        <button onClick={startReplay}
	                          className="flex h-7 items-center gap-1.5 border border-success px-3 font-mono text-[10px] uppercase tracking-widest text-success transition-colors hover:bg-success-surface"
	                        ><Play className="h-3 w-3" /> Play again</button>
	                      )}
	                    </div>

	                    {(replayStatus === 'paused' || replayStatus === 'done') && (
                      <div className="mt-3 flex items-center gap-2">
                        <div className={`flex-1 h-0.5 rounded-full bg-secondary`}>
                          <div
                            className={`h-0.5 rounded-full transition-all ${replayStatus === 'done' ? 'bg-success' : 'bg-warning'}`}
                            style={{ width: `${replayQueue.length > 0 ? (replayIndex / replayQueue.length) * 100 : 0}%` }}
                          />
                        </div>
                        <span className={`font-mono text-[10px] tabular-nums shrink-0 ${replayStatus === 'done' ? 'text-success' : muted}`}>
                          {replayStatus === 'done' ? '✓ done' : `${replayIndex} / ${replayQueue.length}`}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          }
          banner={
            /* One or more of the 7 dashboard RPCs failed (e.g. a statement
               timeout) but at least one succeeded — show what loaded, flag
               what didn't, instead of blanking every chart over a single
               bad query. */
            !entriesOnly && dashboardPartialErrors && Object.keys(dashboardPartialErrors).length > 0 && (
          <div className={`mb-2 flex items-start gap-2 border px-4 py-2 font-mono text-[11px] border-warning-border bg-warning-surface text-warning`}>
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span className="flex-1">
              {Object.keys(dashboardPartialErrors).length} of 7 dashboard queries failed to load:{' '}
              {Object.entries(dashboardPartialErrors).map(([key, msg]) => `${key} (${msg})`).join(', ')}
            </span>
            <button
              onClick={() => setDashboardRetryTick((t) => t + 1)}
              disabled={dashboardAggregateLoading}
              className={`flex shrink-0 items-center gap-1 rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 ${isDark ? 'border-warning-border hover:bg-warning-surface' : 'border-warning-border hover:bg-amber-100'}`}
            >
              <RefreshCw className={`h-3 w-3 ${dashboardAggregateLoading ? 'animate-spin' : ''}`} />
              Retry
            </button>
          </div>
            )
          }
          widgetCols={6}
          widgetRows="8rem 16rem 16rem 26rem"
          widgetsOverlay={
            dashboardReloadVisible && (
              <div className="pointer-events-none absolute inset-0 z-20 ">
                <div className={`absolute inset-0 animate-pulse bg-signal-surface/60`} />
                <div className={`absolute left-0 right-0 top-0 h-1 bg-muted`}>
                  <div
                    className="h-full bg-signal shadow-[0_0_18px_rgba(56,189,248,0.75)] transition-[width] duration-200 ease-out"
                    style={{ width: `${dashboardReloadProgress}%` }}
                  />
                </div>
                <div className={`absolute right-3 top-3 flex items-center gap-2 rounded-[4px] border px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest shadow-sm border-signal/30 bg-card text-signal`}>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Updating range
                </div>
              </div>
            )
          }
          widgets={!entriesOnly && (
            <>
          {dashboardAggregateError ? (
            <div className="col-span-6 h-full">
              <RetryBanner
                message={dashboardAggregateError}
                onRetry={() => setDashboardRetryTick((t) => t + 1)}
                retrying={dashboardAggregateLoading}
                isDark={isDark}
              />
            </div>
          ) : !aggregatePreview ? (
            <div className={`col-span-6 flex h-full items-center gap-2 border px-4 font-mono text-[11px] border-border bg-card text-muted-foreground`}>
              <Loader2 className={`h-4 w-4 shrink-0 ${dashboardAggregateLoading ? 'animate-spin' : ''}`} />
              Loading dashboard RPC micro charts
            </div>
          ) : (
            <>
              <SparkCard
                id="inrange"
                label="In range"
                value={aggregatePreview.total}
                sub1={{ label: 'Entity ref', value: aggregatePreview.entityReferences, color: 'var(--success)' }}
                sub2={{ label: 'Other', value: aggregatePreview.other, color: 'var(--warning)' }}
                trend={aggregatePreview.today > 0 ? `+${aggregatePreview.today} today` : undefined}
                chartData={aggregatePreview.daily}
                series={[
                  { key: 'legitimate', color: 'var(--signal)' },
                  { key: 'fraudulent', color: 'var(--destructive)' },
                ]}
                isDark={isDark}
                className="h-full"
              />
              <SparkCard
                id="dispatched"
                label="Fraudulent"
                value={aggregatePreview.fraudulent}
                trend={aggregatePreview.total > 0 ? `${aggregatePreview.fraudRatio}%` : undefined}
                chartData={aggregatePreview.daily}
                series={[
                  { key: 'fraudulent', color: 'var(--destructive)' },
                ]}
                isDark={isDark}
                className="h-full"
              />
              <SparkCard
                id="remaining"
                label="Legitimate"
                value={aggregatePreview.legitimate}
                chartData={aggregatePreview.daily}
                series={[{ key: 'legitimate', color: 'var(--signal)' }]}
                isDark={isDark}
                className="h-full"
              />
              <SparkCard
                id="fraud-ratio"
                label="Fraud ratio"
                value={aggregatePreview.fraudRatio}
                valueSuffix="%"
                sub1={{ label: 'Fraudulent', value: aggregatePreview.fraudulent, color: 'var(--destructive)' }}
                sub2={{ label: 'Legitimate', value: aggregatePreview.legitimate, color: 'var(--signal)' }}
                chartData={aggregatePreview.daily}
                series={[
                  { key: 'fraudRatio', color: '#f43f5e' },
                ]}
                isDark={isDark}
                className="h-full"
              />
              <ProtectionLatencyCard
                data={aggregatePreview.latencyData}
                isDark={isDark}
                muted={muted}
                text={text}
              />
              <BeneficiaryRankingCard
                eyebrow="Legitimate"
                title="Beneficiaries cleared"
                items={aggregatePreview.topLegitimate}
                accent="var(--signal)"
                isDark={isDark}
                className="col-start-6 row-start-1 row-span-2 h-full"
                limit={12}
                uniqueCount={aggregatePreview.legitimateUniqueBeneficiaries}
              />
            </>
          )}
        {/* ── Chart 1 · Volume ─────────────────────────────────────── */}
        <LegitimateFraudulentWaveChart
          data={legitimateFraudulentWaveData}
          sourceLength={sibsChartData.length}
          yMax={domainLock.yLeftMax}
          isDark={isDark}
          isReplaying={isReplaying}
          replayDayIdxShifted={replayDayIdxShifted}
          chartFrom={chartFrom}
          chartTo={chartTo}
          muted={muted}
          text={text}
          chartDataLoading={chartDataLoading}
          chartDataError={chartDataError}
          onRetry={() => setChartRetryTick((t) => t + 1)}
          onExpand={() => setLegitimateFraudulentExpanded(true)}
          className="col-span-4 row-start-2 h-full"
          chartId="main"
        />

        <BeneficiaryRankingCard
          eyebrow="Entity references"
          title="Emails per beneficiary"
          items={aggregatePreview?.topEmailedBeneficiaries ?? []}
          accent="#a855f7"
          isDark={isDark}
          className="col-start-5 row-start-2 h-full"
          limit={12}
        />

        <BeneficiaryRankingCard
          eyebrow="Scam beneficiaries"
          title="Beneficiaries under attack"
          items={aggregatePreview?.topFraudulent ?? []}
          accent="var(--destructive)"
          isDark={isDark}
          className="col-start-6 row-start-3 h-full"
          limit={8}
          uniqueCount={aggregatePreview?.fraudulentUniqueBeneficiaries}
        />

        <div className="col-span-4 row-start-3 min-w-0 relative">
          {logsRequested ? (
            <>
              <FraudWaveChart
                data={fraudWaveData}
                isDark={isDark}
                replayActive={isReplaying}
                chartFrom={chartFrom}
                replayMaxDate={replayMaxDate}
                medianProtectionMinutes={aggregatePreview?.medianProtectionMinutes ?? medianProtectionMinutes}
                unmitigatedActiveDays={fraudWaveUnmitigatedActiveDays}
                onUnmitigatedActiveDaysChange={setFraudWaveUnmitigatedActiveDays}
                className="h-full"
              />
              <button
                onClick={() => setFraudWaveExpanded(true)}
                title="Expand chart"
                className={`absolute right-3 top-3 z-10 flex h-6 w-6 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
              {!isReplaying && (
                <button
                  onClick={() => setLogsRequested(false)}
                  title="Close logs"
                  className={`absolute right-10 top-3 z-10 flex h-6 w-6 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          ) : (
            <div className={`flex h-full flex-col items-center justify-center gap-3 border border-border bg-card`}>
              <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Fraud wave flattened</div>
              <button
                onClick={() => setLogsRequested(true)}
                className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors border-border text-muted-foreground hover:bg-secondary`}
              >
                <Mail className="h-3 w-3" />
                Show logs
              </button>
            </div>
          )}
        </div>

        <CategoricalPieCard
          data={platformBreakdown}
          eyebrow="Platform"
          totalSuffix="emailed entries"
          emptyLabel="No emailed entries in range"
          isDark={isDark}
          muted={muted}
          text={text}
          className="col-start-5 row-start-3 h-full"
        />

        {/* ── Row 4 · where the entries came from ─────────────────── */}
        {/* Scoped by Hono from the dashboard key (sibs-mail counts
            multibanco-validator entries), over the same range as the charts. */}
        <EntryLocationMap
          dashboard="sibs-mail"
          since={chartFrom}
          until={chartTo}
          isDark={isDark}
          className="col-span-6 row-start-4 h-full"
        />

            </>
          )}
        >
          {!entriesRequested && !isReplaying ? (
          <div className="flex h-32 flex-col items-center justify-center gap-3">
            <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Entries table</div>
            <button
              onClick={() => setEntriesRequested(true)}
              className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors border-border text-muted-foreground hover:bg-secondary`}
            >
              <Search className="h-3 w-3" />
              Load entries
            </button>
          </div>
        ) : loading ? (
          <div className="flex h-32 items-center justify-center">
            <span className={`font-mono text-[11px] uppercase tracking-widest animate-pulse ${muted}`}>Loading…</span>
          </div>
        ) : error ? (
          <div className="flex h-32 items-center justify-center px-4">
            <RetryBanner message={error} onRetry={() => fetchEntries(0, true)} retrying={refreshing} isDark={isDark} />
          </div>
        ) : (
        <>
        {/* rows */}
        {/* No overflow-x wrapper here — verified against the real compiled
            CSS in a headless browser: ANY non-visible overflow value on this
            div (auto, clip, even overflow-x-auto alone, since the CSS spec
            silently forces the other axis to a non-visible value too) makes
            it the nearest "scroll container" ancestor, and position:sticky
            in ResizableTable sticks to THAT instead of escaping to the
            page's real scrolling <section> — which never happens since this
            div's own height always just matches its content. Trade-off: a
            manually-widened table can now overflow the rounded card instead
            of getting a local horizontal scrollbar; sticky headers win. */}
        <div>
          <ResizableTable
            columns={sibsColumns.map((c, i) =>
              i === 0
                // sibsColumns[0] is normally the 32px 'expand' column (empty
                // label) — too narrow for an actual checkbox once repurposed
                // here (see CHECKBOX_COL_WIDTH's own comment), so its width
                // is widened along with the label, not just swapped.
                ? { ...c, initialWidth: CHECKBOX_COL_WIDTH, thClassName: CHECKBOX_COL_TH_CLASS, label: (
                    <TableCheckboxHeaderLabel
                      checked={filtered.some(canBulkClassify) && checkedIds.size === filtered.filter(canBulkClassify).length}
                      onToggle={toggleAll}
                      title="Select all raw entries"
                    />
                  )}
                : c
            )}
            stickyHeader
            theadClassName={'bg-card border-b border-border'}
            fixedLayout
            clipCells
            theme={theme}
            isEmpty={filtered.length === 0}
            emptyMessage="No entries found"
            onRefresh={() => { if (!isReplaying) fetchEntries(0, true) }}
            refreshing={refreshing || isReplaying}
            lastUpdated={lastLoadedAt}
            storageKey="sibs-mail-table"
            toolbar={
              <>
                {refreshing ? (
                  <Loader2 className={`h-3.5 w-3.5 shrink-0 animate-spin text-signal`} />
                ) : (
                  <Search className={`h-3.5 w-3.5 shrink-0 ${muted}`} />
                )}
                <Input
                  placeholder="Filter by message, sender, entity…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-7 border-0 shadow-none bg-transparent focus-visible:ring-0 px-0 text-[13px]"
                />
                {refreshing && (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-signal shrink-0">
                    {query.trim() ? 'Searching…' : 'Loading…'}
                  </span>
                )}
                <Filter className={`h-3.5 w-3.5 shrink-0 ${muted}`} />
                <span className={`font-mono text-[10px] uppercase tracking-widest whitespace-nowrap ${muted}`}>
                  {filtered.length} / {entries.length}
                </span>
                {checkedIds.size > 0 && (
                  <button
                    onClick={copyTsv}
                    className={`ml-2 flex items-center gap-1.5 px-2.5 py-1 border font-mono text-[10px] uppercase tracking-widest transition-colors shrink-0 ${copyFlash ? 'border-success bg-success-surface text-success' : `${border} ${panelBg} ${soft} hover:border-signal hover:text-signal`}`}
                  >
                    <ClipboardCopy className="h-3 w-3" />
                    {copyFlash ? 'Copied!' : `Copy ${checkedIds.size}`}
                  </button>
                )}
                {checkedIds.size > 0 && (
                  <button
                    onClick={() => { setBulkClassifyResult(null); setBulkClassifyOpen(true) }}
                    className={`ml-2 flex items-center gap-1.5 px-2.5 py-1 border font-mono text-[10px] uppercase tracking-widest transition-colors shrink-0 ${border} ${panelBg} ${soft} hover:border-signal hover:text-signal`}
                  >
                    <Tags className="h-3 w-3" />
                    {`Classify ${checkedIds.size}`}
                  </button>
                )}
                {!isReplaying && (
                  <button
                    onClick={() => setEntriesRequested(false)}
                    title="Close entries"
                    className={`flex items-center justify-center h-6 w-6 border transition-colors hover:text-signal hover:border-signal ${border} ${muted}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </>
            }
          >
            {filtered.map((e) => {
              const highlighted = newEntryIds.has(e.id) || lastSelectedId === e.id
              // email_sent takes priority over the blue new/last-selected
              // highlight when both apply — computed as one mutually
              // exclusive class rather than stacking bg-*/border-l-* utility
              // classes, since Tailwind doesn't guarantee the later one in
              // the className string wins if both are present.
              // sentIds (not just e.email_sent) so a row turns red the moment
              // a dispatch succeeds — onSent adds to sentIds immediately, but
              // the entry object's own email_sent field stays stale until the
              // list is refetched from the server.
              const rowStateClass = (e.email_sent || sentIds.has(e.id))
                ? 'border-l-destructive bg-danger-surface hover:bg-danger-surface/70'
                : highlighted
                  ? 'border-l-signal bg-signal-surface hover:bg-signal-surface'
                  : ''
              return (
                <tr
                  key={e.id}
                  data-entry-id={e.id}
                  onClick={(ev) => {
                    // Ctrl/Cmd-click anywhere on the row: the entry in a new
                    // tab, the list stays (the date is a real link too)
                    if (ev.ctrlKey || ev.metaKey) { window.open(detail.href(e.id), '_blank', 'noopener'); return }
                    rowClickToggleOrOpen(
                    checkedIds.size > 0,
                    () => { if (canBulkClassify(e)) toggleCheck(e.id) },
                    () => {
                      const sp = getScrollParent(ev.currentTarget)
                      if (sp) {
                        // Capture for restoreListScrollRef to reapply when
                        // coming back — but reset now so the detail view
                        // (which shares this same scroll ancestor) opens at
                        // its own top instead of inheriting wherever the
                        // list happened to be scrolled.
                        listScrollTopRef.current = sp.scrollTop
                        sp.scrollTop = 0
                      }
                      setLastSelectedId(e.id)
                      setMissingId(null)
                      setSelected(e)
                      detail.open(e.id)
                    },
                  )}}
                  onAuxClick={(ev) => { if (ev.button === 1) window.open(detail.href(e.id), '_blank', 'noopener') }}
                  className={`border-b border-l-2 border-l-transparent transition-colors cursor-pointer hover:border-l-signal hover:bg-secondary ${rowLine} ${checkedIds.has(e.id) ? 'bg-signal-surface' : ''} ${rowStateClass}`}
                >
                  <TableCheckboxTd
                    checked={checkedIds.has(e.id)}
                    onToggle={() => toggleCheck(e.id)}
                    disabled={!canBulkClassify(e)}
                    title={canBulkClassify(e) ? undefined : CANNOT_BULK_CLASSIFY_TITLE}
                  />
                  <TableTd className={`font-mono truncate ${label}`}>
                    {/* A real link to the entry's page (/sibs/mail-entries/{id}):
                        right-click "open in new tab", middle-click and Ctrl/Cmd-click
                        are the browser's own; a plain click stays in the app
                        through the row. */}
                    <a href={detail.href(e.id)} data-action="entry-link" className="hover:underline"
                      onClick={(ev) => {
                        if (ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey || ev.button !== 0) { ev.stopPropagation(); return }
                        ev.preventDefault()   // the row's handler navigates in place and remembers the scroll
                      }}
                      onAuxClick={(ev) => ev.stopPropagation()}>{fmtDate(e.created_at)}</a>
                  </TableTd>
                  <TableTd className="font-mono truncate">{e.post_type ?? ''}</TableTd>
                  <TableTd className="font-mono truncate"><Highlight text={e.beneficiario} query={appliedQuery} /></TableTd>
                  <TableTd className="font-mono truncate">{unsolicitedLabel(e) ?? ''}</TableTd>
                <td className="px-1 py-2 align-middle text-center font-mono text-[13px] select-none" title={sentIds.has(e.id) ? 'Fraudulent case' : 'Legitimate case'}>
                    <span style={{ color: sentIds.has(e.id) ? '#0369a1' : 'var(--subtle-text)' }}>
                      {sentIds.has(e.id) ? '✓' : '✗'}
                    </span>
                  </td>
                  <TableTd className="font-mono truncate">{e.entry_type ?? ''}</TableTd>
                  <TableTd className="font-mono truncate">{e.source_type ?? ''}</TableTd>
                  <TableTd className="font-mono truncate">{e.source ?? ''}</TableTd>
                  <TableTd className="font-mono truncate"><Highlight text={e.entity_reference_entity} query={appliedQuery} /></TableTd>
                  <TableTd className="font-mono truncate"><Highlight text={e.entity_reference_reference} query={appliedQuery} /></TableTd>
                  <TableTd className="font-mono truncate"><Highlight text={e.platform_namelong} query={appliedQuery} /></TableTd>
                  <TableTd className="font-mono truncate"><Highlight text={e.platform_text} query={appliedQuery} /></TableTd>
                  <TableTd className="font-mono truncate text-center">{e.platform_id ?? ''}</TableTd>
                  <TableTd><span className="line-clamp-1"><Highlight text={e.message_content} query={appliedQuery} /></span></TableTd>
                  <TableTd className="font-mono text-center">{claimCount(e.scam_claims)}</TableTd>
                  <TableTd className="font-mono truncate"><Highlight text={e.sender} query={appliedQuery} /></TableTd>
                  <TableTd><span className="line-clamp-1">{e.raw_free_input ?? ''}</span></TableTd>
                  <TableTd className={`font-mono truncate text-[10px] ${muted}`}>{e.id}</TableTd>
                </tr>
              )
            })}
          </ResizableTable>
        </div>

        {/* sentinel */}
        <div ref={sentinelRef} className={`px-4 py-2 border-t flex items-center gap-2 ${border} bg-card`}>
          {isReplaying
            ? replayStatus === 'loading'
              ? <><Loader2 className="h-3 w-3 animate-spin text-warning" /><span className="font-mono text-[10px] uppercase tracking-widest text-warning">Loading replay…</span></>
              : replayStatus === 'done'
                ? <>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-success">✓ Replay complete — {replayQueue.length} entries</span>
                    <div className="ml-auto flex items-center gap-1.5">
                      <button onClick={startReplay} className="flex h-6 items-center gap-1 border border-success px-2 font-mono text-[10px] uppercase tracking-widest text-success hover:bg-success-surface transition-colors">
                        <Play className="h-2.5 w-2.5" /> Play again
                      </button>
                    </div>
                  </>
                : <>
                    <span className={`h-1.5 w-1.5 rounded-full bg-warning ${replayStatus === 'playing' ? 'animate-pulse' : ''}`} />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-warning">
                      {replayStatus === 'paused' ? 'Paused' : 'Replaying'} · {replayIndex} / {replayQueue.length}
                    </span>
                    <div className="ml-auto flex items-center gap-1.5">
                      {replayStatus === 'playing'
                        ? <button onClick={() => setReplayStatus('paused')} className="flex h-6 items-center gap-1 border border-warning-border px-2 font-mono text-[10px] uppercase tracking-widest text-warning hover:bg-warning-surface transition-colors">
                            <Pause className="h-2.5 w-2.5" /> Pause
                          </button>
                        : <>
                            <button onClick={() => setReplayStatus('playing')} className="flex h-6 items-center gap-1 border border-warning-border px-2 font-mono text-[10px] uppercase tracking-widest text-warning hover:bg-warning-surface transition-colors">
                              <Play className="h-2.5 w-2.5" /> Resume
                            </button>
                            <button onClick={startReplay} className="flex h-6 items-center gap-1 border border-warning-border px-2 font-mono text-[10px] uppercase tracking-widest text-warning hover:bg-warning-surface transition-colors">
                              <RotateCcw className="h-2.5 w-2.5" /> Replay
                            </button>
                          </>
                      }
                      <button onClick={stopReplay} className={`flex h-6 items-center gap-1 border px-2 font-mono text-[10px] uppercase tracking-widest transition-colors hover:border-destructive hover:text-destructive ${border} ${muted}`}>
                        <Square className="h-2.5 w-2.5" /> Stop
                      </button>
                    </div>
                  </>
            : loadingMore
              ? <><Loader2 className={`h-3 w-3 animate-spin ${muted}`} /><span className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>Loading…</span></>
              : hasMore
                ? <span className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>{total != null ? `${total - entries.length} more` : 'more'}</span>
                : <span className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>— end —</span>
          }
        </div>
        </>
        )}
        </BaseDashboard>
      )}
      {legitimateFraudulentExpanded && (
        <LegitimateFraudulentMaximizedWindow
          data={legitimateFraudulentWaveData}
          sourceLength={sibsChartData.length}
          yMax={domainLock.yLeftMax}
          isDark={isDark}
          isReplaying={isReplaying}
          replayDayIdxShifted={replayDayIdxShifted}
          chartFrom={chartFrom}
          chartTo={chartTo}
          muted={muted}
          text={text}
          chartDataError={chartDataError}
          onRetry={() => setChartRetryTick((t) => t + 1)}
          chartId="expanded"
          onClose={() => setLegitimateFraudulentExpanded(false)}
        />
      )}
      {fraudWaveExpanded && (
        <FraudWaveMaximizedWindow
          data={fraudWaveData}
          isDark={isDark}
          replayActive={isReplaying}
          chartFrom={chartFrom}
          replayMaxDate={replayMaxDate}
          medianProtectionMinutes={aggregatePreview?.medianProtectionMinutes ?? medianProtectionMinutes}
          unmitigatedActiveDays={fraudWaveUnmitigatedActiveDays}
          onUnmitigatedActiveDaysChange={setFraudWaveUnmitigatedActiveDays}
          onClose={() => setFraudWaveExpanded(false)}
        />
      )}
    </>
  )
}

// ─── Alerts Analysis ────────────────────────────────────────────────────────
// A fraud-only slice of the Custom Mail Alert dashboard above: same KPI/chart
// vocabulary (SparkCard, FraudWaveChart, ProtectionLatencyCard,
// BeneficiaryRankingCard), scoped throughout to `email_sent === true` entries
// — the same predicate this file already calls "Fraudulent" everywhere else
// (there's no separate is_scam flag on SIBS entries; a dispatched takedown
// alert *is* the fraud signal here). Adds one new widget beyond that: a
// histogram of "Time from detection to protection" with auto-sized bins and
// a smoothed (monotone-interpolated) curve over them.
//
// Deliberately does not reuse CampaignsView's raw entries table (checkboxes,
// TSV export, infinite scroll, replay, detail drill-in) — that's a genuinely
// separate, deeply stateful piece, and individual entries are already
// reachable from the Custom Mail Alert tab. This is the analysis layer on
// top of the same data, not a second copy of the entry browser.

type HistogramBin = { label: string; mid: number; count: number }

function buildAutoHistogram(durations: number[]): HistogramBin[] {
  const n = durations.length
  if (n === 0) return []
  const min = durations[0]
  const max = durations[n - 1]
  if (min === max) return [{ label: formatDurationMinutes(min), mid: min, count: n }]
  // Sturges' rule for bin count, clamped to a range that stays readable in a
  // single compact widget regardless of how many measured cases there are.
  const binCount = Math.min(16, Math.max(5, Math.ceil(Math.log2(n) + 1)))
  const width = (max - min) / binCount
  const bins: HistogramBin[] = Array.from({ length: binCount }, (_, i) => ({
    label: '',
    mid: min + width * (i + 0.5),
    count: 0,
  }))
  for (const value of durations) {
    const idx = Math.min(binCount - 1, Math.floor((value - min) / width))
    bins[idx].count++
  }
  return bins.map((b) => ({ ...b, label: formatDurationMinutes(b.mid) }))
}

function ProtectionHistogramCard({
  durations,
  isDark,
  muted,
  text,
  className = 'col-span-6 h-full',
}: {
  durations: number[]
  isDark: boolean
  muted: string
  text: string
  className?: string
}) {
  const bins = useMemo(() => buildAutoHistogram(durations), [durations])
  const tooltipCls = 'border-border bg-card text-muted-foreground'
  const peak = bins.reduce<HistogramBin | null>((best, b) => (!best || b.count > best.count ? b : best), null)

  return (
    <div className={`flex min-w-0 flex-col overflow-hidden border border-border bg-card ${className}`}>
      <div className="flex shrink-0 items-start justify-between gap-4 px-4 pt-3 pb-2">
        <div className="min-w-0">
          <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Takedown time distribution</div>
          <div className={`mt-0.5 text-base font-semibold tracking-tight ${text}`}>
            <span className="font-mono tabular-nums">{durations.length}</span>
            <span className={`ml-2 text-[12px] font-normal ${muted}`}>measured cases · {bins.length} auto bins</span>
          </div>
        </div>
        {peak && (
          <div className={`shrink-0 text-right font-mono text-[10px] ${muted}`}>
            peak <span className={`font-semibold ${text}`}>~{peak.label}</span>
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1">
        {durations.length === 0 ? (
          <div className={`flex h-full items-center justify-center px-4 text-center font-mono text-[11px] ${muted}`}>
            No measured protection times in range
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={bins} margin={{ top: 8, right: 16, left: -20, bottom: 4 }}>
              <XAxis
                dataKey="label"
                tick={{ fill: 'var(--subtle-text)', fontSize: 8, fontFamily: 'monospace' }}
                tickLine={false}
                axisLine={false}
                interval={0}
                angle={-25}
                textAnchor="end"
                height={36}
              />
              <YAxis allowDecimals={false} tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} />
              <Tooltip
                cursor={{ fill: 'var(--secondary)' }}
                content={({ active, payload }: any) => {
                  if (!active || !payload?.length) return null
                  const p = payload[0].payload as HistogramBin
                  return (
                    <div className={`rounded border px-3 py-2 text-[11px] font-mono shadow-notice ${tooltipCls}`}>
                      <div className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-warning" />
                        <span>~{p.label}</span>
                        <span className={`ml-auto pl-4 font-semibold ${text}`}>{p.count}</span>
                      </div>
                    </div>
                  )
                }}
              />
              <Bar dataKey="count" radius={[3, 3, 0, 0]} fill={'color-mix(in oklab, var(--warning) 30%, transparent)'} isAnimationActive={false} />
              <Line type="monotone" dataKey="count" stroke="var(--warning)" strokeWidth={2} dot={false} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

// ─── Campaign type breakdown (pie) ──────────────────────────────────────────
// Derived client-side from the chart's own full-period fetch — see
// AlertsAnalysisView's campaignTypeBreakdown useMemo for why this no longer
// hits the (now-unused) /campaigns/sibs?breakdown=1 endpoint.

type ArrangeKey = 'spark' | 'latency' | 'median' | 'mean' | 'beneficiary' | 'fraudwave' | 'heatmap' | 'histogram' | 'pie' | 'platform'
const DEFAULT_WIDGET_ORDER: ArrangeKey[] = ['spark', 'latency', 'median', 'mean', 'beneficiary', 'fraudwave', 'heatmap', 'histogram', 'pie', 'platform']
// Row-span is expressed in a uniform 8rem unit (see the grid's
// gridTemplateRows/gridAutoRows) so a widget's actual pixel height stays
// identical no matter which row it lands in after a drag — a 2-row-span
// widget is always 16rem tall, whether it's row 1-2 or row 4-5.
const WIDGET_SIZE_CLASS: Record<ArrangeKey, string> = {
  spark: 'col-span-2 row-span-1',
  latency: 'col-span-1 row-span-1',
  median: 'col-span-1 row-span-1',
  mean: 'col-span-1 row-span-1',
  beneficiary: 'col-span-2 row-span-3',
  fraudwave: 'col-span-4 row-span-2',
  // Taller than every other widget (24 hour-rows need real vertical room) and
  // full-width so the weekday header and hour gutter both stay legible.
  heatmap: 'col-span-6 row-span-5',
  histogram: 'col-span-4 row-span-2',
  pie: 'col-span-2 row-span-2',
  platform: 'col-span-2 row-span-2',
}
const WIDGET_ORDER_STORAGE_KEY = 'sibs-alerts-widget-order'

type CampaignTypeCount = { type: string; count: number }

const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
  ENTITY_REFERENCE: 'Entity Reference',
  MBWAY: 'MB Way',
  MULTIBANCO: 'Multibanco',
  REPORT: 'Other / unclassified',
}
const CAMPAIGN_TYPE_COLORS: Record<string, string> = {
  ENTITY_REFERENCE: '#dc2626',
  MBWAY: 'var(--warning)',
  MULTIBANCO: '#0369a1',
  REPORT: 'var(--muted-foreground)',
}

// One slice, fully resolved (label + color already picked) — CategoricalPieCard
// itself is domain-agnostic, so every caller (campaign type's fixed small enum,
// platform's open-ended ranked set — see PLATFORM_PALETTE) does its own
// key→label/color resolution before handing data in.
type PieSliceDatum = { key: string; label: string; count: number; color: string }

// Shared pie-card engine behind both CampaignTypePieCard and the platform
// breakdown widget — same chart, legend, and tooltip regardless of what's
// being counted, so the two don't drift into slightly different pie
// implementations over time (see feedback: backoffice widgets should be
// reusable, not one-off per-view).
function CategoricalPieCard({
  data,
  eyebrow,
  totalSuffix,
  isDark,
  muted,
  text,
  className = 'col-span-2 h-full',
  emptyLabel = 'No data in range',
}: {
  data: PieSliceDatum[]
  eyebrow: string
  totalSuffix: string
  isDark: boolean
  muted: string
  text: string
  className?: string
  emptyLabel?: string
}) {
  const total = data.reduce((s, d) => s + d.count, 0)
  const tooltipCls = 'border-border bg-card text-muted-foreground'

  return (
    <div className={`flex min-w-0 flex-col overflow-hidden border border-border bg-card ${className}`}>
      <div className="flex shrink-0 flex-col gap-0.5 px-4 pt-3 pb-2">
        <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>{eyebrow}</div>
        <div className={`mt-0.5 text-base font-semibold tracking-tight ${text}`}>
          <span className="font-mono tabular-nums">{total}</span>
          <span className={`ml-2 text-[12px] font-normal ${muted}`}>{totalSuffix}</span>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        {data.length === 0 ? (
          <div className={`flex h-full items-center justify-center px-4 text-center font-mono text-[11px] ${muted}`}>{emptyLabel}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="count"
                nameKey="label"
                innerRadius="42%"
                outerRadius="78%"
                paddingAngle={2}
                isAnimationActive={false}
                labelLine={false}
                label={({ cx, cy, midAngle, innerRadius: ir, outerRadius: or, percent, value }: any) => {
                  if (!percent) return null
                  const RADIAN = Math.PI / 180
                  const radius = ir + (or - ir) * 0.58
                  const x = cx + radius * Math.cos(-midAngle * RADIAN)
                  const y = cy + radius * Math.sin(-midAngle * RADIAN)
                  return (
                    <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fill="#fff" style={{ fontFamily: 'monospace', pointerEvents: 'none' }}>
                      <tspan x={x} dy="-0.15em" style={{ fontSize: 12, fontWeight: 700 }}>{value}</tspan>
                      <tspan x={x} dy="1.15em" style={{ fontSize: 9, fontWeight: 500, opacity: 0.85 }}>{Math.round(percent * 100)}%</tspan>
                    </text>
                  )
                }}
              >
                {data.map((d) => (
                  <Cell key={d.key} fill={d.color} stroke="none" />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }: any) => {
                  if (!active || !payload?.length) return null
                  const p = payload[0].payload as PieSliceDatum
                  const pct = total > 0 ? Math.round((p.count / total) * 100) : 0
                  return (
                    <div className={`rounded border px-3 py-2 text-[11px] font-mono shadow-notice ${tooltipCls}`}>
                      <div className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} />
                        <span>{p.label}</span>
                        <span className={`ml-auto pl-4 font-semibold ${text}`}>{p.count} ({pct}%)</span>
                      </div>
                    </div>
                  )
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
      {data.length > 0 && (
        <div className={`flex shrink-0 flex-wrap gap-x-3 gap-y-1 border-t px-4 py-2 font-mono text-[10px] border-border`}>
          {data.map((d) => (
            <span key={d.key} className={`flex items-center gap-1.5 ${muted}`}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: d.color }} />
              {d.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function CampaignTypePieCard({
  data,
  isDark,
  muted,
  text,
  className = 'col-span-2 h-full',
}: {
  data: CampaignTypeCount[]
  isDark: boolean
  muted: string
  text: string
  className?: string
}) {
  const sliceData: PieSliceDatum[] = data.map((d) => ({
    key: d.type,
    label: CAMPAIGN_TYPE_LABELS[d.type] ?? d.type,
    count: d.count,
    color: CAMPAIGN_TYPE_COLORS[d.type] ?? 'var(--muted-foreground)',
  }))
  return (
    <CategoricalPieCard
      data={sliceData}
      eyebrow="Campaign type"
      totalSuffix="fraudulent campaigns"
      isDark={isDark}
      muted={muted}
      text={text}
      className={className}
      emptyLabel="No campaigns in range"
    />
  )
}

// Fixed 8-hue categorical order (dataviz skill default palette, re-stepped
// for this file's actual card surfaces — #ffffff light / #0d1620 dark — and
// re-validated there: passes lightness/chroma/CVD/normal-vision on the
// adjacent pairlist in both modes, which is the right pairlist for a pie —
// each slice only ever borders two neighbors around the ring, same as a
// stacked bar). Platform names are an open-ended, unranked-by-meaning set
// (swapping which platform is "first" carries no information), so this is
// nominal categorical: rank by count and assign slots 1..8 in that fixed
// order, never picking a color by name. A 9th-and-beyond platform folds into
// "Other" (gray, outside the 8-hue set) rather than generating a new hue.
const PLATFORM_PALETTE_LIGHT = ['#2a78d6', '#eb6834', 'var(--success)', '#eda100', '#e87ba4', '#008300', '#4a3aa7', 'var(--destructive)']
const PLATFORM_PALETTE_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767']
const PLATFORM_OTHER_COLOR = 'var(--muted-foreground)'
const PLATFORM_PALETTE_SIZE = PLATFORM_PALETTE_LIGHT.length

// Shared by AlertsAnalysisView and CampaignsView's own platform-breakdown pie
// widgets — same ranking/coloring rules, computed from whichever chart-period
// entries each view already has loaded (chartSource), no extra fetch.
// Dispatched-only (email_sent), same "what counts as a campaign" definition
// campaignTypeBreakdown uses. Missing/blank platform_namelong (multibanco
// entries carry no platform) groups as its own "Unknown" slice rather than
// silently folding into "Other" — that's a real, often-large category here,
// not overflow past the palette.
function buildPlatformBreakdown(entries: SibsChartEntry[], isDark: boolean): PieSliceDatum[] {
  // Grouped by platform_id, not platform_namelong — the id is the real
  // identity (a stable FK into the platform table); the name is a mutable
  // display label on top of it, so grouping by name risks splitting one
  // platform across slices (a rename mid-period) or merging two into one
  // (two ids that happen to share a label). No platform_id (multibanco
  // entries carry none) groups under its own "Unknown" key rather than
  // silently overflowing into the numbered "Other" fold below.
  const counts = new Map<string, number>()
  const labelById = new Map<string, string>()
  for (const e of entries) {
    if (!e.email_sent) continue
    const key = e.platform_id != null ? String(e.platform_id) : 'unknown'
    counts.set(key, (counts.get(key) ?? 0) + 1)
    if (!labelById.has(key)) labelById.set(key, e.platform_namelong?.trim() || 'Unknown')
  }
  const ranked = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  const palette = isDark ? PLATFORM_PALETTE_DARK : PLATFORM_PALETTE_LIGHT
  const head = ranked.slice(0, PLATFORM_PALETTE_SIZE).map(([key, count], i) => ({
    key,
    label: labelById.get(key) ?? key,
    count,
    color: palette[i],
  }))
  const tail = ranked.slice(PLATFORM_PALETTE_SIZE)
  const otherCount = tail.reduce((sum, [, count]) => sum + count, 0)
  return otherCount > 0
    ? [...head, { key: '__other__', label: `Other (${tail.length})`, count: otherCount, color: PLATFORM_OTHER_COLOR }]
    : head
}

// Same beneficiary-ranking shape CampaignsView's own aggregatePreview builds
// inline for its replay mode (recomputing dashboard-RPC-shaped numbers from
// chartSource instead of re-running the RPC) — extracted here so
// AlertsAnalysisView's platform-filtered local summary (see its own
// `summary` useMemo) can build the same ranking without duplicating this
// grouping/sorting logic a third time.
function rankBeneficiaries(entries: SibsChartEntry[], predicate: (e: SibsChartEntry) => boolean, limit = 12): BeneficiaryRankItem[] {
  const counts = new Map<string, number>()
  for (const e of entries) {
    if (!e.beneficiario || !predicate(e)) continue
    counts.set(e.beneficiario, (counts.get(e.beneficiario) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([beneficiario, count], index) => ({ key: beneficiario.toLowerCase(), label: beneficiario, count, rank: index + 1 }))
}

// ─── Fraud-by-weekday/hour heatmap ─────────────────────────────────────────

const HEATMAP_WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HEATMAP_WEEKDAY_LABELS_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

// Sequential warm (amber → vivid red) ramp — one hue family, monotonic
// lightness light→dark, low→high. Same array for both themes, deliberately
// *not* reversed in dark mode the way a neutral sequential ramp normally
// would be (color-formula.md's Sequential row: "flips anchor in dark", so
// the near-zero end recedes into each mode's own card surface). That
// reasoning backfired here: it made LOW counts the darkest, muddiest step —
// on the dark surface that read as "the small numbers are the intense-
// looking ones", exactly backwards from how a reader expects a heat scale to
// work (deeper/more saturated = more), and made low-but-nonzero cells hard
// to tell apart from the empty ones. Anchoring "vivid saturated red = most"
// in both themes instead matches that expectation everywhere, and the
// ramp's own steps were picked to still have real contrast against both
// card surfaces (#ffffff light / #0d1620 dark) at *both* ends — the low
// step is a muted amber, not a bare-white near-invisible tint, and the high
// step is a bright red-orange, not a dark brown that recedes into a dark
// surface — so activity is easy to spot at a glance in either theme, which
// is the whole point of this chart.
const HEATMAP_RAMP = ['#f6c89a', '#f2a565', '#ef8a42', '#ea6b25', '#e14a12', '#d62e08']

function heatmapCellColor(count: number, max: number): string | null {
  if (count === 0) return null
  const ramp = HEATMAP_RAMP
  const t = max > 0 ? count / max : 0
  const idx = Math.min(ramp.length - 1, Math.floor(t * ramp.length))
  return ramp[idx]
}

// Weekday (Mon→Sun) × hour-of-day (00→23) grid of dispatched-alert volume —
// same "which slice of the week is fraud actually landing in" question the
// Fraud Wave chart answers per-day, just cut along hour-of-week instead of
// calendar date. A plain CSS grid (one flat grid, header row + gutter column
// baked into the same track list) rather than Recharts — Recharts has no
// heat-cell primitive, and nesting a grid-per-row here (header row's grid
// separate from each hour row's own grid) risked the two drifting out of
// column alignment; one grid guarantees every column lines up.
function FraudHeatmapCard({
  data,
  isDark,
  muted,
  text,
  className = 'col-span-6 h-full',
  onExpand,
}: {
  data: WeekdayHourDatum[]
  isDark: boolean
  muted: string
  text: string
  className?: string
  /** Same "float a Maximize2 button over the card" contract as
   * LegitimateFraudulentWaveChart's own onExpand — omitted (as it is inside
   * FraudHeatmapMaximizedWindow) means no button, so the maximized modal
   * doesn't get a maximize button pointing at itself. */
  onExpand?: () => void
}) {
  const [hover, setHover] = useState<{ day: number; hour: number; count: number; x: number; y: number } | null>(null)
  // Toggles the cell fills only — the Low→High legend below stays visible
  // either way (it's gated on `total > 0`, not on this). Own local state
  // rather than lifted to AlertsAnalysisView — purely a display preference
  // with nothing else depending on it, so the compact widget and its
  // maximized modal (a separate FraudHeatmapCard instance) each remember
  // their own choice independently, same as every other widget-local UI
  // toggle in this file.
  const [showColors, setShowColors] = useState(true)
  const total = useMemo(() => data.reduce((s, d) => s + d.count, 0), [data])
  const max = useMemo(() => data.reduce((m, d) => Math.max(m, d.count), 0), [data])
  const peak = useMemo(
    () => data.reduce<WeekdayHourDatum | null>((best, d) => (!best || d.count > best.count ? d : best), null),
    [data],
  )
  const byDayHour = useMemo(() => {
    const m = new Map<string, number>()
    for (const d of data) m.set(`${d.day}-${d.hour}`, d.count)
    return m
  }, [data])
  const tooltipCls = 'border-border bg-card text-muted-foreground'
  const emptyCell = 'bg-secondary'
  const gridBorder = 'border-border'
  const clearHover = (day: number, hour: number) => setHover((h) => (h && h.day === day && h.hour === hour ? null : h))

  return (
    <div className={`relative flex min-w-0 flex-col overflow-hidden border border-border bg-card ${className}`}>
      <div className="flex shrink-0 items-start justify-between gap-4 px-4 pt-3 pb-2">
        <div className="min-w-0">
          <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Fraud by weekday &amp; hour</div>
          <div className={`mt-0.5 text-base font-semibold tracking-tight ${text}`}>
            <span className="font-mono tabular-nums">{total}</span>
            <span className={`ml-2 text-[12px] font-normal ${muted}`}>dispatched alerts in range</span>
          </div>
        </div>
        {peak && peak.count > 0 && (
          <div className={`shrink-0 text-right font-mono text-[10px] ${muted} ${onExpand ? 'pr-8' : ''}`}>
            peak <span className={`font-semibold ${text}`}>{HEATMAP_WEEKDAY_LABELS[peak.day]} {String(peak.hour).padStart(2, '0')}h</span>
          </div>
        )}
      </div>
      {onExpand && (
        <button
          onClick={onExpand}
          title="Expand chart"
          className={`absolute right-3 top-3 z-10 flex h-6 w-6 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      )}
      <div className="relative min-h-0 flex-1 px-4 pb-1">
        {total === 0 ? (
          <div className={`flex h-full items-center justify-center text-center font-mono text-[11px] ${muted}`}>
            No dispatched alerts in range
          </div>
        ) : (
          <div
            className="grid h-full"
            style={{
              gridTemplateColumns: '26px repeat(7, minmax(0, 1fr))',
              gridTemplateRows: '16px repeat(24, minmax(0, 1fr))',
              gap: '2px',
            }}
          >
            <div />
            {HEATMAP_WEEKDAY_LABELS.map((label) => (
              <div key={label} className={`self-end text-center font-mono text-[9px] uppercase tracking-wide ${muted}`}>{label}</div>
            ))}
            {Array.from({ length: 24 }, (_, hour) => (
              <React.Fragment key={hour}>
                <div className={`flex items-center justify-end pr-1 font-mono text-[8px] tabular-nums ${muted}`}>
                  {String(hour).padStart(2, '0')}
                </div>
                {HEATMAP_WEEKDAY_LABELS.map((_, day) => {
                  const count = byDayHour.get(`${day}-${hour}`) ?? 0
                  // Real color always computed (heatmapCellColor(count, max)
                  // below feeds the tooltip/aria-label regardless), but the
                  // fill itself only renders when showColors is on — hovering
                  // still reports the true count either way, this toggle is
                  // purely "hide the color fill", not "hide the data".
                  const color = showColors ? heatmapCellColor(count, max) : null
                  const isHovered = hover?.day === day && hover?.hour === hour
                  return (
                    <div
                      key={day}
                      role="gridcell"
                      tabIndex={0}
                      aria-label={`${HEATMAP_WEEKDAY_LABELS_FULL[day]} ${String(hour).padStart(2, '0')}:00 — ${count} dispatched alert${count === 1 ? '' : 's'}`}
                      onMouseMove={(e) => setHover({ day, hour, count, x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => clearHover(day, hour)}
                      onFocus={(e) => {
                        const r = e.currentTarget.getBoundingClientRect()
                        setHover({ day, hour, count, x: r.right, y: r.top })
                      }}
                      onBlur={() => clearHover(day, hour)}
                      className={`rounded-[2px] border outline-none transition-transform ${gridBorder} ${color ? '' : emptyCell} ${isHovered ? 'z-10 scale-110 ring-1 ring-orange-400' : ''}`}
                      style={color ? { background: color } : undefined}
                    />
                  )
                })}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      {total > 0 && (
        <div className={`flex shrink-0 items-center gap-2 border-t px-4 py-2 font-mono text-[9px] border-border ${muted}`}>
          <button
            type="button"
            onClick={() => setShowColors((v) => !v)}
            title={showColors ? 'Hide cell colors' : 'Show cell colors'}
            className={`flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 uppercase tracking-widest transition-colors border-border hover:border-orange-500 hover:text-orange-600 ${!showColors ? ('border-orange-500 text-orange-600') : ''}`}
          >
            {showColors ? <Eye className="h-2.5 w-2.5" /> : <EyeOff className="h-2.5 w-2.5" />}
            Colors
          </button>
          <span>Low</span>
          <span className="flex h-2 flex-1 overflow-hidden rounded-full">
            {HEATMAP_RAMP.map((c, i) => (
              <span key={i} className="h-full flex-1" style={{ background: c }} />
            ))}
          </span>
          <span>High</span>
          <span className={`ml-2 ${text}`}>max {max}/hr</span>
        </div>
      )}
      {hover && (
        <div
          className={`pointer-events-none fixed z-50 rounded border px-3 py-2 text-[11px] font-mono shadow-notice ${tooltipCls}`}
          style={{ left: hover.x + 14, top: hover.y + 14 }}
        >
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: heatmapCellColor(hover.count, max) ?? ('var(--border)') }} />
            <span>{HEATMAP_WEEKDAY_LABELS_FULL[hover.day]} {String(hover.hour).padStart(2, '0')}:00</span>
            <span className={`ml-auto pl-4 font-semibold ${text}`}>{hover.count}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// Resizable full-screen modal for FraudHeatmapCard — same shell (fixed
// backdrop, drag-resize via native CSS `resize: both`, Escape-to-close, the
// bottom-right resize-handle affordance) as CampaignsView's own
// FraudWaveMaximizedWindow above, just wrapping this card instead of
// FraudWaveChart. No ResizeObserver/animationNonce here — unlike the
// Recharts-based FraudWaveChart, this is a plain CSS grid, so it reflows for
// free as the modal is resized without needing to be told to re-animate.
function FraudHeatmapMaximizedWindow({
  data,
  isDark,
  muted,
  text,
  onClose,
}: {
  data: WeekdayHourDatum[]
  isDark: boolean
  muted: string
  text: string
  onClose: () => void
}) {
  const border = 'border-border'
  const shellBg = 'bg-secondary'
  const headerBg = 'bg-card'

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-overlay p-4 backdrop-blur-sm">
      <div
        className={`relative flex flex-col overflow-hidden border shadow-2xl ${border} ${shellBg}`}
        style={{
          width: 'min(1180px, 92vw)',
          height: 'min(760px, 84vh)',
          minWidth: 520,
          minHeight: 360,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 32px)',
          resize: 'both',
        }}
      >
        <div className={`flex shrink-0 items-center justify-between border-b px-3 py-2 ${border} ${headerBg}`}>
          <div>
            <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Expanded chart</div>
            <div className={`text-sm font-semibold ${text}`}>Fraud by weekday &amp; hour</div>
          </div>
          <button
            onClick={onClose}
            title="Close expanded chart"
            className={`flex h-7 w-7 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 p-3">
          <FraudHeatmapCard data={data} isDark={isDark} muted={muted} text={text} className="h-full" />
        </div>
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute bottom-1 right-1 h-5 w-5 border-b-2 border-r-2 border-border`}
        />
      </div>
    </div>
  )
}

// Shared between the compact "Emails sent" card and its maximized modal so
// the two never drift out of sync with each other's columns.
const isNotFraudYet = (e: SibsEntry) => e.is_scam !== true

function EmailsSentTable({
  entries,
  dispatchTimeById,
  isDark,
  onSelect,
  checkedIds,
  onToggleCheck,
  onToggleAll,
  query,
  onQueryChange,
  appliedQuery,
  searching,
  platformFilter,
  onPlatformFilterChange,
  catalog,
  sourceTypeOptions,
  sourceTypeFilter,
  onSourceTypeFilterChange,
  lastSelectedId,
  onRefresh,
  refreshing,
  lastUpdated,
  storageKey,
  stickyOffset,
}: {
  entries: SibsEntry[]
  dispatchTimeById: Map<string, number>
  isDark: boolean
  onSelect: (e: SibsEntry) => void
  checkedIds: Set<string>
  onToggleCheck: (id: string) => void
  onToggleAll: () => void
  /** Same server-side search CampaignsView's own entries table has — see AlertsAnalysisView's emailQuery/fetchAlertedEmails. */
  query: string
  onQueryChange: (q: string) => void
  /** The query actually in effect server-side (set once its fetch lands, same as CampaignsView's appliedQuery) — used for Highlight instead of the live-typing `query`, so mid-debounce keystrokes don't highlight text against results that haven't been searched for it yet. */
  appliedQuery: string
  /** True while a query-driven fetch is in flight, to show "Searching…" the same way CampaignsView's toolbar does. */
  searching: boolean
  /** Same server-side platform filter CampaignsView's own entries table has — see AlertsAnalysisView's platformFilter/fetchAlertedEmails. */
  platformFilter: string[]
  onPlatformFilterChange: (v: string[]) => void
  catalog: Catalog
  /** Same per-column Source_Type filter (raw_entry / processed_entry / …) CampaignsView's own entries table has — client-side over `entries`, see AlertsAnalysisView's emailSourceTypeFilter/filteredEmailEntries. */
  sourceTypeOptions: string[]
  sourceTypeFilter: string[]
  onSourceTypeFilterChange: (values: string[]) => void
  /** The entry last opened, kept highlighted (same "you were just here" marker as CampaignsView's own entries table) after going back from the detail view. */
  lastSelectedId?: string | null
  /** Forwarded straight to ResizableTable's own "..." menu — see its doc comment. Omit to hide that menu, same as any other ResizableTable caller. */
  onRefresh?: () => void
  refreshing?: boolean
  lastUpdated?: number | null
  storageKey?: string
  /** Forwarded to ResizableTable — height of the compact card's own sticky
   * title bar (Mail icon/"Emails sent"/count/Classify/expand), so this
   * table's own sticky search bar + column headers stack directly below it
   * instead of underneath it. The expanded modal never passes this: its own
   * title bar sits in normal flow above a real `overflow-auto` scroll area,
   * so it's already always visible without needing to stack via offset. */
  stickyOffset?: number
}) {
  // Alerted (an email went out) but not classified as fraud yet: is_scam
  // still empty or false. Tinted rows + a legend chip that doubles as a
  // filter; a bulk classify patches is_scam, so the tint clears in place.
  const [notFraudOnly, setNotFraudOnly] = useState(false)
  const notFraudCount = entries.filter(isNotFraudYet).length
  const shown = notFraudOnly ? entries.filter(isNotFraudYet) : entries
  const muted = 'text-subtle'
  return (
    <ResizableTable
      // maximizable=false — this table already only ever appears inside
      // AlertsAnalysisView's own compact-card/expanded-modal pair
      // (tableExpanded), which is that same maximize affordance one level up.
      // ResizableTable's own default-on button nested inside gave the table
      // two stacked "maximize" controls that did almost the same thing.
      maximizable={false}
      columns={[
        { key: 'checkbox', initialWidth: CHECKBOX_COL_WIDTH, thClassName: CHECKBOX_COL_TH_CLASS, stickyLeft: true, label: (
          <TableCheckboxHeaderLabel
            checked={entries.some(canBulkClassify) && checkedIds.size === entries.filter(canBulkClassify).length}
            onToggle={onToggleAll}
            title="Select all raw entries"
          />
        ) },
        { key: 'user_entry_id', label: 'User_Entry_ID', initialWidth: 180 },
        { key: 'created_at', label: 'Created_At', initialWidth: 130 },
        { key: 'sent_at', label: 'Sent_At', initialWidth: 130, thTitle: 'Confirmed email dispatch timestamp' },
        { key: 'delta', label: 'Detection → Sent', initialWidth: 120, thTitle: 'Time between the entry’s created_at and the confirmed dispatch time' },
        { key: 'entry_type', label: 'Type', initialWidth: 150 },
        // Same field set as sibsColumns (CampaignsView's own entries table)
        // from here down — post_type through id — so an entry looks the same
        // whether you're looking at it from Custom Mail Alert's table or
        // here. sent_at/delta above stay alerts-analysis-only (dispatch
        // timing isn't a sibsColumns concept), and email_sent is skipped —
        // every row here is already dispatched by definition.
        { key: 'post_type', label: 'Post_Type', initialWidth: 100 },
        { key: 'source_type', label: 'Source_Type', initialWidth: 120,
          filterOptions: sourceTypeOptions, activeFilters: sourceTypeFilter, onFilterChange: onSourceTypeFilterChange },
        { key: 'source', label: 'Source', initialWidth: 140 },
        { key: 'sender', label: 'Sender', initialWidth: 130 },
        { key: 'message_content', label: 'Scam_Content' },
        { key: 'entity_reference_entity', label: 'Org_Entity', initialWidth: 130 },
        { key: 'entity_reference_reference', label: 'Reference', initialWidth: 140 },
        { key: 'beneficiario', label: 'Beneficiário', initialWidth: 160 },
        { key: 'platform_namelong', label: 'Platform', initialWidth: 150 },
        { key: 'message_type_text', label: 'Message_Type', initialWidth: 140 },
        { key: 'platform_text', label: 'Platform_Text', initialWidth: 150 },
        // Server-side, same as Source_Type above — see fetchAlertedEmails'
        // platform_ids/platform_unassigned query params.
        { key: 'platform_id', label: 'Platform_ID', initialWidth: 90,
          filterOptions: platformFilterOptions(catalog), activeFilters: platformFilter, onFilterChange: onPlatformFilterChange,
          filterOptionLabel: platformFilterOptionLabel(catalog) },
        { key: 'scam_claims', label: 'Claims', initialWidth: 70 },
        { key: 'raw_free_input', label: 'Raw_Free_Input', initialWidth: 180 },
        { key: 'id', label: 'ID', initialWidth: 120 },
      ]}
      stickyHeader
      stickyOffset={stickyOffset}
      theadClassName={'bg-card border-b border-border'}
      fixedLayout
      clipCells
      onRefresh={onRefresh}
      refreshing={refreshing}
      lastUpdated={lastUpdated}
      storageKey={storageKey}
      theme={isDark ? 'dark' : 'light'}
      toolbar={
        <>
          {searching ? (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-signal" />
          ) : (
            <Search className={`h-3.5 w-3.5 shrink-0 ${muted}`} />
          )}
          <Input
            placeholder="Filter by message, sender, entity…"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            className="h-7 border-0 shadow-none bg-transparent focus-visible:ring-0 px-0 text-[13px]"
          />
          {searching && (
            <span className="font-mono text-[10px] uppercase tracking-widest text-signal shrink-0">
              {query.trim() ? 'Searching…' : 'Loading…'}
            </span>
          )}
          <button
            type="button"
            onClick={() => setNotFraudOnly((v) => !v)}
            title="Alerted (an email was sent) but not classified as fraud yet — is_scam is empty or false. Click to show only these."
            className={`ml-auto flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors ${
              notFraudOnly ? 'border-amber-500 bg-amber-500/15 text-amber-600' : `${isDark ? 'border-slate-700' : 'border-slate-200'} ${muted} hover:border-amber-500 hover:text-amber-600`
            }`}
          >
            <span className="h-2.5 w-2.5 rounded-sm border border-amber-500 bg-amber-500/30" />
            {notFraudCount} not fraud yet
          </button>
        </>
      }
    >
      {shown.map((e) => {
        // user_entry_id preferred over id — see tableDispatchTimeById's own
        // doc comment for why (dispatchTimeById here is always that map).
        const dispatchKey = e.user_entry_id ?? e.id
        const dispatchedAt = dispatchKey ? dispatchTimeById.get(dispatchKey) : undefined
        const deltaMinutes = dispatchedAt != null ? (dispatchedAt - new Date(e.created_at).getTime()) / 60000 : null
        const highlighted = lastSelectedId === e.id
        return (
          <tr
            key={e.id}
            onClick={() => rowClickToggleOrOpen(
              checkedIds.size > 0,
              () => { if (canBulkClassify(e)) onToggleCheck(e.id) },
              () => onSelect(e),
            )}
            className={`cursor-pointer border-b transition-colors hover:bg-secondary border-border ${checkedIds.has(e.id) ? 'bg-signal-surface' : highlighted ? 'bg-signal-surface' : isNotFraudYet(e) ? 'bg-warning-surface/60' : ''}`}
          >
            <TableCheckboxTd
              checked={checkedIds.has(e.id)}
              onToggle={() => onToggleCheck(e.id)}
              disabled={!canBulkClassify(e)}
              title={canBulkClassify(e) ? undefined : CANNOT_BULK_CLASSIFY_TITLE}
              sticky
            />
            <TableTd className="font-mono truncate text-[10px]">{e.user_entry_id ?? ''}</TableTd>
            <TableTd className="font-mono truncate">{fmtDate(e.created_at)}</TableTd>
            <TableTd className="font-mono truncate">{dispatchedAt != null ? fmtDate(new Date(dispatchedAt).toISOString()) : '—'}</TableTd>
            <TableTd className="font-mono truncate">{deltaMinutes != null && deltaMinutes >= 0 ? formatDurationMinutes(deltaMinutes) : '—'}</TableTd>
            <TableTd className="font-mono truncate">{e.entry_type ?? ''}</TableTd>
            <TableTd className="font-mono truncate">{e.post_type ?? ''}</TableTd>
            <TableTd className="font-mono truncate">{e.source_type ?? ''}</TableTd>
            <TableTd className="font-mono truncate">{e.source ?? ''}</TableTd>
            <TableTd className="font-mono truncate"><Highlight text={e.sender} query={appliedQuery} /></TableTd>
            <TableTd><span className="line-clamp-1"><Highlight text={e.message_content} query={appliedQuery} /></span></TableTd>
            <TableTd className="font-mono truncate"><Highlight text={e.entity_reference_entity} query={appliedQuery} /></TableTd>
            <TableTd className="font-mono truncate"><Highlight text={e.entity_reference_reference} query={appliedQuery} /></TableTd>
            <TableTd className="font-mono truncate"><Highlight text={e.beneficiario} query={appliedQuery} /></TableTd>
            <TableTd className="font-mono truncate"><Highlight text={e.platform_namelong} query={appliedQuery} /></TableTd>
            <TableTd className="font-mono truncate">{e.message_type_text ?? ''}</TableTd>
            <TableTd className="font-mono truncate"><Highlight text={e.platform_text} query={appliedQuery} /></TableTd>
            <TableTd className="font-mono truncate text-center">{e.platform_id ?? ''}</TableTd>
            <TableTd className="font-mono text-center">{claimCount(e.scam_claims)}</TableTd>
            <TableTd><span className="line-clamp-1">{e.raw_free_input ?? ''}</span></TableTd>
            <TableTd className="font-mono truncate text-[10px]">{e.id}</TableTd>
          </tr>
        )
      })}
    </ResizableTable>
  )
}

function emailsSentCsv(entries: SibsEntry[], dispatchTimeById: Map<string, number>): string {
  const headers = [
    'user_entry_id', 'created_at', 'sent_at', 'delta_minutes', 'entry_type',
    'post_type', 'source_type', 'source', 'sender',
    'message_content', 'entity_reference_entity', 'entity_reference_reference', 'beneficiario',
    'platform_namelong', 'message_type_text', 'platform_text', 'platform_id', 'scam_claims', 'raw_free_input', 'id',
  ]
  const escapeCsv = (v: unknown) => {
    const s = String(v ?? '')
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const rows = entries.map((e) => {
    // user_entry_id preferred over id — see tableDispatchTimeById's own doc
    // comment (dispatchTimeById here is always that map).
    const dispatchKey = e.user_entry_id ?? e.id
    const dispatchedAt = dispatchKey ? dispatchTimeById.get(dispatchKey) : undefined
    const deltaMinutes = dispatchedAt != null ? Math.round((dispatchedAt - new Date(e.created_at).getTime()) / 60000) : ''
    return [
      e.user_entry_id ?? '',
      e.created_at ?? '',
      dispatchedAt != null ? new Date(dispatchedAt).toISOString() : '',
      deltaMinutes,
      e.entry_type ?? '',
      e.post_type ?? '',
      e.source_type ?? '',
      e.source ?? '',
      e.sender ?? '',
      e.message_content ?? '',
      e.entity_reference_entity ?? '',
      e.entity_reference_reference ?? '',
      e.beneficiario ?? '',
      e.platform_namelong ?? '',
      e.message_type_text ?? '',
      e.platform_text ?? '',
      e.platform_id ?? '',
      claimCount(e.scam_claims),
      e.raw_free_input ?? '',
      e.id,
    ].map(escapeCsv).join(',')
  })
  return [headers.join(','), ...rows].join('\n')
}

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function AlertsAnalysisView({ theme = 'dark' }: { theme?: ProcessedTheme }) {
  // Its sticky bars pin under the shell's topbar (see stickyStack.ts).
  const stackTop = useStickyStackTop()
  const isDark = theme === 'dark'
  const muted = 'text-subtle'
  const text = 'text-foreground'
  const border = 'border-border'

  // The shared dashboard range (dashboardRange.ts): last 30 days unless this
  // dashboard's cookie remembers another choice (a pilot month included).
  const { from: chartFrom, to: chartTo, setRange: setChartRange } = useDashboardRange('sibs-alerts')
  // Top-of-dashboard platform filter — isolates the chart-derived widgets
  // (Fraud Wave, the weekday/hour heatmap, campaign-type/platform pies,
  // takedown-time histogram) to one or more platforms for the selected
  // period. Client-side over chartSource (see matchesPlatformFilter) rather
  // than a new server param: chart mode already returns platform_id per row,
  // and the KPI cards above (spark/latency/beneficiary ranking) stay
  // period-wide since those come from separate dashboard RPCs that don't
  // accept a platform filter today — see the header note that appears
  // alongside this control when it's active. It also scopes the email alerts
  // table, through that table's own platform column filter (see the effect
  // next to emailPlatformFilter).
  const [platformFilter, setPlatformFilter] = useState<string[]>([])

  const [chartEntries, setChartEntries] = useState<SibsChartEntry[]>([])
  const [dispatchLogs, setDispatchLogs] = useState<SibsDispatchLog[]>([])
  const [dashboardAggregate, setDashboardAggregate] = useState<SibsDashboardAggregate | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryTick, setRetryTick] = useState(0)
  const [unmitigatedActiveDays, setUnmitigatedActiveDays] = useState(5)
  // Same "float a Maximize2 button over the card, expand into a resizable
  // modal on click" pattern as CampaignsView's own fraudWaveExpanded — see
  // FraudHeatmapMaximizedWindow below. fraudWaveExpanded reuses that same
  // FraudWaveMaximizedWindow component CampaignsView already has, just fed
  // this view's own fraudWaveData/dispatchLogs instead.
  const [heatmapExpanded, setHeatmapExpanded] = useState(false)
  const [fraudWaveExpanded, setFraudWaveExpanded] = useState(false)

  // Widgetized like the Custom Mail Alert dashboard's own Fraud Wave chart
  // and entries table — opt-in behind a button on first open instead of
  // eagerly firing every fetch on mount, same "Show logs" / "Load entries"
  // pattern as CampaignsView above.
  const [logsRequested, setLogsRequested] = useState(false)
  const [entriesRequested, setEntriesRequested] = useState(false)
  const [payloadRequested, setPayloadRequested] = useState(false)

  // Row-click detail view — same EntryDetailShell + Sibs*Widget set
  // CampaignsView's own table uses, since these are the same SibsEntry rows.
  const [selected, setSelected] = useState<SibsEntry | null>(null)
  // Same "you were just here" pattern as CampaignsView's own entries table
  // (lastSelectedId/listScrollTopRef/restoreListScrollRef): the entry last
  // opened stays highlighted after going back, and the list's scroll
  // position — captured from this view's own root rather than from
  // whichever table the click came from, so it's correct whether the click
  // originated in the compact card or the expanded-table modal (that
  // modal's own overflow-auto div is a different scroll ancestor than the
  // page-level one this restores) — is reapplied once the list remounts.
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null)
  const listRootRef = useRef<HTMLDivElement | null>(null)
  const listScrollTopRef = useRef(0)
  const restoreListScrollRef = useCallback((el: HTMLDivElement | null) => {
    listRootRef.current = el
    if (!el) return
    el.style.visibility = 'hidden'
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const sp = getScrollParent(el)
        if (sp) sp.scrollTop = listScrollTopRef.current
        el.style.visibility = 'visible'
      })
    })
  }, [])
  const handleSelectEntry = useCallback((e: SibsEntry) => {
    const sp = getScrollParent(listRootRef.current)
    if (sp) {
      listScrollTopRef.current = sp.scrollTop
      sp.scrollTop = 0
    }
    setLastSelectedId(e.id)
    setSelected(e)
  }, [])
  const [sentIds, setSentIds] = useState<Set<string>>(new Set())
  const screenshotTurnstileRef = useRef<TurnstileHandle>(null)
  const pendingTokenRef = useRef<((token: string) => void) | null>(null)
  const handleScreenshotVerify = useCallback((token: string) => {
    pendingTokenRef.current?.(token)
    pendingTokenRef.current = null
  }, [])
  const getScreenshotToken = useCallback((): Promise<string> =>
    new Promise((resolve) => {
      pendingTokenRef.current = resolve
      const tryRun = (attempt = 0) => {
        if (screenshotTurnstileRef.current) {
          screenshotTurnstileRef.current.reset()
          screenshotTurnstileRef.current.run()
          return
        }
        if (attempt < 20) setTimeout(() => tryRun(attempt + 1), 100)
      }
      tryRun()
    }), [])

  // Alerted-emails table — same paginated /api/campaigns/sibs
  // entries endpoint (and PAGE_SIZE) the Custom Mail Alert table itself
  // uses, not a new endpoint. There's no server-side "email_sent only"
  // filter, so each page is fetched in full and filtered to alerted rows
  // client-side — loaded fractionally (one PAGE_SIZE page per "Load more",
  // same as the main table's infinite scroll) rather than all at once.
  const [emailEntries, setEmailEntries] = useState<SibsEntry[]>([])
  const [emailOffset, setEmailOffset] = useState(0)
  const [emailScanned, setEmailScanned] = useState(0)
  const [emailHasMore, setEmailHasMore] = useState(true)
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailLoadedOnce, setEmailLoadedOnce] = useState(false)
  // Drives the table's own "..." refresh menu — see ResizableTable's
  // onRefresh/lastUpdated props, same "updated Xm ago" readout CampaignsView's
  // own entries table already has via lastLoadedAt.
  const [emailLastUpdated, setEmailLastUpdated] = useState<number | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [tableExpanded, setTableExpanded] = useState(false)
  const [emailQuery, setEmailQuery] = useState('')
  const [appliedEmailQuery, setAppliedEmailQuery] = useState('')
  const [emailPlatformFilter, setEmailPlatformFilter] = useState<string[]>([])
  // The header platform picker scopes the email alerts table too, through the
  // table's own column filter (so the column header shows it and the fetch
  // sends it server-side as platform_ids). The column filter can still narrow
  // it afterwards; the next header change resets it to the header's choice.
  useEffect(() => { setEmailPlatformFilter(platformFilter) }, [platformFilter])
  // Same per-column Source_Type filter (raw_entry / processed_entry /
  // brand_campaign / sCalls) CampaignsView's own entries table already has —
  // client-side over whatever's currently loaded in emailEntries, same as
  // that one, rather than pushed server-side like platformFilter: unlike
  // platform_id, get_sibs_fraud_entries has no p_source_type param, and this
  // table's own scan-until-MIN_ALERTED_PER_LOAD loop already fetches ahead
  // of what's rendered, so filtering the loaded set client-side (with "Load
  // more" still available to pull in more if a narrow filter empties the
  // page) matches CampaignsView's own tradeoff here instead of requiring a
  // new RPC param for this one field.
  const [emailSourceTypeFilter, setEmailSourceTypeFilter] = useState<string[]>([])

  // Measured so the compact "Emails sent" card's own sticky title bar
  // (Mail icon/label/count/Classify/expand) and EmailsSentTable's own
  // sticky search bar + column headers stack directly below each other
  // instead of overlapping — same pattern as CasesDashboardView's
  // stickyBarRef. Only the compact card needs this: the expanded modal's
  // title bar already sits in normal flow above a real overflow-auto area.
  const emailsCardTitleRef = useRef<HTMLDivElement>(null)
  const [emailsCardTitleHeight, setEmailsCardTitleHeight] = useState(0)
  useEffect(() => {
    const el = emailsCardTitleRef.current
    if (!el) { setEmailsCardTitleHeight(0); return }
    const ro = new ResizeObserver(() => setEmailsCardTitleHeight(el.offsetHeight))
    ro.observe(el)
    setEmailsCardTitleHeight(el.offsetHeight)
    return () => ro.disconnect()
  }, [])
  const [checkedEmailIds, setCheckedEmailIds] = useState<Set<string>>(new Set())
  const { catalog } = useCatalog()
  const [bulkClassifyOpen, setBulkClassifyOpen] = useState(false)
  const [bulkClassifySubmitting, setBulkClassifySubmitting] = useState(false)
  const [bulkClassifyResult, setBulkClassifyResult] = useState<BulkClassifyResult | null>(null)

  // Alerted entries are a sparse subset of the raw feed (e.g. ~10% in
  // practice) and there's no server-side "email_sent only" filter, so a
  // single PAGE_SIZE page can easily contain zero of them — the most recent
  // 30 entries overall often do. A literal one-page-per-click "Load more"
  // reads as broken in that case (nothing ever appears). Instead, each call
  // keeps pulling further pages from the same endpoint — still paginated,
  // never the whole dataset — until it collects a decent batch, exhausts the
  // feed, or hits a safety cap.
  const MIN_ALERTED_PER_LOAD = 10
  const MAX_SCAN_PAGES_PER_LOAD = 15

  // Guards against overlapping calls stomping on each other's results —
  // every other fetch in this file uses an AbortController for the same
  // "latest call wins" guarantee, but this one didn't, and it's a real gap:
  // React 18 Strict Mode double-invokes the mount effect below in dev, so
  // two calls to fetchAlertedEmails(0) actually run concurrently, each
  // awaiting up to 15 sequential requests — whichever happened to finish
  // *last* would silently overwrite the other's state, success or not.
  const emailRequestIdRef = useRef(0)

  // Same scan, but for the "Load all" button in the maximized widget:
  // ignores the MIN_ALERTED_PER_LOAD early-stop and keeps going until the
  // feed is genuinely exhausted (hasMore false) or this generous safety cap
  // is hit — still page-by-page against the same endpoint, never a single
  // unbounded request.
  const MAX_SCAN_PAGES_LOAD_ALL = 1000

  const fetchAlertedEmails = useCallback(async (startOffset: number, loadAll = false) => {
    const requestId = ++emailRequestIdRef.current
    setEmailLoading(true)
    setEmailError(null)
    let offset = startOffset
    let collected: SibsEntry[] = []
    let scannedThisCall = 0
    let more = true
    const maxPages = loadAll ? MAX_SCAN_PAGES_LOAD_ALL : MAX_SCAN_PAGES_PER_LOAD
    const requestQuery = emailQuery.trim()
    // `since`/`until` are real server-side bounds (get_sibs_fraud_entries'
    // p_since_date/p_until_date) — pin the table to the same period as the
    // rest of the dashboard. Before `until` reached the RPC, the scan started
    // at today and paged back, so a past period with many newer rows found
    // nothing in range. `chartTo` is still checked client-side below as a
    // guard.
    const untilMs = new Date(chartTo + 'T23:59:59').getTime()
    // Same server-side ILIKE search get_sibs_fraud_entries already does for
    // CampaignsView's own table (sender/message_content/platform/entity/…,
    // see that RPC's `params` CTE) — pushed into the same scan loop rather
    // than filtered client-side, since only entries actually fetched so far
    // would be searchable otherwise. Alerted rows are already sparse (~10%
    // of the feed); adding a search term makes matches sparser still, so a
    // narrow search can legitimately need "Load more"/"Scan further back" a
    // few times before MIN_ALERTED_PER_LOAD is satisfied within maxPages —
    // same tradeoff CampaignsView's own search already accepts.
    const searchQs = requestQuery ? `&q=${encodeURIComponent(requestQuery)}` : ''
    // Same server-side platform filter as CampaignsView's own table — see
    // get_sibs_fraud_entries' p_platform_ids/p_platform_unassigned.
    const emailPlatformParams = platformFilterToQueryParams(emailPlatformFilter)
    const platformQs = Object.entries(emailPlatformParams)
      .map(([k, v]) => `&${k}=${encodeURIComponent(v)}`)
      .join('')
    // Same server-side push as platform above — see get_sibs_fraud_entries'
    // p_source_types. Was client-side-only at first (filtering whatever
    // emailEntries already had loaded), which meant a narrow filter just
    // emptied the visible table instead of actually finding more matching
    // entries further back in the date range, the same problem search and
    // platform would have if either were client-side.
    const sourceTypesQs = emailSourceTypeFilter.length > 0
      ? `&source_types=${encodeURIComponent(emailSourceTypeFilter.join(','))}`
      : ''
    try {
      for (let page = 0; more && (loadAll || collected.length < MIN_ALERTED_PER_LOAD) && page < maxPages; page++) {
        const r = await fetch(`/api/campaigns/sibs?offset=${offset}&limit=${PAGE_SIZE}&since=${chartFrom}&until=${chartTo}${searchQs}${platformQs}${sourceTypesQs}`)
        const payload = await r.json()
        if (requestId !== emailRequestIdRef.current) return // superseded by a newer call
        if (!payload?.success) {
          setEmailError(payload?.error ?? 'Failed to load entries')
          return
        }
        const rows: SibsEntry[] = payload.data.entries ?? []
        // The server already stops at chartTo; this only guards against a
        // backend without p_until_date.
        collected = [...collected, ...rows.filter((e) => e.email_sent && (parseDbTimestamp(e.created_at)?.getTime() ?? 0) <= untilMs)]
        scannedThisCall += rows.length
        offset += rows.length
        more = payload.data.hasMore ?? false
        if (rows.length === 0) break
      }
      if (requestId !== emailRequestIdRef.current) return // superseded
      setEmailEntries((prev) => (startOffset === 0 ? collected : [...prev, ...collected]))
      setEmailScanned((prev) => (startOffset === 0 ? scannedThisCall : prev + scannedThisCall))
      if (startOffset === 0) setAppliedEmailQuery(requestQuery)
      setEmailHasMore(more)
      setEmailOffset(offset)
      setEmailLastUpdated(Date.now())
    } catch {
      if (requestId === emailRequestIdRef.current) setEmailError('Network error')
    } finally {
      if (requestId === emailRequestIdRef.current) {
        setEmailLoading(false)
        setEmailLoadedOnce(true)
      }
    }
  }, [chartFrom, chartTo, emailQuery, emailPlatformFilter, emailSourceTypeFilter])

  const toggleEmailCheck = (id: string) =>
    setCheckedEmailIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const toggleAllEmailChecks = () => {
    const eligible = emailEntries.filter(canBulkClassify)
    setCheckedEmailIds((prev) =>
      prev.size === eligible.length ? new Set() : new Set(eligible.map((e) => e.id)),
    )
  }

  // Same shape as CampaignsView's own handleBulkClassify — see
  // BulkClassifyDialog's doc comment for why this is a shared, table-
  // agnostic widget rather than a CampaignsView-only one. Patches the
  // classified rows in place (applyBulkClassifyPatch) — a refetch from
  // offset 0 would drop every page loaded past the first.
  const handleBulkClassify = async (form: BulkClassifyForm) => {
    const selected = emailEntries.filter((e) => checkedEmailIds.has(e.id))
    const rawEntryIds = selected.map((e) => e.raw_entry_id ?? e.id)
    // See CampaignsView's own handleBulkClassify for why this reverse
    // lookup exists — un-checking rows that just flipped to
    // source_type: 'processed_entry' on a partial-failure batch.
    const entryIdByRawEntryId = new Map(selected.map((e) => [e.raw_entry_id ?? e.id, e.id]))
    if (rawEntryIds.length === 0) return
    setBulkClassifySubmitting(true)
    setBulkClassifyResult(null)
    const result = await apiPost<{ results: Array<{ rawEntryId: string; success: boolean; error?: unknown }> }>('/bulk-classify', {
      rawEntryIds,
      ...form,
    })
    setBulkClassifySubmitting(false)
    // Gate on results, not result.success: the backend's success is
    // results.every(ok), so one failed row used to discard every row that
    // did get classified (nothing patched, all reported failed).
    if (result.data?.results) {
      const failures = result.data.results.filter((r) => !r.success)
      const succeededIds = new Set(result.data.results.filter((r) => r.success).map((r) => r.rawEntryId))
      // Patch in place rather than refetching — see applyBulkClassifyPatch's
      // own doc comment for why a refetch here used to make just-accepted
      // rows vanish from the table.
      if (succeededIds.size > 0) {
        setEmailEntries((prev) => applyBulkClassifyPatch(prev, succeededIds, form, catalog))
        setCheckedEmailIds((prev) => {
          const next = new Set(prev)
          for (const rawEntryId of succeededIds) {
            const id = entryIdByRawEntryId.get(rawEntryId)
            if (id) next.delete(id)
          }
          return next
        })
      }
      setBulkClassifyResult({ successCount: succeededIds.size, failures })
      if (failures.length === 0) setBulkClassifyOpen(false)
    } else {
      setBulkClassifyResult({
        successCount: 0,
        failures: rawEntryIds.map((rawEntryId) => ({ rawEntryId, error: result.error ?? 'Request failed' })),
      })
    }
  }

  useEffect(() => {
    if (emailEntries.length === 0) return
    setSentIds((prev) => new Set([...prev, ...emailEntries.map((e) => e.id)]))
  }, [emailEntries])

  useEffect(() => {
    if (!entriesRequested) return
    // fetchAlertedEmails' identity changes whenever chartFrom/chartTo/
    // emailQuery do (see its own dependency array), so this effect re-fires
    // on period changes and search edits too — fetchAlertedEmails(0)
    // replaces (not appends) emailEntries, which is exactly the reset-and-
    // reload behavior both need. The dispatch-logs "already covered since"
    // bookkeeping is a ref rather than state (see its own comment), so it
    // doesn't reset itself the same way — done explicitly here instead, or
    // a backward/unrelated period change could wrongly appear "already
    // covered" and skip fetching fresh logs.
    tableLogsSinceRef.current = null
    // Debounced only when there's a query to type — same reasoning and
    // 450ms as CampaignsView's own search (see its matching effect):
    // long enough to coalesce normal typing, short enough not to feel
    // laggy, and it keeps a plain entriesRequested/period-change trigger
    // instant rather than delaying the very first load.
    const timer = setTimeout(() => fetchAlertedEmails(0), emailQuery.trim() ? 450 : 0)
    return () => clearTimeout(timer)
  }, [entriesRequested, fetchAlertedEmails, emailQuery])

  // Dispatch logs for the "Detection → Sent" column — the alerted-emails
  // table isn't bound to chartFrom/chartTo (it pages back through the whole
  // feed, same as the entries endpoint's own default range), so the
  // chart's own dispatchLogs fetch below often doesn't cover these rows.
  // Widens its own `since` to the oldest currently-loaded row instead of
  // fetching the logs table unbounded, and only re-fetches when "Load more"
  // actually reaches further back than what's already covered.
  const [tableDispatchLogs, setTableDispatchLogs] = useState<SibsDispatchLog[]>([])
  // Bookkeeping only — must NOT be state. It used to be, with itself in this
  // same effect's dependency array: calling setTableLogsSince synchronously
  // before the fetch started scheduled a re-render, and since the effect
  // depends on the value it had just changed, React reran it — cleanup from
  // the first run fired *first*, aborting the fetch a moment after it
  // started, silently (AbortError is deliberately swallowed below). The
  // second run then saw "already covered" and skipped re-fetching entirely.
  // Net effect: every fetch got created and immediately self-aborted, and
  // the "fetch result" log below never printed. A ref sidesteps this
  // entirely since writing to it doesn't trigger a re-render or rerun.
  const tableLogsSinceRef = useRef<string | null>(null)

  useEffect(() => {
    const oldest = emailEntries[emailEntries.length - 1]?.created_at
    if (!oldest) return
    const sinceDate = oldest.slice(0, 10)
    if (tableLogsSinceRef.current && sinceDate >= tableLogsSinceRef.current) return
    tableLogsSinceRef.current = sinceDate
    const controller = new AbortController()
    fetch(`/api/campaigns/logs?since=${sinceDate}`, { signal: controller.signal })
      .then((r) => r.json())
      .then((payload) => {
        if (payload?.success && Array.isArray(payload.data)) setTableDispatchLogs(payload.data)
      })
      .catch(() => { /* non-fatal — delta column just falls back to "—" */ })
    return () => controller.abort()
  }, [emailEntries])

  const tableDispatchTimeById = useMemo(() => {
    const map = new Map<string, number>()
    for (const log of tableDispatchLogs) {
      // user_entry_id preferred over entry_id — see SibsDispatchLog's own
      // doc comment. Consumers (EmailsSentTable, emailsSentCsv) look these
      // rows up the same way (e.user_entry_id ?? e.id), so both sides of
      // the join agree on which identity to use.
      const key = log.user_entry_id ?? log.entry_id
      if (!key || log.status !== 'sent') continue
      const parsed = parseDbTimestamp(log.created_at)
      if (!parsed) continue
      const t = parsed.getTime()
      const existing = map.get(key)
      if (existing == null || t < existing) map.set(key, t)
    }
    return map
  }, [tableDispatchLogs])



  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    Promise.all([
      fetch(`/api/campaigns/sibs?chart=1&since=${chartFrom}&until=${chartTo}`, { signal: controller.signal }).then((r) => r.json()),
      fetch(`/api/campaigns/sibs?dashboard=1&since=${chartFrom}&until=${chartTo}`, { signal: controller.signal }).then((r) => r.json()),
    ])
      .then(([chartPayload, dashboardPayload]) => {
        let firstError: string | null = null
        if (chartPayload?.success && Array.isArray(chartPayload.data)) setChartEntries(chartPayload.data)
        else firstError = chartPayload?.error ?? 'Failed to load chart data'

        if (dashboardPayload?.success) setDashboardAggregate(dashboardPayload.data)
        else firstError = firstError ?? (dashboardPayload?.error ?? 'Failed to load dashboard RPCs')

        setError(firstError)
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') setError('Network error loading alerts analysis')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [chartFrom, chartTo, retryTick])

  // Used to be gated behind logsRequested ("Show logs") on the theory that
  // dispatchLogs was purely supplementary to the always-on KPI cards — true
  // when those cards' numbers came straight from dashboardAggregate. It
  // stopped being true once the median/mean-to-protection cards and the
  // latency-buckets chart got their own always-visible local calc (see
  // protectionDurations/dispatchTimeById and summary's platformFilter
  // branch above): those need dispatchLogs to ever be filled in, and they
  // render unconditionally, not behind "Show logs" — so with the old gate,
  // selecting a platform (or just opening the page) left them empty until
  // the user separately clicked "Show logs" for the Fraud Wave chart, which
  // has nothing to do with those cards. Fetching eagerly here instead;
  // logsRequested still gates the Fraud Wave/histogram *reveal* below (see
  // their own "Show logs" placeholders), it just no longer gates whether
  // this data exists at all.
  useEffect(() => {
    const controller = new AbortController()
    fetch(`/api/campaigns/logs?since=${chartFrom}&until=${chartTo}`, { signal: controller.signal })
      .then((r) => r.json())
      .then((payload) => {
        if (payload?.success && Array.isArray(payload.data)) setDispatchLogs(payload.data)
      })
      .catch(() => { /* non-fatal — dependent cards just render with what they have */ })
    return () => controller.abort()
  }, [chartFrom, chartTo, retryTick])

  // Screenshot-arrangement mode: lets an editor drag the row-1/2/3 widgets
  // (everything except the pinned beneficiary sidebar, which stays fixed at
  // the right) into any order. Each widget keeps its own authored size
  // (WIDGET_SIZE_CLASS) — dragging only changes widgetOrder, and CSS grid's
  // dense auto-flow repacks widgets around that order and around the pinned
  // sidebar, so nothing is ever resized or overlapped.
  const [arranging, setArranging] = useState(false)
  const [widgetOrder, setWidgetOrder] = useState<ArrangeKey[]>(DEFAULT_WIDGET_ORDER)
  const [dragKey, setDragKey] = useState<ArrangeKey | null>(null)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(WIDGET_ORDER_STORAGE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length === DEFAULT_WIDGET_ORDER.length && DEFAULT_WIDGET_ORDER.every((k) => parsed.includes(k))) {
        setWidgetOrder(parsed)
      }
    } catch { /* corrupt/stale storage — keep default order */ }
  }, [])

  useEffect(() => {
    localStorage.setItem(WIDGET_ORDER_STORAGE_KEY, JSON.stringify(widgetOrder))
  }, [widgetOrder])

  const handleWidgetDragStart = useCallback((key: ArrangeKey) => (e: React.DragEvent) => {
    setDragKey(key)
    e.dataTransfer.effectAllowed = 'move'
  }, [])
  const handleWidgetDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])
  const handleWidgetDrop = useCallback((targetKey: ArrangeKey) => (e: React.DragEvent) => {
    e.preventDefault()
    if (dragKey && dragKey !== targetKey) {
      setWidgetOrder((prev) => {
        const next = prev.filter((k) => k !== dragKey)
        next.splice(next.indexOf(targetKey), 0, dragKey)
        return next
      })
    }
    setDragKey(null)
  }, [dragKey])

  const chartSource = useMemo<SibsChartEntry[]>(() => {
    const from = new Date(chartFrom + 'T00:00:00').getTime()
    const to = new Date(chartTo + 'T23:59:59').getTime()
    return chartEntries.filter((e) => {
      const t = new Date(e.created_at).getTime()
      return t >= from && t <= to && matchesPlatformFilter(e.platform_id, platformFilter)
    })
  }, [chartEntries, chartFrom, chartTo, platformFilter])

  // Campaign-type pie — derived from chartSource (the chart's own full-period
  // fetch) rather than a dedicated ?breakdown=1 request: that endpoint ran
  // the exact same batched RPC scan (get_sibs_fraud_chart_data, same
  // since/until) a second time just to sum server-side what chartSource
  // already has client-side, doubling one of this view's heaviest queries on
  // every load and every period change for no new data. Mirrors the removed
  // endpoint's own aggregation exactly: only dispatched (fraudulent) entries
  // count as a "campaign", keyed by entry_type with a null/other type
  // grouped under "REPORT" (same fallback label used elsewhere, e.g.
  // SibsWidgets.tsx) rather than mislabeled as Multibanco.
  const campaignTypeBreakdown = useMemo<CampaignTypeCount[]>(() => {
    const counts = new Map<string, number>()
    for (const e of chartSource) {
      if (!e.email_sent) continue
      const key = e.entry_type ?? 'REPORT'
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return Array.from(counts.entries()).map(([type, count]) => ({ type, count }))
  }, [chartSource])

  const platformBreakdown = useMemo(() => buildPlatformBreakdown(chartSource, isDark), [chartSource, isDark])

  const weekdayHourData = useMemo(() => buildWeekdayHourHeatmap(chartSource), [chartSource])

  // dispatchLogs itself carries no platform_id (it's the raw email_logs
  // table, not joined against a platform anywhere server-side) — fed
  // straight into buildFraudWaveData below, its own `takenDown` counter
  // would count every dispatch in the period across every platform, even
  // once fraudCases (from chartSource, already platform-filtered) was
  // scoped to just one. That's what made the wave chart ignore the platform
  // filter: the numerator moved, the "taken down" side didn't. Narrowing to
  // logs whose entry_id shows up among chartSource's own ids fixes that —
  // left alone (not filtered) when no platform filter is active, so the
  // unfiltered chart's behavior doesn't change at all.
  const platformScopedDispatchLogs = useMemo(() => {
    if (platformFilter.length === 0) return dispatchLogs
    const idsInScope = new Set(chartSource.map((e) => e.id).filter((id): id is string => !!id))
    return dispatchLogs.filter((log) => !!log.entry_id && idsInScope.has(log.entry_id))
  }, [dispatchLogs, chartSource, platformFilter])

  const fraudWaveData = useMemo(() => {
    const from = new Date(chartFrom + 'T00:00:00')
    const to = new Date(chartTo + 'T23:59:59')
    const days = Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000) + 1)
    return buildFraudWaveData(chartSource, platformScopedDispatchLogs, from, days, unmitigatedActiveDays)
  }, [chartSource, platformScopedDispatchLogs, chartFrom, chartTo, unmitigatedActiveDays])

  // Same join CampaignsView's protectionDurations uses — earliest confirmed
  // dispatch time per entry, then per-entry detection→dispatch minutes.
  // Still entry_id-keyed, not user_entry_id-preferred — see that other
  // dispatchTimeById's own doc comment for why (chartSource rows don't
  // carry user_entry_id).
  const dispatchTimeById = useMemo(() => {
    const map = new Map<string, number>()
    for (const log of dispatchLogs) {
      if (!log.entry_id || log.status !== 'sent') continue
      const parsed = parseDbTimestamp(log.created_at)
      if (!parsed) continue
      const t = parsed.getTime()
      const existing = map.get(log.entry_id)
      if (existing == null || t < existing) map.set(log.entry_id, t)
    }
    return map
  }, [dispatchLogs])

  const protectionDurations = useMemo(() => {
    const minutes: number[] = []
    for (const e of chartSource) {
      if (!e.email_sent) continue
      const dispatchedAt = e.id ? dispatchTimeById.get(e.id) : undefined
      if (dispatchedAt == null) continue
      const diffMin = (dispatchedAt - new Date(e.created_at).getTime()) / 60000
      if (diffMin < 0) continue
      minutes.push(diffMin)
    }
    return minutes.sort((a, b) => a - b)
  }, [chartSource, dispatchTimeById])

  const medianProtectionMinutesLocal = useMemo(() => {
    if (protectionDurations.length === 0) return null
    const mid = Math.floor(protectionDurations.length / 2)
    return protectionDurations.length % 2 === 1
      ? protectionDurations[mid]
      : (protectionDurations[mid - 1] + protectionDurations[mid]) / 2
  }, [protectionDurations])

  // No server equivalent (get_sibs_median_time_to_protection only returns a
  // median), so unlike medianProtectionMinutes above this is always the
  // local calc — no dashboardAggregate value to prefer, and therefore no
  // separate platformFilter-aware branch needed either: protectionDurations
  // is already chartSource-derived, so this is always correctly scoped.
  const meanProtectionMinutesLocal = useMemo(() => {
    if (protectionDurations.length === 0) return null
    return protectionDurations.reduce((sum, m) => sum + m, 0) / protectionDurations.length
  }, [protectionDurations])

  // Fraud-scoped summary — same derivation as CampaignsView's non-replay
  // aggregatePreview branch, minus everything "Legitimate".
  const summary = useMemo(() => {
    // Platform filter active: none of the 7 dashboardAggregate RPCs accept a
    // platform param, so every number they return is period-wide regardless
    // of platformFilter — silently showing those next to platform-scoped
    // chart widgets would misrepresent the data, not just look inconsistent.
    // Rebuilt entirely from chartSource instead, same "recompute the
    // dashboard-RPC shape client-side" technique CampaignsView's own
    // aggregatePreview already uses for replay mode — chartSource already
    // carries every entry (fraud and not) for the period, filtered to this
    // platform, so every field below stays correctly scoped.
    if (platformFilter.length > 0) {
      const total = chartSource.length
      const fraudulent = chartSource.filter((e) => e.email_sent).length
      const fraudRatio = total > 0 ? Math.round((fraudulent / total) * 100) : 0

      // Same day-key format (D/M, local time) as buildFraudWaveData/
      // buildSibsChartData above, so this lines up with every other locally
      // bucketed chart in this file — pre-seeded with every day in range
      // (not just days with data) so a quiet day still shows as a real zero.
      const dayMs = 86400000
      const origin = new Date(chartFrom + 'T00:00:00')
      origin.setHours(0, 0, 0, 0)
      const days = Math.max(1, Math.round((new Date(chartTo + 'T23:59:59').getTime() - origin.getTime()) / dayMs) + 1)
      const dailyMap = new Map<string, number>()
      for (let i = 0; i < days; i++) {
        const d = new Date(origin.getTime() + i * dayMs)
        dailyMap.set(`${d.getDate()}/${d.getMonth() + 1}`, 0)
      }
      for (const e of chartSource) {
        if (!e.email_sent) continue
        const d = parseDbTimestamp(e.created_at)
        if (!d) continue
        const key = `${d.getDate()}/${d.getMonth() + 1}`
        if (dailyMap.has(key)) dailyMap.set(key, (dailyMap.get(key) ?? 0) + 1)
      }
      const daily = Array.from(dailyMap.entries()).map(([date, count]) => ({ date, fraudulent: count }))

      const fraudulentUniqueBeneficiaries = new Set(
        chartSource.filter((e) => e.email_sent && e.beneficiario).map((e) => e.beneficiario!.toLowerCase()),
      ).size

      const latencyCounts = new Map(LATENCY_BUCKET_ORDER.map((k) => [k, 0]))
      for (const min of protectionDurations) {
        const key = latencyBucketForMinutes(min)
        latencyCounts.set(key, (latencyCounts.get(key) ?? 0) + 1)
      }

      return {
        fraudulent,
        fraudRatio,
        fraudulentUniqueBeneficiaries,
        medianProtectionMinutes: medianProtectionMinutesLocal,
        daily,
        latencyData: LATENCY_BUCKET_ORDER.map((key) => ({ key, count: latencyCounts.get(key) ?? 0 })),
        topFraudulent: rankBeneficiaries(chartSource, (e) => !!e.email_sent),
      }
    }
    if (!dashboardAggregate) return null
    const overview = dashboardAggregate.overview
    const beneficiaryCounts = dashboardAggregate.beneficiaryCounts
    const total = toNumber(overview?.total_entries)
    const fraudulent = toNumber(overview?.total_email_sent)
    const fraudRatio = total > 0 ? Math.round((fraudulent / total) * 100) : 0
    const daily = dashboardAggregate.daily.map((d) => {
      const day = new Date(`${d.day}T00:00:00`)
      return { date: `${day.getDate()}/${day.getMonth() + 1}`, fraudulent: toNumber(d.email_sent_count) }
    })
    const toRankItems = (rows: Array<{ beneficiario: string; entry_count: number | string }>): BeneficiaryRankItem[] =>
      rows.map((row, index) => ({
        key: row.beneficiario.toLowerCase(),
        label: row.beneficiario,
        count: toNumber(row.entry_count),
        rank: index + 1,
      }))
    return {
      fraudulent,
      fraudRatio,
      fraudulentUniqueBeneficiaries: toNumber(beneficiaryCounts?.fraudulent_unique_beneficiaries),
      medianProtectionMinutes: dashboardAggregate.medianProtection?.median_hours == null
        ? medianProtectionMinutesLocal
        : toNumber(dashboardAggregate.medianProtection.median_hours) * 60,
      daily,
      latencyData: dashboardAggregate.latencyBuckets.map((row) => ({ key: row.bucket, count: toNumber(row.entry_count) })),
      topFraudulent: toRankItems(dashboardAggregate.topFraudulent),
    }
  }, [dashboardAggregate, medianProtectionMinutesLocal, platformFilter, chartSource, chartFrom, chartTo, protectionDurations])

  if (selected) {
    return (
      <>
        <Turnstile ref={screenshotTurnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleScreenshotVerify} />
        <SibsEntryDetailPage
          entry={selected}
          theme={theme}
          onClose={() => setSelected(null)}
          sentIds={sentIds}
          onSent={(id) => setSentIds((prev) => new Set([...prev, id]))}
          payloadRequested={payloadRequested}
          onRequestPayload={() => setPayloadRequested(true)}
          getScreenshotToken={getScreenshotToken}
        />
      </>
    )
  }

  return (
    <div ref={restoreListScrollRef} className={`flex flex-col gap-3 p-4 bg-background`}>
      <BulkClassifyDialog
        open={bulkClassifyOpen}
        onOpenChange={setBulkClassifyOpen}
        count={checkedEmailIds.size}
        catalog={catalog}
        theme={theme}
        submitting={bulkClassifySubmitting}
        result={bulkClassifyResult}
        onSubmit={handleBulkClassify}
      />
      {/* sticky top-0, not just a plain header row — same treatment as
          BaseDashboard's own title row (see its doc comment): this view
          doesn't build on BaseDashboard (bespoke widget-arranging header),
          so it needs the negative-margin-to-fill-parent-padding +
          opaque-bg + z-[45] pattern applied directly instead of getting it
          for free. Pins the title/period controls while the widget grid
          and table scroll past below, matching the entry-detail page's own
          sticky header (EntryDetailShell's ShellPage). */}
      <div style={{ top: stackTop }} className={`sticky z-20 -mx-4 -mt-4 flex flex-wrap items-center justify-between gap-3 px-4 pb-3 pt-4 bg-background`}>
        <div>
          <div className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>SIBS</div>
          <h1 className={`text-lg font-semibold tracking-tight ${text}`}>Alerts Analysis</h1>
          <div className={`mt-0.5 font-mono text-[10px] ${muted}`}>
            Fraudulent = takedown alert dispatched{platformFilter.length > 0 ? ' · platform filter scopes the chart widgets and the email table below, KPI cards stay period-wide' : ''}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {arranging && (
            <button
              onClick={() => { setWidgetOrder(DEFAULT_WIDGET_ORDER); localStorage.removeItem(WIDGET_ORDER_STORAGE_KEY) }}
              className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors border-border text-muted-foreground hover:bg-secondary`}
            >
              <RotateCcw className="h-3 w-3" />
              Reset layout
            </button>
          )}
          <button
            onClick={() => setArranging((a) => !a)}
            className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${arranging ? 'border-signal bg-signal-surface text-signal' : 'border-border text-muted-foreground hover:bg-secondary'}`}
          >
            <GripVertical className="h-3 w-3" />
            {arranging ? 'Done arranging' : 'Arrange'}
          </button>
          <PlatformFilterPicker
            catalog={catalog}
            value={platformFilter}
            onChange={setPlatformFilter}
            isDark={isDark}
            border={border}
            muted={muted}
          />
          <PilotMonthStepper
            from={chartFrom}
            to={chartTo}
            border={border}
            muted={muted}
            onApply={setChartRange}
          />
          <DashboardRangePicker
            from={chartFrom}
            to={chartTo}
            isDark={isDark}
            border={border}
            muted={muted}
            onApply={setChartRange}
          />
        </div>
      </div>

      {error && (
        <RetryBanner message={error} onRetry={() => setRetryTick((t) => t + 1)} retrying={loading} isDark={isDark} />
      )}

      <div className="grid grid-cols-6 gap-2" style={{ gridTemplateRows: 'repeat(5, 8rem)', gridAutoRows: '8rem', gridAutoFlow: 'row dense' }}>
        {!summary ? (
          <div className={`col-span-6 flex h-full items-center gap-2 border px-4 font-mono text-[11px] border-border bg-card text-muted-foreground`}>
            <Loader2 className={`h-4 w-4 shrink-0 ${loading ? 'animate-spin' : ''}`} />
            Loading fraud alert analysis
          </div>
        ) : (
          <>
            {widgetOrder.map((key) => {
              let node: React.ReactNode
              if (key === 'spark') {
                node = (
                  <SparkCard
                    id="fraudulent-inrange"
                    label="Fraudulent in range"
                    value={summary.fraudulent}
                    trend={`${summary.fraudRatio}% of all entries`}
                    chartData={summary.daily}
                    series={[{ key: 'fraudulent', color: 'var(--destructive)' }]}
                    isDark={isDark}
                    className="h-full"
                  />
                )
              } else if (key === 'latency') {
                node = <ProtectionLatencyCard data={summary.latencyData} isDark={isDark} muted={muted} text={text} />
              } else if (key === 'median') {
                node = (
                  <div className={`flex h-full flex-col justify-center gap-1 border px-4 border-border bg-card`}>
                    <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Median to protection</div>
                    <div className={`text-lg font-semibold tabular-nums ${text}`}>{formatDurationMinutes(summary.medianProtectionMinutes)}</div>
                    <div className={`font-mono text-[10px] ${muted}`}>{protectionDurations.length} measured</div>
                  </div>
                )
              } else if (key === 'mean') {
                // Mean, not median — pulls toward whichever tail is longer,
                // so it's shown alongside (not instead of) the median card
                // rather than replacing it: the two disagreeing is itself a
                // signal (a skewed mean vs. a stable median means a handful
                // of slow outliers, not that "typical" protection time moved).
                node = (
                  <div className={`flex h-full flex-col justify-center gap-1 border px-4 border-border bg-card`}>
                    <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Mean to protection</div>
                    <div className={`text-lg font-semibold tabular-nums ${text}`}>{formatDurationMinutes(meanProtectionMinutesLocal)}</div>
                    <div className={`font-mono text-[10px] ${muted}`}>{protectionDurations.length} measured</div>
                  </div>
                )
              } else if (key === 'beneficiary') {
                node = (
                  <BeneficiaryRankingCard
                    eyebrow="Scam beneficiaries"
                    title="Beneficiaries under attack"
                    items={summary.topFraudulent}
                    accent="var(--destructive)"
                    isDark={isDark}
                    className="h-full"
                    limit={12}
                    uniqueCount={summary.fraudulentUniqueBeneficiaries}
                  />
                )
              } else if (key === 'fraudwave') {
                // FraudWaveChart itself takes no onExpand prop — same
                // "float a sibling Maximize2 button over a relative
                // wrapper" pattern CampaignsView's own fraudWaveExpanded
                // already uses for this exact component, rather than
                // adding a maximize affordance to the shared chart itself.
                node = logsRequested ? (
                  <div className="relative h-full">
                    <FraudWaveChart
                      data={fraudWaveData}
                      isDark={isDark}
                      replayActive={false}
                      chartFrom={chartFrom}
                      replayMaxDate={null}
                      medianProtectionMinutes={summary?.medianProtectionMinutes ?? medianProtectionMinutesLocal}
                      unmitigatedActiveDays={unmitigatedActiveDays}
                      onUnmitigatedActiveDaysChange={setUnmitigatedActiveDays}
                      className="h-full"
                    />
                    <button
                      onClick={() => setFraudWaveExpanded(true)}
                      title="Expand chart"
                      className={`absolute right-3 top-3 z-10 flex h-6 w-6 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className={`flex h-full flex-col items-center justify-center gap-3 border border-border bg-card`}>
                    <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Fraud wave flattened</div>
                    <button
                      onClick={() => setLogsRequested(true)}
                      className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors border-border text-muted-foreground hover:bg-secondary`}
                    >
                      <Mail className="h-3 w-3" />
                      Show logs
                    </button>
                  </div>
                )
              } else if (key === 'heatmap') {
                // Unlike fraudwave/histogram, this doesn't need dispatchLogs
                // (only chartSource's own email_sent/created_at), so it's
                // always on rather than gated behind "Show logs".
                node = loading && chartSource.length === 0 ? (
                  <div className={`flex h-full items-center justify-center border border-border bg-card`}>
                    <Loader2 className={`h-4 w-4 animate-spin ${muted}`} />
                  </div>
                ) : (
                  <FraudHeatmapCard
                    data={weekdayHourData}
                    isDark={isDark}
                    muted={muted}
                    text={text}
                    className="h-full"
                    onExpand={() => setHeatmapExpanded(true)}
                  />
                )
              } else if (key === 'histogram') {
                node = logsRequested ? (
                  <ProtectionHistogramCard
                    durations={protectionDurations}
                    isDark={isDark}
                    muted={muted}
                    text={text}
                    className="h-full"
                  />
                ) : (
                  <div className={`flex h-full flex-col items-center justify-center gap-3 border border-border bg-card`}>
                    <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Takedown time distribution</div>
                    <button
                      onClick={() => setLogsRequested(true)}
                      className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors border-border text-muted-foreground hover:bg-secondary`}
                    >
                      <Mail className="h-3 w-3" />
                      Show logs
                    </button>
                  </div>
                )
              } else if (key === 'pie') {
                node = loading && campaignTypeBreakdown.length === 0 ? (
                  <div className={`flex h-full items-center justify-center border border-border bg-card`}>
                    <Loader2 className={`h-4 w-4 animate-spin ${muted}`} />
                  </div>
                ) : (
                  <CampaignTypePieCard data={campaignTypeBreakdown} isDark={isDark} muted={muted} text={text} className="h-full" />
                )
              } else {
                node = loading && platformBreakdown.length === 0 ? (
                  <div className={`flex h-full items-center justify-center border border-border bg-card`}>
                    <Loader2 className={`h-4 w-4 animate-spin ${muted}`} />
                  </div>
                ) : (
                  <CategoricalPieCard
                    data={platformBreakdown}
                    eyebrow="Platform"
                    totalSuffix="emailed entries"
                    emptyLabel="No emailed entries in range"
                    isDark={isDark}
                    muted={muted}
                    text={text}
                    className="h-full"
                  />
                )
              }

              return (
                <div
                  key={key}
                  draggable={arranging}
                  onDragStart={handleWidgetDragStart(key)}
                  onDragOver={handleWidgetDragOver}
                  onDrop={handleWidgetDrop(key)}
                  className={`h-full ${WIDGET_SIZE_CLASS[key]} ${arranging ? `cursor-grab  ring-2 ring-dashed ring-ring/40` : ''} ${dragKey === key ? 'opacity-40' : ''}`}
                >
                  {node}
                </div>
              )
            })}
          </>
        )}
      </div>

      {/* overflow-clip, not overflow-hidden — same sticky-trap reasoning as
          the entries table card above. */}
      <div className={`border overflow-clip border-border bg-card`}>
        <div
          ref={emailsCardTitleRef}
          style={{ top: stackTop }}
          className={`sticky z-40 flex items-center justify-between gap-3 border-b px-4 py-3 ${border} bg-card`}
        >
          <div className="flex items-center gap-2">
            <Mail className={`h-3.5 w-3.5 ${muted}`} />
            <span className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>Emails sent</span>
            <span className={`font-mono text-[11px] ${muted}`}>
              {emailEntries.length} alerted{emailScanned > 0 ? ` · ${emailScanned} scanned` : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {emailLoading && <Loader2 className={`h-3.5 w-3.5 animate-spin ${muted}`} />}
            {checkedEmailIds.size > 0 && (
              <button
                onClick={() => { setBulkClassifyResult(null); setBulkClassifyOpen(true) }}
                className={`flex items-center gap-1.5 px-2.5 py-1 border font-mono text-[10px] uppercase tracking-widest transition-colors shrink-0 ${border} bg-card text-muted-foreground hover:border-signal hover:text-signal`}
              >
                <Tags className="h-3 w-3" />
                {`Classify ${checkedEmailIds.size}`}
              </button>
            )}
            {entriesRequested && (
              <button
                onClick={() => setTableExpanded(true)}
                title="Expand table"
                className={`flex h-6 w-6 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        {!entriesRequested ? (
          <div className="flex h-32 flex-col items-center justify-center gap-3">
            <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Alerted entries</div>
            <button
              onClick={() => setEntriesRequested(true)}
              className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors border-border text-muted-foreground hover:bg-secondary`}
            >
              <Search className="h-3 w-3" />
              Load entries
            </button>
          </div>
        ) : emailError ? (
          <div className="p-4">
            <RetryBanner message={emailError} onRetry={() => fetchAlertedEmails(emailOffset)} retrying={emailLoading} isDark={isDark} />
          </div>
        ) : !emailLoadedOnce ? (
          <div className={`flex h-24 items-center justify-center font-mono text-[11px] ${muted}`}>
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Loading emails sent…
          </div>
        ) : emailEntries.length === 0 ? (
          <div className={`flex h-24 flex-col items-center justify-center gap-2 font-mono text-[11px] ${muted}`}>
            <span>No alerted emails found in the {emailScanned} entries scanned so far.</span>
            {emailHasMore && (
              <button onClick={() => fetchAlertedEmails(emailOffset)} className={`underline text-signal`}>
                Scan further back
              </button>
            )}
          </div>
        ) : (
          <>
            {/* No overflow wrapper — see the matching comment on the entries
                table above for why overflow here would break the sticky
                header. */}
            <div>
              <EmailsSentTable
                entries={emailEntries}
                dispatchTimeById={tableDispatchTimeById}
                isDark={isDark}
                onSelect={handleSelectEntry}
                checkedIds={checkedEmailIds}
                onToggleCheck={toggleEmailCheck}
                onToggleAll={toggleAllEmailChecks}
                query={emailQuery}
                onQueryChange={setEmailQuery}
                appliedQuery={appliedEmailQuery}
                searching={emailLoading}
                platformFilter={emailPlatformFilter}
                onPlatformFilterChange={setEmailPlatformFilter}
                catalog={catalog}
                sourceTypeOptions={EMAIL_SOURCE_TYPE_OPTIONS}
                sourceTypeFilter={emailSourceTypeFilter}
                onSourceTypeFilterChange={setEmailSourceTypeFilter}
                lastSelectedId={lastSelectedId}
                onRefresh={() => fetchAlertedEmails(0)}
                refreshing={emailLoading}
                lastUpdated={emailLastUpdated}
                storageKey="sibs-alerts-emails-table"
                stickyOffset={emailsCardTitleHeight}
              />
            </div>
            {emailHasMore && (
              <div className="flex justify-center border-t p-3">
                <button
                  onClick={() => fetchAlertedEmails(emailOffset)}
                  disabled={emailLoading}
                  className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 border-border text-muted-foreground hover:bg-secondary`}
                >
                  {emailLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {tableExpanded && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-overlay p-4 backdrop-blur-sm">
          <div
            className={`relative flex flex-col overflow-hidden border shadow-2xl border-border bg-secondary`}
            style={{
              width: 'min(1400px, 96vw)',
              height: 'min(860px, 92vh)',
              minWidth: 520,
              minHeight: 360,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 'calc(100vh - 32px)',
              resize: 'both',
            }}
          >
            <div className={`flex shrink-0 flex-wrap items-center justify-between gap-2 border-b px-3 py-2 border-border bg-card`}>
              <div>
                <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>Expanded table</div>
                <div className={`text-sm font-semibold ${text}`}>
                  Emails sent <span className={`font-mono text-[11px] font-normal ${muted}`}>({emailEntries.length} alerted · {emailScanned} scanned)</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {checkedEmailIds.size > 0 && (
                  <button
                    // Closes the expanded modal first, same as onSelect below
                    // — this modal's z-[80] sits above the classify dialog's
                    // own z-50, so leaving both open would bury the dialog.
                    onClick={() => { setTableExpanded(false); setBulkClassifyResult(null); setBulkClassifyOpen(true) }}
                    className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors border-border text-muted-foreground hover:bg-secondary`}
                  >
                    <Tags className="h-3 w-3" />
                    {`Classify ${checkedEmailIds.size}`}
                  </button>
                )}
                <button
                  onClick={() => fetchAlertedEmails(emailOffset, true)}
                  disabled={emailLoading || !emailHasMore}
                  title={emailHasMore ? 'Keep scanning the feed until every alerted email is loaded' : 'Already loaded everything'}
                  className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 border-border text-muted-foreground hover:bg-secondary`}
                >
                  {emailLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  {emailHasMore ? 'Load all' : 'Fully loaded'}
                </button>
                <button
                  onClick={() => downloadCsv(emailsSentCsv(emailEntries, tableDispatchTimeById), `sibs-emails-sent-${chartFrom}-to-${chartTo}.csv`)}
                  disabled={emailEntries.length === 0}
                  className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 border-border text-muted-foreground hover:bg-secondary`}
                >
                  <Download className="h-3 w-3" />
                  Export CSV
                </button>
                <button
                  onClick={() => setTableExpanded(false)}
                  title="Close expanded table"
                  className={`flex h-7 w-7 items-center justify-center rounded transition-colors text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-3">
              <EmailsSentTable
                entries={emailEntries}
                dispatchTimeById={tableDispatchTimeById}
                isDark={isDark}
                onSelect={(e) => { setTableExpanded(false); handleSelectEntry(e) }}
                checkedIds={checkedEmailIds}
                onToggleCheck={toggleEmailCheck}
                onToggleAll={toggleAllEmailChecks}
                query={emailQuery}
                onQueryChange={setEmailQuery}
                appliedQuery={appliedEmailQuery}
                searching={emailLoading}
                platformFilter={emailPlatformFilter}
                onPlatformFilterChange={setEmailPlatformFilter}
                catalog={catalog}
                sourceTypeOptions={EMAIL_SOURCE_TYPE_OPTIONS}
                sourceTypeFilter={emailSourceTypeFilter}
                onSourceTypeFilterChange={setEmailSourceTypeFilter}
                lastSelectedId={lastSelectedId}
                onRefresh={() => fetchAlertedEmails(0)}
                refreshing={emailLoading}
                lastUpdated={emailLastUpdated}
                storageKey="sibs-alerts-emails-table"
              />
            </div>
            <div
              aria-hidden="true"
              className={`pointer-events-none absolute bottom-1 right-1 h-5 w-5 border-b-2 border-r-2 border-border`}
            />
          </div>
        </div>
      )}
      {heatmapExpanded && (
        <FraudHeatmapMaximizedWindow
          data={weekdayHourData}
          isDark={isDark}
          muted={muted}
          text={text}
          onClose={() => setHeatmapExpanded(false)}
        />
      )}
      {fraudWaveExpanded && (
        <FraudWaveMaximizedWindow
          data={fraudWaveData}
          isDark={isDark}
          replayActive={false}
          chartFrom={chartFrom}
          replayMaxDate={null}
          medianProtectionMinutes={summary?.medianProtectionMinutes ?? medianProtectionMinutesLocal}
          unmitigatedActiveDays={unmitigatedActiveDays}
          onUnmitigatedActiveDaysChange={setUnmitigatedActiveDays}
          onClose={() => setFraudWaveExpanded(false)}
        />
      )}
    </div>
  )
}

// ─── Pilot overview ─────────────────────────────────────────────────────────
// One glance across the whole Apr–Sep 2026 pilot instead of stepping through
// PilotMonthStepper one month at a time — fetches the same dashboard=1 RPC
// bundle CampaignsView/AlertsAnalysisView use, once per pilot month, and lays
// the key metrics out as a table (metrics as rows, months as columns) so
// evolution across the pilot reads left-to-right at a glance. Hardcoded to
// PILOT_METRICS/PILOT_MONTHS for the same reason PilotMonthStepper is — this
// is scoped to this specific pilot, not a generic reporting builder.
type PilotMonthResult = {
  month: (typeof PILOT_MONTHS)[number]
  data: SibsDashboardAggregate | null
  // Per-RPC error messages (keyed the same way campaignManagement.ts's
  // dashboard=1 handler keys its own `errors` object, e.g. "overview",
  // "medianProtection") — the endpoint still returns success:true with
  // partial data when only some of the 7 RPCs time out, so this is tracked
  // separately from a total request failure rather than folded into one
  // pass/fail flag.
  errors: Record<string, string> | null
  // Total request failure (network error, or payload.success:false) — as
  // opposed to `errors`, which can be non-null even when data partially
  // came back.
  failed: boolean
}

async function fetchPilotMonth(month: (typeof PILOT_MONTHS)[number]): Promise<PilotMonthResult> {
  try {
    const res = await fetch(`/api/campaigns/sibs?dashboard=1&since=${month.from}&until=${month.to}`)
    const payload = await res.json()
    if (!payload?.success) return { month, data: null, errors: null, failed: true }
    return { month, data: payload.data as SibsDashboardAggregate, errors: payload.errors ?? null, failed: false }
  } catch {
    return { month, data: null, errors: null, failed: true }
  }
}

export function SibsPilotOverviewView({ theme = 'dark' }: { theme?: ProcessedTheme }) {
  const isDark = theme === 'dark'
  const muted = 'text-subtle'
  const text = 'text-foreground'
  const border = 'border-border'
  const softBg = 'bg-secondary'
  const panelBg = 'bg-card'

  const [results, setResults] = useState<PilotMonthResult[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [retryTick, setRetryTick] = useState(0)
  const [retryingMonths, setRetryingMonths] = useState<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false

    // Sequential on purpose — each month's dashboard=1 request already fans
    // out to 7 RPCs server-side (see campaignManagement.ts), so firing all 6
    // months at once meant 42 concurrent queries against the database,
    // enough to blow every RPC's statement timeout except whichever month
    // happened to be small/fast. Fetching one month at a time (same
    // sequencing philosophy CampaignsView/AlertsAnalysisView already use for
    // their own chart/dashboard/logs fetches) trades total load time for
    // actually finishing, and fills in columns as they arrive instead of
    // all-or-nothing.
    const run = async () => {
      setResults([])
      setLoading(true)
      for (const month of PILOT_MONTHS) {
        if (cancelled) return
        const result = await fetchPilotMonth(month)
        if (cancelled) return
        setResults((prev) => [...(prev ?? []), result])
      }
      if (!cancelled) setLoading(false)
    }

    run()
    return () => { cancelled = true }
  }, [retryTick])

  const retryMonth = async (month: (typeof PILOT_MONTHS)[number]) => {
    setRetryingMonths((prev) => new Set(prev).add(month.label))
    const result = await fetchPilotMonth(month)
    setResults((prev) => (prev ?? []).map((r) => (r.month.label === month.label ? result : r)))
    setRetryingMonths((prev) => {
      const next = new Set(prev)
      next.delete(month.label)
      return next
    })
  }

  return (
    <div className={`flex h-full flex-col gap-3 p-4 ${softBg}`}>
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>// SIBS · Custom Mail Alerts</div>
          <h1 className={`mt-0.5 text-xl font-semibold tracking-tight ${text}`}>Pilot Overview</h1>
          <p className={`mt-0.5 text-[12px] ${muted}`}>April – September 2026, side by side</p>
        </div>
        <button
          onClick={() => setRetryTick((t) => t + 1)}
          disabled={loading}
          className={`flex h-7 items-center gap-1.5 border px-2.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-40 ${border} ${muted} hover:border-signal hover:text-signal`}
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh all
        </button>
      </div>

      <div className={`flex-1 overflow-auto border ${border} ${panelBg}`}>
        <ResizableTable
          columns={[
            { key: 'metric', label: 'Metric', stickyLeft: true, thClassName: `whitespace-nowrap ${muted}` },
            ...PILOT_MONTHS.map((m) => {
              const result = results?.find((r) => r.month.label === m.label)
              const isRetrying = retryingMonths.has(m.label)
              const hasIssue = !!result && (result.failed || !!(result.errors && Object.keys(result.errors).length > 0))
              return {
                key: m.label,
                // justify-end (rather than relying on the <th>'s own
                // text-align) since ResizableTable's own header markup
                // always wraps `label` in a plain flex row — this is the
                // one place that flex row needs to end-align instead.
                label: (
                  <div className="flex w-full items-center justify-end gap-1.5">
                    {hasIssue && (
                      <span title={result?.failed ? 'Failed to load this month' : `Some metrics failed to load: ${Object.keys(result?.errors ?? {}).join(', ')}`}>
                        <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
                      </span>
                    )}
                    <span className={hasIssue ? 'text-destructive' : muted}>{m.label}</span>
                    {result && (
                      <button
                        type="button"
                        onClick={() => retryMonth(m)}
                        disabled={isRetrying}
                        title={`Refresh ${m.label}`}
                        className={`shrink-0 rounded p-0.5 transition-colors hover:text-signal ${muted}`}
                      >
                        <RefreshCw className={`h-2.5 w-2.5 ${isRetrying ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                  </div>
                ),
              } as ColumnDef
            }),
          ]}
          stickyHeader
          maximizable={false}
          theme={theme}
          tableClassName="w-full min-w-[720px] text-[12px]"
          theadClassName={`border-b ${border} ${panelBg}`}
          thPadding="px-3 py-2"
          thRowClassName="font-mono text-[10px] uppercase tracking-widest"
        >
          {PILOT_METRICS.map((metric) => (
            <tr key={metric.key} className={`border-b last:border-b-0 ${border}`}>
              <TableTd sticky className={`whitespace-nowrap font-medium ${text}`}>
                {metric.label}
              </TableTd>
              {PILOT_MONTHS.map((m, i) => {
                const result = results?.find((r) => r.month.label === m.label)
                if (!result) {
                  return (
                    <TableTd key={m.label} className="text-right">
                      {loading && <Loader2 className={`ml-auto h-3 w-3 animate-spin ${muted}`} />}
                    </TableTd>
                  )
                }
                if (result.failed) {
                  return (
                    <TableTd key={m.label} className="text-right">
                      <span title="Failed to load this month">
                        <AlertCircle className="ml-auto h-3.5 w-3.5 text-destructive" />
                      </span>
                    </TableTd>
                  )
                }
                const rpcError = result.data ? result.errors?.[metric.rpcKey] : undefined
                if (rpcError) {
                  return (
                    <TableTd key={m.label} className="text-right">
                      <span title={rpcError}>
                        <AlertCircle className="ml-auto h-3.5 w-3.5 text-destructive" />
                      </span>
                    </TableTd>
                  )
                }
                const value = metric.get(result.data!, m)
                const prevResult = i > 0 ? results?.find((r) => r.month.label === PILOT_MONTHS[i - 1].label) : null
                const prevValue = prevResult?.data && !prevResult.errors?.[metric.rpcKey] ? metric.get(prevResult.data, prevResult.month) : null
                const delta = value != null && prevValue != null ? value - prevValue : null
                return (
                  <TableTd key={m.label} className={`text-right font-mono ${text}`}>
                    {value == null ? '—' : metric.format(value)}
                    {delta != null && delta !== 0 && (
                      <span className={`ml-1.5 text-[10px] ${delta > 0 ? 'text-signal' : muted}`}>
                        {delta > 0 ? '▲' : '▼'}
                      </span>
                    )}
                  </TableTd>
                )
              })}
            </tr>
          ))}
        </ResizableTable>
      </div>
    </div>
  )
}
